/**
 * English Translations
 * English translations for DQFleet
 */

export const translationsEn: Record<string, any> = {
  // Common
  common: {
    save: 'Save',
    selectTime: 'Select time',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    add: 'Add',
    remove: 'Remove',
    module: 'Module',
    type: 'Type',
    description: 'Description',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    update: 'Update',
    search: 'Search',
    clear: 'Clear',
    filter: 'Filter',
    allOrganizations: 'All organizations',
    allRoles: 'All roles',
    clearFilters: 'Clear filters',
    refresh: 'Refresh',
    loading: 'Loading...',
    error: 'Error',
    tryAgain: 'Try again',
    success: 'Success',
    confirm: 'Confirm',
    confirmDelete: 'Are you sure you want to delete this?',
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    back: 'Back',
    pageErrorTitle: 'This page could not be displayed',
    pageErrorDescription: 'An unexpected error occurred. Go back to the previous page.',
    next: 'Next',
    previous: 'Previous',
    actions: 'Actions',
    notes: 'Notes',
    open: 'Open',
    view: 'View',
    hide: 'Hide',
    notFound: 'Not found',
    unknown: 'Unknown',
    comingSoon: 'Coming soon',
    plannedFeatures: 'Planned features',
    underDevelopment: 'This feature is under development',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    activate: 'Activate',
    deactivate: 'Deactivate',
    suspended: 'Suspended',
    overview: 'Overview',
    organizations: 'Organizations',
    vehicles: 'Vehicles',
    vehicle: 'Vehicle',
    trend: 'Trend',
    trips: 'Trips',
    distance: 'Distance',
    organization: 'Organization',
    fromDate: 'From date',
    toDate: 'To date',
    from: 'From',
    to: 'To',
    startDate: 'Start date',
    endDate: 'End date',
    all: 'All',
    export: 'Export',
    exportCsv: 'Export to CSV',
    saveError: 'Could not save',
    deleteError: 'Could not delete',
    activateError: 'Could not activate',
    loadDataError: 'Could not load data',
    updateStatusError: 'Could not update status',
    changeLanguageError: 'Could not change language',
    copiedToClipboard: 'Copied to clipboard',
    week: 'Week',
    month: 'Month',
    day: 'Day',
    quarter: 'Quarter',
    selected: 'Selected',
    noResults: 'No results found',
    select: 'Select...',
    selectOrganization: 'Select organization',
    selectLanguage: 'Select language',
    searchOrSelect: 'Search or select',
  },

  // Navigation
  nav: {
    dashboard: 'Dashboard',
    fleet: 'Vehicle Fleet',
    liveTracking: 'Live Map',
    reports: 'Reports',
    statistics: 'Statistics',
    vehicles: 'Vehicles',
    profile: 'Profile',
    administration: 'Administration',
    sysAdmin: 'SysAdmin',
    crm: 'CRM',
    logout: 'Logout',
    organization: 'Organization',
  },

  // Auth
  auth: {
    login: 'Login',
    signup: 'Sign up',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    rememberMe: 'Remember me',
    loginSuccess: 'Login successful',
    loginError: 'Login failed',
    logout: 'Logout',
    otpTitle: 'Verification Code',
    otpDescription: 'We have sent a verification code to',
    otpSent: 'Verification code sent',
    otpVerified: 'Verification successful',
    otpResent: 'New verification code sent',
    invalidOtp: 'Invalid verification code',
    resend: 'Resend',
    samlRequired: 'SAML authentication required for this organization',
    authMethodChanged: 'Authentication method has changed',
    ssoRedirect: 'Redirecting to sign-in with your organization identity...',
    ssoCompleting: 'Signing in...',
    ssoCompleteError: 'Could not complete sign-in. Please try again.',
    checkingSso: 'Checking login method...',
    ssoContinue: 'Continue to {org}',
    ssoDomainSignupBlocked: 'Your organization uses SSO login. Do not create an account – sign in with your organization identity.',
    useSsoInstead: 'Sign in with SSO',
    // SSO
    ssoLogin: 'Single Sign-On',
    chooseLoginMethod: 'Choose login method',
    loginWith: 'Login with',
    loginWithSaml: 'Login with SAML',
    loginWithPassword: 'Login with password',
    backToLogin: 'Back to login',
    noOrganizationId: 'No organization specified',
    samlError: 'SAML authentication failed',
    samlErrorTitle: 'Login failed',
    retryLogin: 'Try again',
    backToLanding: 'Back to start page',
    goToLogin: 'Go to login',
    contactSupport: 'If the problem persists, contact support.',
  },

  // Reset Password
  resetPassword: {
    title: 'Reset Password',
    description: 'Enter your email address and we will send you a link to reset your password.',
    emailSentTitle: 'Email Sent',
    emailSentMessage: 'If a user with this email address exists, a reset email will be sent. Check your inbox and follow the instructions to reset your password.',
    newPasswordLabel: 'New Password',
    newPasswordPlaceholder: 'Enter new password',
    confirmPasswordLabel: 'Confirm Password',
    confirmPasswordPlaceholder: 'Confirm new password',
    sendButton: 'Send Reset Link',
    resetButton: 'Reset Password',
    sending: 'Sending...',
    resetting: 'Resetting...',
    backToLogin: 'Back to Login',
    rememberPassword: 'Remember your password?',
  },

  // Admin
  admin: {
    title: 'Administration',
    roles: 'Role Management',
    organizations: 'Organization Management',
    users: 'User Management',
    deleteUserConfirm: 'Are you sure you want to delete this user?',
    hardDeleteUserConfirm: 'This permanently removes {{name}} ({{email}}) so SSO can recreate the account. This cannot be undone.',
    translations: 'Translations',
    imports: 'Import Generator',
    packages: 'Price Packages',
    sysAdmin: 'System Administrator',
    devices: 'Devices',
    deviceManagement: 'Device Management',
  },

  // Import Generator
  packages: {
    title: 'Price Packages',
    description: 'Manage price packages for systems and modules',
    createNew: 'Create new package',
    noPackages: 'No packages created',
    priceMonthly: 'Price per month',
    priceYearly: 'Price per year',
    includedModules: 'Included modules',
    maxUsers: 'Max users',
    maxVehicles: 'Max vehicles',
    features: 'Extra features',
    isDefault: 'Default package',
    selectPackage: 'Select package',
    currentPackage: 'Current package',
    totalMonthly: 'Total monthly cost',
    // Module pricing
    modulePrices: 'Module Prices',
    modulePrice: 'Module Price',
    setDefaultPrice: 'Set default price',
    // Subscriptions
    addon: 'Add-on',
    demo: 'Demo',
    trial: 'Trial',
    included: 'Included',
    startDemo: 'Start demo',
    startTrial: 'Start trial',
    upgrade: 'Upgrade',
    upgradeToFull: 'Upgrade to full',
    daysRemaining: 'days remaining',
    trialDays: 'Trial days',
    trialPrice: 'Trial price',
    addModule: 'Add module',
    removeModule: 'Remove module',
    moduleAdded: 'Module added',
    moduleRemoved: 'Module removed',
    demoStarted: 'Demo started (30 days)',
    trialStarted: 'Trial started',
    upgraded: 'Upgraded to full subscription',
    syncFromPackage: 'Sync from package',
    modulesSynced: 'Modules synced',
    // SystemPackagesManager
    packages: 'Packages',
    modules: 'Modules',
    pricePerMonth: 'Price/month',
    pricePerYear: 'Price/year',
    modulePricesDescription: 'Set default price for each module. These prices are used as add-on modules when customers want to add modules beyond their package.',
    packagesCount: 'packages',
    newPackage: 'New package',
    systemsWithoutPackages: 'Systems without packages',
    modulesWithoutPackages: 'Modules without packages',
    noModulesFound: 'No modules found',
    noSystemsFound: 'No systems found',
    noModulesForSystem: 'No modules available for this system',
    noPacketsSelected: 'No packets selected',
    addPacketsInSectionAbove: 'Add packets in the Price Packages section above to see available modules.',
    systemsActive: 'systems active',
    enableModule: 'Enable module',
    disableModule: 'Disable module',
    enableAction: 'Enable',
    disableAction: 'Disable',
    noActionsForModule: 'No actions available for this module',
    moduleEnabledForCustomer: 'Module enabled for customer',
    moduleDisabledForCustomer: 'Module disabled for customer',
    moduleToggleError: 'Could not change the module',
    moduleActionsLoadError: 'Could not load the module actions',
    moduleActionEnabled: 'Action enabled',
    moduleActionDisabled: 'Action disabled',
    moduleActionToggleError: 'Could not change the action',
    defaultPackage: 'Default package for new customers',
    extraFeatures: 'Extra features (one per line)',
    internalName: 'Internal name',
    displayName: 'Display name',
    descriptionPlaceholder: 'Short description of the package',
    codeExample: 'e.g. standard',
    nameExample: 'e.g. Deque Standard',
    unlimited: 'Unlimited',
    editPrice: 'Edit price',
    createPackage: 'Create package',
    editPackage: 'Edit package',
    notSet: 'Not set',
    loadError: 'Could not load packages',
    saveError: 'Could not save package',
    modulePriceSaved: 'Module price saved',
    modulePriceError: 'Could not save module price',
    packageUpdated: 'Package updated',
    packageCreated: 'Package created',
    // CustomerPackages
    packageActivatedAndSynced: 'Package activated and modules synced',
    activateError: 'Could not activate package',
    addModuleError: 'Could not add module',
    confirmRemoveModule: 'Remove module from customer?',
    removeModuleError: 'Could not remove module',
    upgradeError: 'Could not upgrade',
    noPackagesAvailable: 'No packages available',
    selectModule: 'Select module',
    fullFunctionFullPrice: 'Full function, full price',
    demoDescription: '30 days, limited',
    trialDescription: 'Full function, custom time',
    standard: 'Standard',
    free: 'Free',
    perMonth: 'per month',
  },

  imports: {
    title: 'Import Generator',
    description: 'Create and manage data imports',
    createNew: 'Create new import',
    noImports: 'No imports created yet',
    name: 'Import name',
    targetTable: 'Target table',
    schedule: 'Schedule',
    lastRun: 'Last run',
    status: 'Status',
    actions: 'Actions',
    run: 'Run import',
    edit: 'Edit',
    delete: 'Delete',
    fileLoaded: 'File loaded',
    missingFields: 'Please fill in all required fields',
    saved: 'Import saved',
    deleted: 'Import deleted',
    loadError: 'Could not load imports',
    confirmDelete: 'Are you sure you want to delete this import?',
    step1: 'Basic settings',
    step2: 'Upload file',
    step3: 'Map fields',
    step4: 'Settings',
    sourceFields: 'Source fields',
    targetFields: 'Target fields',
    fieldsMapped: 'fields mapped',
    decimalSeparator: 'Decimal separator',
    csvDelimiter: 'CSV delimiter',
    hasHeader: 'First row is header',
    duplicateAction: 'On duplicate',
    skipDuplicate: 'Skip (keep existing)',
    updateDuplicate: 'Update existing',
    errorAction: 'On error',
    skipErrors: 'Skip errored rows',
    abortOnError: 'Abort entire import',
    loadForEditError: 'Could not load import for editing',
    importSource: 'Import source',
    importSourceNone: 'No source',
    selectedSource: 'Selected source',
    lastFetchAt: 'Last fetch',
    lastFetchStatus: 'Health status',
    filesInSource: 'Files in source',
    notFetchedYet: 'Never',
    sourceHealthSuccess: 'Successful',
    sourceHealthFailed: 'Failed',
    sourceHealthUnknown: 'Unknown',
    sourcesStatusTitle: 'Source status',
    sourceSyncError: 'Sync error',
    sourcePreviewLoading: 'Loading latest file from source...',
    sourcePreviewLoaded: 'Template loaded from source',
    sourcePreviewNotFound: 'No synced file found for selected source',
    sourcePreviewLoadError: 'Could not load template from source',
    sourcePreviewReload: 'Reload from source',
    livePreviewNeedsSample:
      'No sample file loaded – go back to step 2 and reload from source to see the preview.',
    runLatestSource: 'Force run from latest source file',
    autoMappedFields: 'fields were automatically mapped',
    importHistoryTitle: 'Import history',
    allDefinitions: 'All definitions',
    historyUpdateBtn: 'Refresh',
    historyLoadingText: 'Loading history…',
    noRunsFound: 'No runs found',
    showDetails: 'Show details',
    loadRunError: 'Could not load run',
    runDetailsTitle: 'Run details',
    runDetailsStatus: 'Status',
    runDetailsStarted: 'Started',
    runDetailsEnded: 'Ended',
    runDetailsFile: 'File',
    runDetailsRows: 'Rows',
    runDetailsErrors: 'Errors',
    runDetailsAndMore: 'and {n} more',
    importingFile: 'Importing...',
    importThisFile: 'Import this file',
    noFilesForSource: 'No files found for this source. Sync the source first.',
    loadingFiles: 'Loading files...',
    filesFromSourceTitle: 'Files from source',
    ftpImportTitle: 'FTP import',
    ftpPathLabel: 'Path',
    ftpListBtn: 'List',
    ftpLoadingText: 'Loading...',
    ftpNoFiles: 'No files found.',
    ftpOpen: 'Open',
    ftpRunImport: 'Run import',
    importingProgress: 'Importing…',
    rowsSuffix: 'rows',
    colsSuffix: 'cols.',
    createFirstImport: 'Create your first import',
    importUpdated: 'Import updated',
    statusSuccess: 'Successful',
    statusFailed: 'Failed',
    statusPartial: 'Partial',
    statusRunning: 'Running',
    statusQueued: 'Queued',
    statusCancelled: 'Cancelled',
    statusAll: 'All',
    colDefinition: 'Definition',
    colFileSource: 'File / Source',
    colStarted: 'Started',
    colRows: 'Rows',
    colErrors: 'Errors',
    colAction: 'Action',
    showingRuns: 'Showing {shown} of {total} runs',
    noTransformsAdded: 'No transforms added.',
    addTransform: 'Add transform',
    sourceLabel: 'Source',
    typeLabel: 'Type',
    paramsLabel: 'Parameters (JSON)',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    livePreview: 'Value as saved',
    runDetailsDuplicates: 'Duplicates (skipped)',
    downloadFailedRows: 'Download failed rows (CSV)',
    downloadDuplicateRows: 'Download duplicates (CSV)',
    artifactDownloadError: 'Could not download the file',
    identifiedColumns: 'Identified columns:',
    changeFile: 'Change file',
    supportedFormats: 'Supported formats: Excel, CSV, JSON, XML, TXT, DAT',
    schemaMappingError: 'Schema validation: errors in mappings',
    sourcePanelTitle: 'Source fields (from file)',
    targetPanelTitle: 'Target fields',
    examplePrefix: 'Ex',
    fieldsMappedCount: '{n} fields mapped',
    addMappingInStep3: 'Add at least one field mapping in step 3 to configure transforms.',
    scheduleLabel: 'Scheduling',
    scheduleExecution: 'Execution',
    scheduleManual: 'Manual only',
    scheduleDaily: 'Daily',
    scheduleWeekly: 'Weekly',
    scheduleMonthly: 'Monthly',
    scheduleTimeLabel: 'Time (HH:mm)',
    scheduleDayOfWeek: 'Day of week (0=Sunday … 6=Saturday)',
    scheduleDayOfMonth: 'Day of month (1–31)',
    scheduleCronLabel: 'Cron (optional, e.g. 0 6 * * *)',
    fileSourcePathLabel: 'File source (path or glob, e.g. /var/imports/fuel_*.dat)',
    stableFileTitle: 'Wait for stable file',
    stableCheckInterval: 'Check interval (ms)',
    stableStableFor: 'Stable for (ms)',
    stableTimeout: 'Timeout (ms, 0 = unlimited)',
    retryTitle: 'Retry on error',
    retryMaxRetries: 'Max retries',
    retryInitialDelay: 'Initial delay (ms)',
    retryMaxDelay: 'Max delay (ms)',
    retryBackoff: 'Backoff multiplier',
    summaryTitle: 'Summary',
    summaryName: 'Name',
    summaryTargetTable: 'Target table',
    summaryMappedFields: 'Mapped fields',
    summaryFileFormat: 'File format',
    summarySchedule: 'Schedule',
    invalidJsonTransform: 'Invalid JSON in transform parameters',
    duplicateActionLabel: 'On duplicates',
    updateExisting: 'Update existing',
    errorActionLabel: 'On error',
    skipErroneousRows: 'Skip erroneous rows',
    abortEntireImport: 'Abort entire import',
    wizardTitleCreate: 'Create new import',
    wizardTitleEdit: 'Edit import',
    wizardStepBasicInfo: 'Basic info',
    wizardStepLoadFile: 'Load file',
    wizardStepMapFields: 'Map fields',
    wizardStepTransforms: 'Transforms',
    wizardStepSettings: 'Settings',
    wizardCancel: 'Cancel',
    wizardBack: 'Back',
    wizardNext: 'Next',
    wizardSave: 'Save import',
    descriptionPlaceholder: 'Optional description...',
    presetsLabel: 'Presets (optional)',
    clearPreset: 'Clear preset',
    targetTableLabel: 'Target table *',
    importNameLabel: 'Import name *',
    descriptionLabel: 'Description',
    step1Title: 'Step 1: Basic settings',
    step2Title: 'Step 2: Upload template file',
    step3Title: 'Step 3: Map fields',
    step4Title: 'Step 4: Transforms per field',
    step5Title: 'Step 5: Import settings',
    filenameFilterTitle: 'Filename filter',
    filenameFilterHint:
      'Which source files this import should process. Glob: *K02*, *K05*. Regex (starts with ^): ^\\d = filename must start with a digit (Swedish OKQ8). Empty = all files.',
    filenameFilterAdd: 'Add pattern',
    decimalSeparatorLabel: 'Decimal separator',
    decimalComma: 'Comma (1 234,56)',
    decimalDot: 'Dot (1,234.56)',
    csvSeparatorLabel: 'CSV separator',
    csvSemicolon: 'Semicolon (;)',
    csvComma: 'Comma (,)',
    csvTab: 'Tab',
    csvPipe: 'Pipe (|)',
    excelSheetNameLabel: 'Excel sheet name (optional)',
    excelSheetNamePlaceholder: 'E.g. Sheet1',
    excelSheetIndexLabel: 'Excel sheet index (0-based)',
    excelSheetIndexHint: 'If sheet name is provided it takes precedence over sheet index.',
    headerCellsTitle: 'Invoice header above the table',
    headerCellsHint: 'Click a cell in the rows above the header row to map it to a target field. The value is applied to every imported row.',
    headerCellsClickToMap: 'Click to map',
    headerCellsRemove: 'Remove',
    headerCellsEditorTitle: 'Map cell',
    headerCellsEmpty: '(empty cell)',
    headerCellsAsColumn: 'Virtual column name',
    headerCellsTargetField: 'Target field',
    headerCellsNoTarget: '— No target field —',
    headerCellsUseSplit: 'Split cell value (e.g. customer no. + name)',
    headerCellsSplitPattern: 'Regex pattern',
    headerCellsSplitHint: 'Default: leading digits → part 1, remainder → part 2 (as Qstar B3).',
    headerCellsPart1Column: 'Part 1 (group 1)',
    headerCellsPart2Column: 'Part 2 (group 2)',
    headerCellsReloadHint: 'Skip rows is set, but the invoice header is missing from the preview. Reload the file/source so rows above the table can be mapped.',
    runPendingSource: 'Import all pending',
    runPendingSourceTitle: 'Import all source files that have not been imported yet',
    pendingImportDone: 'Pending import done',
    pendingImportFailed: 'Could not import pending source files',
    pendingImportRunning: 'Importing pending files…',
    pageTitle: 'Import Generator',
    pageDescription: 'Create and manage data imports without writing code',
    colName: 'Name',
    colDate: 'Date',
    colSize: 'Size',
    colTargetTable: 'Target table',
    colSchedule: 'Schedule',
    colLastRun: 'Last run',
    testImportTitle: 'Test import ({n} rows)',
    loadingHistory: 'Loading history...',
    cannotSave: 'Could not save',
    importComplete: 'Import done: {imported} imported, {updated} updated, {failed} failed',
    testImportComplete: 'Test import done: {processed} rows tested, {failed} failed (no data saved)',
    loadSourceFilesError: 'Could not load files from source',
    loadHistoryError: 'Could not load run history',
    ftpListError: 'Could not list FTP files',
    ftpImportFailed: 'FTP import failed',
    importFailed: 'Import failed',
    latestSourceImportFailed: 'Import from latest source file failed',
  },

  // Sysadmin
  sysadmin: {
    loadSlowQueriesError: 'Could not load slow queries',
    clearLogError: 'Could not clear log',
    loadErrorLogError: 'Could not load error log',
    loadAuditLogError: 'Could not load audit log',
  },

  // Tracking
  tracking: {
    saveMapViewError: 'Could not save map view',
  },

  // Support
  support: {
    updateTicketError: 'Could not update ticket',
    sendReplyError: 'Could not send reply',
    sendBroadcastError: 'Could not send broadcast',
    deleteBroadcastError: 'Could not delete broadcast',
    closeTicketError: 'Could not close ticket',
    sendMessageError: 'Could not send message',
  },

  // Device Management
  deviceManagement: {
    title: 'Device Management',
    devices: 'Devices',
    device: 'Device',
    imei: 'IMEI',
    iccid: 'ICCID',
    status: 'Status',
    vehicle: 'Vehicle',
    organization: 'Organization',
    lastKnownVin: 'Last known VIN',
    history: 'History',
    actions: 'Actions',
    details: 'Details',
    
    // Status
    statusStock: 'Stock',
    statusCustomer: 'At customer',
    statusVehicle: 'In vehicle',
    statusLost: 'Lost',
    statusDecommissioned: 'Decommissioned',
    
    // Actions
    toStock: 'To stock',
    unmountKeep: 'Unmount (keep with customer)',
    unmountStock: 'Unmount (to stock)',
    markLost: 'Mark as lost',
    decommission: 'Decommission',
    assignOrganization: 'Assign organization',
    selectOrganization: 'Select organization',
    organizationFollowsVehicle: 'Follows vehicle assignment',

    obdModeTitle: 'OBD II Feature',
    obdModeHint:
      'Sends setparam via gateway (queued if offline). OBD = passenger car, ELD = heavy vehicles (J1939/FMS), Non-OBD = EV/OEM (VIN auto/manual, web_connect). Anti-alarm = Non-OBD + Movement Stop Delay 5 s + Ignition Averaging off + Accelerometer ignition (ID.x / car alarm). OBD-Disable = only 40000:0 (turn off OBD II Feature).',
    obdModeAuto: 'OBD (auto)',
    obdModeEld: 'ELD – Heavy vehicles',
    obdModeNonObd: 'Non-OBD Compliant + OEM',
    obdModeAntiAlarm: 'Anti-alarm',
    obdModeDisable: 'OBD-Disable',
    obdModeConfirmTitle: 'Send OBD command',
    obdModeConfirmObdAuto:
      'Enable OBD (auto) on {reg}? Sends setparam 40000:1 and VIN Auto 40005:0 (same as P&P for passenger car/ICE).',
    obdModeConfirmEld:
      'Enable ELD (heavy vehicles) on {reg}? Sends setparam 40000:2 (J1939/FMS). Use only for trucks/buses with FMS — not passenger cars, hybrids or EVs.',
    obdModeConfirmNonObd:
      'Enable Non-OBD Compliant + OEM provisioning on {reg}? Queues setparam 40000:3, VIN auto/manual and web_connect.',
    obdModeConfirmAntiAlarm:
      'Send anti-alarm to {reg}? Queues setparam 40000:3;19002:5;50005:0;101:2 and adds the device to the Non-OBD list.',
    obdModeConfirmDisable:
      'Send OBD-Disable to {reg}? Sends setparam 40000:0 and saves the vehicle on the problem list so Disable is reapplied after FW/cfg upgrade.',
    obdModeSuccess: 'OBD command sent to device',
    obdModePartialFail: 'Command sent but device did not respond to all steps (offline/timeout?)',
    obdModeError: 'Could not send OBD command',
    obdDisableLastSent: 'Last Disable on this device: {when} ({status}) by {user}',
    obdDisableLogShow: 'Show Disable log (recent sends)',
    obdDisableLogHide: 'Hide Disable log',
    obdDisableLogEmpty: 'No Disable sends logged yet.',
    obdDisableLogWhen: 'When',
    obdDisableLogDevice: 'Device',
    obdDisableLogStatus: 'Status',
    obdDisableLogBy: 'By',

    obdBaselineTitle: 'OBD Plug & Play – baseline (FW + config)',
    obdBaselineHelp:
      'Set exact FOTA WEB file names for FMM003 before enabling P&P. Auto-run stays off until OBD_PROVISIONING_ENABLED=true in .env.',
    obdBaselineRuntimeOn: 'P&P globally ON',
    obdBaselineRuntimeOff: 'P&P globally OFF (safe mode)',
    obdBaselineModels: 'Allowed models',
    obdBaselineAllProfiles: 'all profiles',
    obdBaselineFw: 'Target firmware (FOTA file name)',
    obdBaselineCfg: 'Base config (FOTA file name)',
    obdBaselineNote: 'Note',
    obdBaselineSaved: 'Baseline saved',
    obdBaselineLoadError: 'Could not load baselines',
    obdBaselineSaveError: 'Could not save baseline',
    obdBaselineNeedFile: 'Enter at least a firmware or config file name',
    obdBaselineEmpty: 'No baselines. Run migration 345 or create an FMM003 row.',

    obsTitle: 'Watch list',
    obsHint: 'Devices to bench-test or review when they come back.',
    obsOpenCount: '{count} open',
    obsEmpty: 'No open watch-list items.',
    obsBadge: 'WATCH',
    obsAdd: 'Add to watch list',
    obsAddSelected: 'Add selected device to watch list',
    obsNotePlaceholder: 'What should be tested when the device comes back?',
    obsDefaultNote: 'Bench-test when the device is returned.',
    obsAdded: 'Added to watch list',
    obsMarkDone: 'Tested / done',
    obsMarkedDone: 'Watch-list item marked done',
    obsCancelled: 'Watch-list item cancelled',
    obsLoadError: 'Could not load watch list',
    obsSaveError: 'Could not save watch-list item',

    // Import
    importReturns: 'Import returns',
    importTitle: 'Import returned devices',
    importDescription: 'Paste IMEI numbers from Excel (one per line)',
    importSettings: 'Settings',
    setToStock: 'Set status to STOCK',
    disconnectVehicle: 'Disconnect from vehicle',
    deactivateSim: 'Deactivate SIM card at Jasper',
    validImeis: 'valid IMEI numbers found',
    importing: 'Importing...',
    successful: 'Successful',
    failed: 'Failed',
    manualRequired: 'Manual required',
    needsAttention: 'Needs attention',
    
    // History
    changeHistory: 'Change history',
    noHistory: 'No history',
    statusChanged: 'Status changed',
    vehicleMounted: 'Mounted in vehicle',
    vehicleUnmounted: 'Unmounted',
    vehicleTransferred: 'Transferred to another vehicle',
    vinDetected: 'VIN detected',
    vehicleCreated: 'Vehicle created',
    simActivated: 'SIM activated',
    simSuspended: 'SIM suspended',
    deviceCreated: 'Device created',
    deviceImported: 'Device imported',
    
    // Bulk & filters
    searchPlaceholder: 'Search IMEI (full or partial, e.g. last 4), ICCID, reg.no...',
    allStatuses: 'All statuses',
    total: 'Total',
    selectAllAvailable: 'Select all available',
    selected: 'selected',
    deselectAll: 'Deselect all',
    importImeiToOrg: 'Import IMEI → Organization',
    devicesAssigned: 'devices assigned',
    deviceDetails: 'Device Details',
    
    // Messages
    noDevices: 'No devices found',
    selectDevice: 'Select a device to view details',
    confirmDecommission: 'Are you sure? This cannot be undone.',
  },

  // Device AVL Capabilities
  deviceAvlCapabilities: {
    title: 'AVL Capabilities per Device',
    description: 'Which AVL parameters (id + name) each device/vehicle delivers. One row per IMEI+VIN. Click IMEI to see name and description.',
    filterImei: 'Filter by IMEI',
    filterRegNumber: 'Filter by reg no.',
    filterAvlId: 'AVL id (e.g. 13)',
    imei: 'IMEI',
    vin: 'VIN',
    regNumber: 'Reg no.',
    avlParameters: 'AVL parameters',
    firstSeen: 'First seen',
    lastSeen: 'Last seen',
    noData: 'No data yet. Data is collected when devices send GPS points.',
    modalTitle: 'AVL parameters for device',
    parameterCountOne: 'parameter',
    parameterCount: 'parameters',
  },

  // Roles
  roles: {
    title: 'Role Management',
    createRole: 'Create new role',
    editRole: 'Edit role',
    viewRights: 'View rights',
    roleName: 'Name',
    roleCode: 'Code',
    roleDescription: 'Description',
    permissions: 'Permissions',
    systemRole: 'System role',
    customRole: 'Custom role',
    allActions: 'All actions',
    codePlaceholder: 'e.g. CUSTOM_ROLE',
    namePlaceholder: 'e.g. Custom role',
    descriptionPlaceholder: 'Role description...',
    codeCannotChange: 'Code cannot be changed',
    rightsAndAccess: 'Rights and access',
    loadingFeatures: 'Loading features...',
    noActionsAvailable: 'No actions available',
    noAccessToFeature: 'No access to this feature',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    createRoleButton: 'Create role',
    updateRoleButton: 'Save changes',
    roleCreated: 'Role created!',
    roleUpdated: 'Role updated!',
    createRoleError: 'Could not create role',
    updateRoleError: 'Could not update role',
    loadRoleError: 'Could not load role',
    systemRoleOnlyGlobalAdmin: 'System roles can only be edited by global admin.',
    codeAndNameRequired: 'Code and name are required',
    loadDataError: 'Could not load data',
    systemRoleNote: ' (System role)',
    summary: 'Summary',
    permissionsCount: 'permissions',
    noPermissionsSelected: 'No permissions selected',
    actions: 'actions',
    // Two-tier role system
    systemRoles: 'System Roles',
    customerRoles: 'Customer Specific Roles',
    visibleToAll: 'Visible to all customers',
    visibleToOrganization: 'Visible to organization and descendants',
    showInactive: 'Show inactive roles',
    createAsSystemRole: 'Create as system role',
    customerRoleHint: 'Role will be created for your organization and descendants',
    systemTemplatesHint: 'Templates copied to new customers',
    templateStartingPoint: 'Starting template',
    selectTemplate: 'Select template...',
    templateRequired: 'Select a system template as starting point',
    templateHint: 'Choose a system template. You can then adjust modules and functions within your assigned services.',
    loadTemplateError: 'Could not load template permissions',
    permissionCeilingExceeded: 'Permission exceeds customer assigned services',
    roleActivated: 'Role activated!',
    roleDeactivated: 'Role deactivated!',
    toggleError: 'Failed to toggle role status',
    noRolesFound: 'No roles found',
    menu: 'Menu',
  },

  // Organizations
  organizations: {
    title: 'Organization Management',
    searchPlaceholder: 'Search organization...',
    selectOrganization: 'Select organization',
    chart: 'Organization Chart',
    chartDescription: 'View and edit organization hierarchy graphically',
    managementDescription: 'Create and manage organizations',
    createTopNode: 'Create top node',
    createSubOrganization: 'Create customer',
    organizationName: 'Name',
    description: 'Description',
    parentOrganization: 'Parent organization',
    topNode: 'Top node',
    customerTopNode: 'Customer top node',
    isTopNode: 'Top node',
    isTopNodeHint:
      'A customer top node can sit under a partner (e.g. Dialect) with its own settings. Check and save to enable.',
    authSettings: 'Authentication Settings',
    noOrganizations: 'No organizations yet. Create a top node to get started.',
    deleteConfirm: 'Are you sure you want to delete this organization and all its sub-organizations? If no vehicles are linked, the node is permanently removed; otherwise it is deactivated (soft delete).',
    editOrganization: 'Edit organization',
    addSub: 'Add customer',
    deleteOrganization: 'Delete',
    createButton: 'Create',
    updateButton: 'Update',
    customerLogo: 'Customer logo',
    uploadLogo: 'Upload logo',
    removeLogo: 'Remove logo',
    logoUploadSuccess: 'Logo uploaded',
    logoDeleteSuccess: 'Logo removed',
    organizationCreated: 'Organization created!',
    organizationUpdated: 'Organization updated!',
    organizationDeleted: 'Organization deleted!',
    organizationDeletedHard: 'Organization permanently deleted!',
    organizationDeletedSoft: 'Organization deactivated (soft delete) because linked vehicles exist.',
    createError: 'Could not create organization',
    updateError: 'Could not update organization',
    deleteError: 'Could not delete organization',
    loadHierarchyError: 'Could not load hierarchy',
    loadChildrenError: 'Could not load sub-organizations',
    nameRequired: 'Name is required',
    namePlaceholder: 'Organization name',
    descriptionPlaceholder: 'Description...',
    noParent: 'None (create as top node)',
    status: 'Status',
    active: 'Active',
    suspended: 'Suspended',
    inactive: 'Inactive',
    loginParameters: 'Login Parameters',
    authMethod: 'Authentication Method',
    passwordAuth: 'Password',
    twoFactorAuth: 'Two-Factor Authentication',
    samlAuth: 'SAML2',
    oidcAuth: 'OpenID Connect (e.g. Entra ID)',
    allowedDomains: 'Allowed Domains',
    allowedDomainsPlaceholder: 'e.g. acme.com, intranet.acme.com',
    allowedDomainsHint: 'Separate multiple domains with comma',
    organizationIdentifier: 'Organization Identifier',
    organizationIdentifierPlaceholder: 'e.g. acme',
    organizationIdentifierHint: 'Used for identification via query parameter (?org=)',
    additionalInfo: 'Additional Information',
    address: 'Address',
    addressPlaceholder: 'e.g. Main Street 1',
    postNr: 'Postal Code',
    postNrPlaceholder: 'e.g. 12345',
    town: 'City',
    townPlaceholder: 'e.g. Stockholm',
    orgNr: 'Organization Number',
    orgNrPlaceholder: 'e.g. 556123-4567',
    contact: 'Contact Person',
    contactPlaceholder: 'e.g. Anna Andersson',
    contacttel: 'Contact Phone',
    contacttelPlaceholder: 'e.g. +46-8-123456',
    email: 'Email',
    emailPlaceholder: 'e.g. info@example.com',
    carRental: 'Car Rental Company',
    costNo: 'Cost Number',
    costNoPlaceholder: 'e.g. KST-123',
    move: 'Move',
    moveOrganization: 'Move Organization',
    moveOrganizationDescription: 'Move the organization and all its sub-organizations to a new parent organization. All settings will be preserved.',
    newParent: 'New Parent Organization',
    selectParent: 'Select parent organization',
    moveButton: 'Move',
    organizationMoved: 'Organization moved!',
    moveError: 'Could not move organization',
    loadDataError: 'Could not load data',
    loadError: 'Could not load organizations',
    importError: 'Could not import file',
    importModalTitle: 'Import organizations',
    importModalSubtitle: 'Under',
    importDropzone: 'Drag and drop an Excel file (.xlsx) here, or click to choose a file',
    importStart: 'Import',
    importNoFile: 'Select a file before importing',
    importFileError: 'Could not read the file. Check that it is a valid Excel file.',
    importFileSelected: 'Selected file',
    importPreviewTitle: 'Preview',
    importPreviewCount: 'rows to import',
    importPreviewScrollHint: 'Showing up to 20 rows at a time — scroll to see more',
    importColumnName: 'Name',
    importColumnParent: 'Parent organization',
    importColumnContact: 'Contact person',
    importColumnPhone: 'Phone',
    importColumnEmail: 'Email',
    importColumnCostNo: 'Cost center',
    importCreated: 'organizations created',
    importErrors: 'errors',
    importRowError: 'Row',
    importOrganizations: 'Import organizations',
    importOrganizationsTooltip: 'Import sub-organizations from Excel',
    downloadTemplate: 'Download import template',
    downloadTemplateTooltip: 'Download Excel template for organization import',
    fuelCompanies: {
      tabTitle: 'Fuel Companies',
    },
    fuelAccounts: {
      loadError: 'Could not load fuel accounts',
      saveError: 'Could not save fuel accounts',
      saved: 'Saved',
      savedWithLinks: 'Saved. {{count}} fuel transactions were linked to the organization.',
      fillAll: 'Select a fuel company and enter customer number',
      alreadyAdded: 'That fuel company is already added',
      empty: 'No fuel accounts linked. Add fuel companies and customer numbers below.',
      fuelCompany: 'Fuel company',
      selectCompany: 'Select fuel company',
      customerNumber: 'Customer number',
      customerNumberPlaceholder: 'e.g. 12345',
      add: 'Add',
    },
    contactRoles: {
      title: 'Responsible / Contact Roles',
      noAssignment: 'None assigned',
      addCustomRole: 'Add custom role',
      codeRequired: 'Code is required',
      codePlaceholder: 'e.g. regional_manager',
      roleName: 'Role name',
      description: 'Description',
      createRole: 'Create role',
      roleCreated: 'Role created',
      roleDeleted: 'Role deleted',
      confirmDeleteRole: 'Delete this role?',
      attestationResponsible: 'Attestation responsible',
      manager: 'Manager',
      localVehicleResponsible: 'Local vehicle responsible',
      fleetManager: 'Fleet manager',
      maintenanceResponsible: 'Maintenance responsible',
      servicePersonnel: 'Service personnel',
    },
    bookingSettings: 'Booking settings',
    drivingLog: {
      tabTitle: 'Driving log settings',
      title: 'Driving log',
      intro:
        'Defaults for the whole customer tree. When linking vehicles to users, service hours apply unless changed manually. Reminders and submission follow these settings.',
      serviceHoursTitle: 'Default service hours (remaining time is private)',
      serviceStart: 'Service start',
      serviceEnd: 'Service end',
      serviceHoursHelp: 'Applies as the default for all vehicles until changed per vehicle or at assignment time.',
      cutOffEnabled: 'Enable monthly cut-off for submission',
      cutOffEnabledHelp: 'When disabled, no monthly cut-off is used in the system for this top node.',
      cutOffDay: 'Cut-off day of month',
      cutOffDayHelp: 'Example: 5 means by the 5th each month. Use 1–28 to avoid short-month edge cases.',
      reminderDays: 'Reminder (days before cut-off)',
      reminderHelp:
        'Days before the cut-off when the reminder is sent. Push goes to the app (drivers and users with driving log access). Email goes to the forwarding address.',
      forwardingEmail: 'Default forward-to email',
      forwardingEmailPlaceholder: 'e.g. finance@company.com; hr@company.com',
      forwardingEmailHelp:
        'Used as the prefilled forwarding destination when a driving log is sent. Separate multiple addresses with a semicolon (;).',
      lockAfterSubmit: 'Lock driving log after submission',
      lockAfterSubmitHelp:
        'Locks editing when the driving log is submitted manually, and when the monthly report is emailed on the cut-off day. The reminder (email/push) does not lock.',
      odometerAdjustmentLock: 'Block odometer adjustment during closing period',
      odometerAdjustmentLockHelp: 'When enabled, the odometer cannot be adjusted manually on the 1st–3rd and 26th–end of month (closing/attestation period). Disabled by default.',
    },
    reminders: {
      tabTitle: 'Subscriptions',
      title: 'Subscriptions',
      intro:
        'Applies to the whole customer tree under this top node. A disabled service creates neither assignments nor messages. Notification controls email and push.',
      service: 'Service',
      notify: 'Notification',
      inspection: 'Inspection',
      inspectionHelp: 'Reminder when inspection is due within 40 days.',
      tax: 'Vehicle tax',
      taxHelp: 'Reminder when tax must be paid within 45 days.',
    },
    automaticService: 'Automatic service',
    cabinetSettings: 'Cabinet settings',
    cabinetHasCabinet: 'Customer has key cabinets',
    cabinetHasCabinetHelp: 'Enables cabinets for the top node. The first time, open the cabinet with KeyWin’s built-in SuperAdmin and insert the tags. Then only tags in that cabinet are synced to the customer SuperAdmin and the cabinet LocalAdmin (KeyWin users, not web administrators).',
    cabinetEnabled: 'Cabinets enabled for the customer',
    cabinetDisabled: 'Cabinets disabled for the customer',
    cabinetEnableError: 'Could not change cabinet setting',
    cabinetSuperAdminPin: 'SuperAdmin PIN',
    cabinetSuperAdminPinHelp: '8 digits, unique in the system. Keypad PIN after the cabinet tags are synced to the SuperAdmin user. Does not grant KeyWin web login. First tag insertion uses the built-in SuperAdmin code that can always open every cabinet.',
    cabinetPinCopied: 'PIN copied',
    cabinetGeneratePin: 'Generate PIN',
    cabinetPinSync: 'Save to KeyWin',
    cabinetPinSaved: 'SuperAdmin PIN saved in KeyWin',
    cabinetPinGenerated: 'New unique SuperAdmin PIN created and saved in KeyWin',
    cabinetPinInvalid: 'PIN must be exactly 8 digits',
    cabinetPinSaveError: 'Could not save SuperAdmin PIN',
    cabinetWebAdmin: 'Web login (KeyWin admin)',
    cabinetUsername: 'Username',
    cabinetPassword: 'Password',
    cabinetPasswordHelp: 'Used to sign in to the KeyWin web interface.',
    cabinetPasswordCopied: 'Password copied',
    cabinetRegeneratePassword: 'Generate new password',
    cabinetRegenerateConfirm: 'Generate a new web password? The current one will stop working.',
    cabinetRegenerateError: 'Could not generate a new password',
    cabinetLinkedCabinets: 'Linked cabinets',
    cabinetNoLinkedCabinets: 'No cabinets are linked to the customer yet. Do that under Key management.',
    cabinetSyncTags: 'Sync tags',
    cabinetSyncTagsHelp: 'Connects only tags KeyWin sees in this cabinet to LocalAdmin and the top-node SuperAdmin. In KeyWin, check Users, not Groups.',
    cabinetSyncTagsSuccess: '{{count}} tags connected to SuperAdmin and LocalAdmin',
    cabinetSyncTagsEmpty: 'No tags in the cabinet yet. Open with the built-in SuperAdmin, insert the tags, then sync again.',
    cabinetSyncTagsError: 'Could not sync tags',
    defaultTimelineView: 'Timeline as default view in Customer Service',
    defaultTimelineViewHelp: 'List is always the default. Tick to show Timeline first in Customer Service for this organization. Users can always switch view themselves.',
    maxRecurringBookings: 'Max recurring bookings',
    maxRecurringBookingsHelp: 'Maximum number of future bookings per recurring series (e.g. same time every week). 0 or empty = no limit.',
    noBookingsBefore: 'No bookings before',
    noBookingsAfter: 'No bookings after',
    noBookingsHelp: '0 or empty = no restriction.',
    minLengthHours: 'Minimum length (hours)',
    maxLengthHours: 'Maximum length (hours)',
    lengthHelp: '0 or empty = no restriction.',
    bookableMonthsForward: 'Bookable months forward',
    bookableMonthsHelp: '0 or empty = no restriction.',
    cancellationChargeHours: 'Cancellation charge (hours before start)',
    cancellationChargeHelp: 'If the customer cancels within this time before start, the booking can be charged but the vehicle is released. 0 or empty = no such rule.',
    noShowReleaseMinutes: 'No-show: release slot (minutes after start)',
    noShowReleaseMinutesHelp: 'If the vehicle is not checked out within this many minutes after booking start, the slot is released; the booking can still be invoiced. 0 = disabled. New pickup locations get this value pre-filled (editable per location).',
    bookingRestMinutes: 'Rest time between bookings (minutes)',
    bookingRestMinutesHelp: 'Minimum minutes after a booking ends before the same vehicle can be booked again. 0 or empty = no rest time.',
    bookingMinLeadTimeHours: 'Minimum lead time (hours)',
    bookingMinLeadTimeHoursHelp: 'Applies only to requests handled by the car rental company. The booking must start at least this many hours in the future. 0 or empty = no restriction.',
    bookingPolicyHtml: 'Booking policy',
    bookingPolicyHtmlHelp: 'Text that bookers must open and confirm before booking (in addition to driving licence). Leave empty if no policy is required.',
  },

  // User Manual
  manual: {
    title: 'User Manual',
    searchPlaceholder: 'Search for functions or menus...',
    mainMenu: 'Main Menu',
    tools: 'Tools',
    administration: 'Administration',
    path: 'Path',
    availableActions: 'Available Actions',
    noResults: 'No results found',
    icons: 'Available Icons',
    iconsDescription: 'Here are all the icons available in the Heroicons library. You can choose any icon to use in the system.',
    navigationIcons: 'Navigation Icons',
    arrowsIcons: 'Arrow Icons',
    actionsIcons: 'Action Icons',
    adminIcons: 'Administrative Icons',
    communicationIcons: 'Communication Icons',
    filesIcons: 'File Icons',
    statusIcons: 'Status Icons',
    mediaIcons: 'Media Icons',
    otherIcons: 'Other Icons',
    guides: {
      title: 'Guides and processes',
      deviceVehicleTitle: 'Moving a device to another vehicle',
      deviceVehicleIntro: 'What happens when a GPS device is moved from one vehicle to another, and how to do it manually.',
      autoHeading: 'What happens automatically',
      autoSteps: [
        'When a device is moved to another vehicle, the system detects it by itself.',
        'The device is unlinked from the old vehicle and linked to the new one.',
        'If it is a brand new vehicle, it is created automatically with the correct details (make, model, reg. no.).',
        'If the old vehicle had a driver, the driver follows along to the new vehicle.',
        'Everything is logged so trips end up on the correct vehicle and driver.',
      ],
      manualHeading: 'Moving a device manually',
      manualSteps: [
        'Go to Administration → Device Management.',
        'Find the device and choose to link it to the new vehicle.',
        'If the new vehicle already has a device, you can swap the devices or unlink the old one.',
        'Confirm – the device is moved to the new vehicle and unlinked from the old one.',
        'The old vehicle becomes free and the new vehicle now shows the device.',
      ],
    },
  },

  // Icons
  icons: {
    fleet: 'Vehicle Fleet',
    statistics: 'Statistics',
    trips: 'Trips',
    user: 'User',
    manual: 'User Manual',
    search: 'Search',
    notification: 'Notifications',
    moon: 'Dark Mode',
    sun: 'Light Mode',
    arrowLeft: 'Back',
    eye: 'Show',
    eyeSlash: 'Hide',
    settings: 'Settings',
    database: 'Database',
    sync: 'Sync',
    userGroup: 'User Group',
  },

  // Translations Management
  translations: {
    title: 'Translations',
    key: 'Key',
    swedish: 'Swedish',
    english: 'English',
    editTranslation: 'Edit translation',
    saveSuccess: 'Translation saved',
    saveError: 'Could not save translation',
    searchPlaceholder: 'Search for key or text...',
    noTranslations: 'No translations found',
    noMatchSearch: 'No translations matched the search',
    exportFiles: 'Export to files',
    exportNoteLabel: 'Note',
    exportNote: 'Translations are stored in files (sv.ts and en.ts). Use "Export to files" to download updated files, or edit the files directly in code.',
    total: 'Total',
    filtered: 'filtered from',
    updatedNote: 'Translation updated! (Note: Changes must be saved in the files manually)',
  },

  // Dashboard
  dashboard: {
    title: 'Dashboard',
    activeVehicles: 'Active Vehicles',
    totalDistance: 'Total Distance',
    averageSpeed: 'Average Speed',
    liveMap: 'Live Map',
    liveTracking: 'Live Tracking',
    mapComingSoon: 'OpenStreetMap integration coming soon',
    addressUnavailable: 'Address could not be retrieved',
    edit: 'Edit',
    editDashboard: 'Edit dashboard',
    save: 'Save',
    cancel: 'Cancel',
    addWidget: 'Add widget',
    addFirstWidget: 'Add your first widget',
    emptyDashboard: 'Your dashboard is empty',
    emptyDashboardDescription: 'Add widgets to customize your dashboard. You can choose from a variety of widgets that display information about your vehicles and organization.',
    dragHere: 'Drop widget here',
    layoutSaved: 'Layout saved',
    layoutSaveFailed: 'Failed to save layout',
    widgetAdded: 'Widget added',
    widgetRemoved: 'Widget removed',
    widgetNotAvailable: 'Widget not available',
    widgetNoPermission: 'You do not have permission for this widget',
    saving: 'Saving...',
    favorites: 'Favorites',
    reorderFavorites: 'Reorder favorite',
    reorderFavoritesHint: 'drag to change order',
  },

  // Live Tracking
  liveTracking: {
    title: 'Live Tracking',
    description: 'View vehicles in real-time on map with organization tree view',
    search: 'Search vehicles...',
    searchHits: 'Search results',
    lists: 'Lists',
    alarms: 'Alarms',
    online: 'Online',
    offline: 'Offline',
    all: 'All',
    noVehicles: 'No vehicles selected',
    loading: 'Loading...',
    fitBounds: 'Fit map view',
    status: 'Status',
    regNumber: 'Reg. no.',
    name: 'Name',
    receivedTime: 'Received time',
    location: 'Location',
    speed: 'Speed',
    direction: 'Direction',
    acc: 'ACC',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    live: 'Live',
    connecting: 'Connecting…',
    liveDelayed: 'Live (delayed)',
    dataStaleWarning:
      'Real-time data has not updated for {{minutes}} min. The map may be temporarily out of date (network, device, or service). If this persists, contact support.',
    pollingFallback: 'Position updates are polled periodically as a fallback when Live is active.',
    saveAsStartView: 'Save this view as start view',
    goToStartView: 'Go to saved start view',
    exitFullscreen: 'Exit fullscreen',
    fullscreen: 'Fullscreen',
    driving: 'driving',
    noDataOver1h: 'No data >1h',
    loadingMap: 'Loading map...',
    noVehiclesSelected: 'No vehicles selected',
    regNr: 'Reg. no.',
    statusDriving: 'Driving',
    statusStationary: 'Stationary',
    statusStaleParked: 'Parked, no data >1h',
    statusLostWhileDriving: 'Was driving, no contact >1h',
    statusNoContact: 'No contact >6h',
    markerLegendDriving: 'Driving',
    markerLegendStationary: 'Stationary (<1h)',
    markerLegendStaleParked: 'Parked, no data >1h',
    markerLegendLostWhileDriving: 'Was driving, no contact >1h',
    markerLegendNoContact: 'No contact >6h',
    lostWhileDrivingHint: 'Last known driving — no data for {{minutes}} min',
    lastSeenMinutesAgo: 'Last seen: {{minutes}} min ago',
    ignitionOn: 'On',
    ignitionOff: 'Off',
    mapNotReady: 'Map is not ready',
    restoredToSavedView: 'Restored to saved view',
    areaFilter: {
      title: 'Filter by area',
      open: 'Area filter',
      active: '{{count}} vehicles in area',
      clear: 'Clear area filter',
      help: 'Draw an area on the map (at least 3 points). Choose placement location or current position.',
      modePlacement: 'Placement location in area',
      modeCurrent: 'Currently in area',
      privateExcluded: 'Vehicles marked private by schedule are excluded.',
      apply: 'Filter',
      resultCount: '{{count}} vehicles match the filter',
      error: 'Could not filter vehicles in area',
      placementMarker: 'Placement',
      placementClusterCount: '{{count}} vehicles at same location',
      drawOnMapHint: 'Click and drag on the map to draw a rectangle.',
      placementModeActive: 'Showing placement location (purple dots)',
    },
  },

  // CRM
  crm: {
    title: 'CRM',
    crmModule: 'Customer Management',
    economyModule: 'Economy',
    // Dashboard
    customers: 'Customers',
    customersDescription: 'Manage customer cards and contacts',
    projects: 'Projects',
    projectsDescription: 'Ongoing and planned projects',
    procurements: 'Procurements',
    procurementsDescription: 'Public procurement processes',
    pipeline: 'Sales Pipeline',
    pipelineDescription: 'Sales funnel and leads',
    economyOverview: 'Economic Overview',
    economyOverviewDescription: 'Rolling 12, trends and revenue',
    invoices: 'Invoice Basis',
    invoicesDescription: 'Generate and manage invoices',
    pricelist: 'Price List',
    pricelistDescription: 'Services, materials and pricing',
    scbTesting: 'SCB API Testing',
    scbTestingDescription: 'Test SCB API endpoints and view results',
    moduleCatalog: {
      title: 'Module Catalog',
      dashboardDescription: 'Overview of all modules and functions',
      searchPlaceholder: 'Search module, purpose, function...',
      sync: 'Sync',
      syncDone: 'Synced: {{inserted}} new, {{updated}} updated',
      syncError: 'Sync failed',
      addCustom: 'Add custom',
      editEntry: 'Edit module',
      exportPdf: 'Export PDF',
      exportError: 'Export failed',
      exportGenerated: 'Generated {{date}}',
      exportPageOf: 'Page {{page}} of {{pages}}',
      empty: 'No modules found.',
      customBadge: 'Custom',
      appBadge: 'App',
      confirmDelete: 'Delete/deactivate this entry?',
      systemKey: 'System (key)',
      systemName: 'System name',
      name: 'Module name',
      purpose: 'Purpose',
      functions: 'Functions',
      requirementsNotes: 'Requirements notes (Must/Should)',
    },
    // Customer list
    searchCustomerPlaceholder: 'Search customer, org no, city...',
    allStatus: 'All status',
    totalLabel: 'Total',
    contactsLabel: 'Contacts',
    notesLabel: 'Notes',
    statusAndType: 'Status & type',
    signupDomains: 'Signup email domain',
    signupDomainsHelp:
      'One domain per customer. If a user self-registers with an email on this domain, the account is linked to the customer organization with the Viewer role.',
    signupDomainsEmpty: 'No domain set',
    signupDomainsListTitle: 'Saved signup domain',
    signupDomainsAddedAt: 'Added {{date}}',
    signupDomainsNoOrg:
      'The customer needs a linked organization (active customer) before you can add signup domains.',
    signupDomainPlaceholder: 'e.g. company.com',
    signupDomainAdd: 'Add',
    signupDomainSave: 'Save domain',
    signupDomainSaved: 'Domain saved',
    signupDomainRemove: 'Remove domain',
    signupDomainsLoadError: 'Could not load domains',
    signupDomainAdded: 'Domain added',
    signupDomainAddError: 'Could not add domain',
    signupDomainRemoved: 'Domain removed',
    signupDomainRemoveError: 'Could not remove domain',
    newCustomer: 'New customer',
    createCustomer: 'Create new customer',
    editCustomer: 'Edit customer',
    deleteCustomer: 'Delete customer',
    deleteCustomerConfirm: 'Are you sure you want to delete this customer?',
    // Contacts
    contacts: 'Contacts',
    newContact: 'New contact',
    editContact: 'Edit contact',
    deleteContact: 'Delete contact',
    // Status
    statusProspect: 'Prospect',
    statusActive: 'Active',
    statusPaused: 'Paused',
    statusAtRisk: 'At Risk',
    billingStartsOn: 'Billing starts on',
    billingStartsOnShort: 'Billing from',
    billingStartsOnHelp: 'Date when the customer should start being billed. Leave empty if not decided yet.',
    billingStartsOnNotSet: 'Not set',
    // Type
    typeMunicipality: 'Municipality',
    typeRegion: 'Region',
    typePrivate: 'Private',
    typePartner: 'Partner',
    typeCarRental: 'Car rental',
    carRental: 'Car rental',
    // Federation / SSO
    federation: 'Federation',
    federationDescription: 'Configure Single Sign-On (SSO) for the customer',
    saml: 'SAML 2.0',
    oidc: 'OpenID Connect',
    samlConfig: 'SAML Configuration',
    oidcConfig: 'OIDC Configuration',
    entityId: 'Entity ID / Issuer',
    ssoUrl: 'SSO URL',
    sloUrl: 'SLO URL (Single Logout)',
    certificate: 'IdP Certificate',
    nameIdFormat: 'NameID Format',
    issuerUrl: 'Issuer URL',
    clientId: 'Client ID',
    clientSecret: 'Client Secret',
    scopes: 'Scopes',
    provider: 'Provider',
    activateSaml: 'Enable SAML 2.0',
    activateOidc: 'Enable OpenID Connect',
    spMetadata: 'Service Provider Metadata',
    redirectUri: 'Redirect URI',
    configuredButInactive: 'Configured but inactive',
    notConfigured: 'Not configured',
    saveSaml: 'Save SAML',
    updateSaml: 'Update SAML',
    saveOidc: 'Save OIDC',
    updateOidc: 'Update OIDC',
    deleteSaml: 'Delete SAML',
    deleteOidc: 'Delete OIDC',
    samlCreated: 'SAML configuration created',
    samlUpdated: 'SAML configuration updated',
    samlDeleted: 'SAML configuration deleted',
    oidcCreated: 'OIDC configuration created',
    oidcUpdated: 'OIDC configuration updated',
    oidcDeleted: 'OIDC configuration deleted',
    customerMustBeActivated: 'Customer must be activated to configure federation',
    // New translations for forms
    municipality: 'Municipality',
    region: 'Region',
    privateCompany: 'Private company',
    partner: 'Partner',
    loadCustomerError: 'Could not load customer',
    fetchCorporateStructureError: 'Could not fetch corporate structure',
    customerUpdated: 'Customer updated',
    saveContactError: 'Could not save contact',
    ssoUrlAndCertRequired: 'SSO URL and certificate required',
    saveSamlError: 'Could not save SAML configuration',
    samlConfigRemoved: 'SAML configuration removed',
    deleteSamlError: 'Could not delete SAML configuration',
    issuerAndClientIdRequired: 'Issuer URL and Client ID required',
    saveOidcError: 'Could not save OIDC configuration',
    oidcConfigRemoved: 'OIDC configuration removed',
    deleteOidcError: 'Could not delete OIDC configuration',
    createApiKeyError: 'Could not create API key',
    deactivateApiKeyError: 'Could not deactivate API key',
    updateEndpointAccessError: 'Could not update endpoint access',
    loadPricelistError: 'Could not load pricelist',
    saveProductError: 'Could not save product',
    duplicateProductCode: 'The product code already exists – choose a unique code',
    deleteProductError: 'Could not delete product',
    // Pricelist tabs
    tabProducts: 'Products & Services',
    tabPackages: 'System packages',
    // Categories
    categoriesTitle: 'Categories',
    allProducts: 'All products',
    // Product table headers
    colCode: 'Code',
    colName: 'Name',
    colType: 'Type',
    colPricingModel: 'Pricing model',
    colPrice: 'Price',
    colUnit: 'Unit',
    newProduct: 'New product',
    noProductsInCategory: 'No products in this category',
    // Product modal
    editProduct: 'Edit product',
    // Product type labels
    productTypeService: 'Service',
    productTypeMaterial: 'Material',
    productTypeBundle: 'Bundle',
    // Pricing type labels
    pricingHourly: 'Per hour',
    pricingMonthly: 'Per month',
    pricingPerVehicle: 'Per vehicle',
    pricingPerUser: 'Per user',
    pricingOneTime: 'One-time',
    pricingCustom: 'Custom',
    // Product form labels
    labelCode: 'Code',
    labelCategory: 'Category',
    labelName: 'Name *',
    labelDescription: 'Description',
    labelProductType: 'Product type',
    labelPricingModel: 'Pricing model',
    labelPrice: 'Price (SEK)',
    labelUnit: 'Unit',
    noCategory: 'No category',
    unitPlaceholder: 'pcs, hrs, month...',
    productCreated: 'Product created',
    productUpdated: 'Product updated',
    productDeleted: 'Product deleted',
    confirmDeleteProduct: 'Delete product?',
    // CustomerDetail
    customerNotFound: 'Customer not found',
    backToCustomerList: '← Back to customer list',
    activateCustomer: 'Activate customer',
    activating: 'Activating...',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    tabInfo: 'Customer info',
    tabServices: 'Services',
    tabStatistics: 'Statistics',
    tabFederation: 'Federation',
    companyInfoTitle: 'Company information',
    contactsTitle: 'Contacts',
    addContact: 'Add',
    noContacts: 'No contacts',
    primaryBadge: 'Primary',
    noNotes: 'No notes',
    labelStatus: 'Status',
    labelCustomerType: 'Customer type',
    companyRegister: 'Company register',
    loadingCheckbiz: 'Loading data from Checkbiz...',
    noCheckbizData: 'No data found for organization number {{orgNumber}}',
    checkbizApiNote: '(Checkbiz API may be unconfigured or the organization number is not in the registry)',
    vehiclesInGroup: 'Vehicles in group ({{total}} total)',
    companyGroupTitle: 'Corporate structure ({{count}} companies)',
    fetchAndSave: 'Fetch & Save',
    orgNumber: 'Org no',
    ownerShare: 'Ownership',
    labelOrgNumber: 'Organization number',
    labelAddress: 'Address',
    labelPostalCode: 'Postal code',
    labelCity: 'City',
    labelWebsite: 'Website',
    labelSniCode: 'SNI code',
    labelSizeClass: 'Size class',
    // CustomerWizard
    wizard: {
      basicInfo: 'Basic information',
      addressContact: 'Address & Contact',
      contactPerson: 'Contact person',
      pricing: 'Pricing',
      summary: 'Summary',
      companyName: 'Company name *',
      orgNumber: 'Organization number',
      customerType: 'Customer type *',
      statusLabel: 'Status *',
      statusProspect: 'Prospect',
      statusProspectNote: '(no organization created)',
      statusActive: 'Active',
      statusActiveNote: '(creates organization)',
      address: 'Address',
      postalCode: 'Postal code',
      city: 'City',
      website: 'Website',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      emailRequired: 'Email is required for the contact person',
      phone: 'Phone',
      mobile: 'Mobile',
      role: 'Role',
      sendWelcomeEmail: 'Send welcome email and create user account',
      sendWelcomeEmailDesc: 'The contact person receives an email with a link to activate their account as Administrator in DQFleet.',
      pricingModel: 'Pricing model',
      monthlyFee: 'Monthly fee (SEK)',
      pricePerVehicle: 'Price per vehicle (SEK/month)',
      pricePerUser: 'Price per user (SEK/month)',
      perVehicle: 'Per vehicle',
      customPricing: 'Custom',
      notes: 'Notes',
      noOrgNumber: 'No org. number',
      statusActiveLabel: '✓ Active (organization created)',
      pricingLabel: 'Pricing model',
      back: 'Back',
      next: 'Next',
      creating: 'Creating...',
      createCustomer: 'Create customer',
      municipality: 'Municipality',
      region: 'Region',
      partner: 'Partner',
      carRental: 'Car rental',
      roleDecisionMaker: 'Decision maker',
      roleTechnical: 'Technical contact',
      roleBilling: 'Billing',
      roleOther: 'Other',
    },
    loadSystemConfigError: 'Could not load system configuration',
    changeSystemStatusError: 'Could not change system status',
    changeModuleStatusError: 'Could not change module status',
    loadCustomersError: 'Could not load customers',
    // Tabs
    tabs: {
      devices: 'Devices',
      vehicleOrg: 'Org No. Vehicles',
    },
    // Org numbers for vehicle management
    vehicleOrg: {
      title: 'Organization numbers for vehicle management',
      orgNumber: 'Organization number',
      companyName: 'Company name',
      companyNamePlaceholder: 'Optional company name',
      source: 'Source',
      sourceKoncern: 'Group',
      sourceManual: 'Manual',
      fetchFromKoncern: 'Fetch from corporate structure',
      addManual: 'Add manually',
      empty: 'No organization numbers registered',
      emptyHint: 'Click "Fetch from corporate structure" to populate from group companies',
      alreadyExists: 'Organization number already registered',
      totalCount: '{{count}} organization numbers registered',
    },
    // Customer devices tab
    customerDevices: {
      inVehicle: 'In vehicle',
      customerStock: 'Customer stock',
      total: 'Total devices',
      searchPlaceholder: 'Search IMEI, reg.nr...',
      allStatuses: 'All statuses',
      noDevices: 'No devices linked to this customer',
      noMatch: 'No devices matched the filter',
      updated: 'Updated',
    },
  },

  projects: {
    devTitle: 'Product Development',
    newProject: 'New project',
    projectName: 'Project name',
    selectCustomer: 'Select customer',
    searchPlaceholder: 'Search projects...',
    allStatuses: 'All statuses',
    noProjects: 'No projects found',
    openTasks: 'open tasks',
    addTask: 'Add task',
    editTask: 'Edit task',
    editProject: 'Edit project',
    taskTitle: 'Task',
    subtasks: 'Subtasks',
    subtaskPlaceholder: 'New subtask...',
    subtasksProgress: '{{done}} of {{total}} done',
    milestone: 'Milestone',
    milestones: 'Milestones',
    milestoneName: 'Milestone name',
    priority: 'Priority',
    plannedStart: 'Planned start',
    plannedEnd: 'Planned end',
    budget: 'Budget',
    actualCost: 'Actual cost',
    billingType: 'Billing',
    hourlyRate: 'Hourly rate',
    billingTypes: {
      fixed: 'Fixed price',
      hourly: 'Hourly',
      milestone: 'Per milestone',
    },
    totalHours: 'Total hours',
    timeTracking: 'Time tracking',
    hours: 'Hours',
    logTime: 'Log time',
    billable: 'Billable',
    noTask: 'No task',
    dependsOn: 'Depends on',
    projectType: 'Project type',
    manager: 'Project manager',
    primaryContact: 'Primary contact',
    primaryContactHint: 'Users in the customer organization (not global administrators).',
    owner: 'Owner',
    assignedTo: 'Assigned to',
    notAssigned: 'Not assigned',
    globalAdmin: 'Global admin',
    managerCandidatesHint: 'Customer administrators and global administrators.',
    ownerCandidatesHint: 'Global administrators only.',
    releaseTarget: 'Target release',
    component: 'Component',
    featureCode: 'Feature code',
    tabs: {
      overview: 'Overview',
      kanban: 'Kanban',
      gantt: 'Gantt',
      milestones: 'Milestones',
      time: 'Time',
    },
    stats: {
      total: 'Total',
      active: 'Active',
      myTasks: 'My tasks',
      overdue: 'Overdue',
      hoursWeek: 'Hours this week',
    },
    status: {
      planned: 'Planned',
      active: 'Active',
      on_hold: 'On hold',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    taskStatus: {
      backlog: 'Backlog',
      todo: 'To do',
      in_progress: 'In progress',
      review: 'Review',
      done: 'Done',
      cancelled: 'Cancelled',
    },
    priorityLevel: {
      low: 'Low',
      normal: 'Normal',
      high: 'High',
      urgent: 'Urgent',
    },
    types: {
      implementation: 'Implementation',
      migration: 'Migration',
      upgrade: 'Upgrade',
      integration: 'Integration',
      consulting: 'Consulting',
      other: 'Other',
    },
    components: {
      backend: 'Backend',
      frontend: 'Frontend',
      mobile: 'Mobile',
      gateway: 'Gateway',
      infra: 'Infrastructure',
      other: 'Other',
    },
    gantt: {
      task: 'Task',
      noData: 'No schedule – add dates to tasks',
      unassignedTasks: 'Tasks without milestone',
      holiday: 'Public holiday',
      dependencies: 'Dependencies',
      criticalPath: 'Critical path',
      tasks: 'tasks',
    },
  },

  // Fleet
  fleet: {
    title: 'Vehicle Fleet',
    overview: 'Vehicle Fleet',
    backToDeque: 'Back to Deque',
    overviewDescription: 'View and manage all vehicles',
    liveTrackingDescription: 'See vehicles in real-time on the map',
    vehicle: 'Vehicle',
    status: 'Status',
    position: 'Position',
    speed: 'Speed',
    noVehicles: 'No vehicles found',
    tryAllOrganizations: 'Try selecting "All organizations" in the filter.',
    loading: 'Loading vehicles...',
    error: 'Failed to load vehicles',
    searchPlaceholder: 'Search reg.no, IMEI, make, model, org, fuel type, postal code, city, vehicle category, equipment, owned, leased…',
    searchPlaceholderShort: 'Search reg.no, IMEI, make, model, reference, equipment, owned or leased …',
    ownershipColumn: 'Owned / leased / lease ended',
    ownershipOwned: 'Owned',
    ownershipLeased: 'Leased',
    ownershipLeaseEnded: 'Lease ended',
    filterBarLabel: 'Filter',
    columnFilterToggle: 'Column filter',
    columnFilterToggleHint: 'Show/hide a search box per column to filter the list',
    showing: 'Showing',
    of: 'of',
    vehicles: 'vehicles',
    page: 'Page',
    first: 'First',
    last: 'Last',
    lastSeen: 'Last seen',
    placementCity: 'City',
    referenceNumber: 'Reference',
    organization: 'Organization',
    connected: 'Connected',
    filterAll: 'All',
    filterConnected: 'Connected',
    filterDisconnected: 'Disconnected',
    filterAllOrganizations: 'All organizations',
    systemStatus: 'System status',
    filterPreset: 'Filter',
    filterPresetAll: 'All',
    filterPoolcars: 'All pool cars',
    filterMil: 'Check engine light (MIL)',
    filterTaxDue: 'Tax due within 45 days',
    filterInspectionDue: 'Inspection due within 40 days',
    filterUsageStatus: 'Status',
    exportVehicleRegister: 'Vehicle register',
    exportVehicleRegisterExcel: 'Excel',
    exportVehicleRegisterCsv: 'CSV',
    exportVehicleRegisterSuccess: 'Download started',
    exportVehicleRegisterError: 'Could not export vehicle register',
    exportCarInfoVehicleReportExcel: 'Vehicle report Excel',
    exportCarInfoVehicleReportSuccess: 'Vehicle report download started',
    exportCarInfoVehicleReportError: 'Could not export vehicle report',
    exportFleetReport: 'Vehicle report',
    exportFleetReportExcel: 'Excel',
    exportFleetReportCsv: 'CSV',
    exportFleetReportSuccess: 'Vehicle report downloaded',
    exportFleetReportError: 'Could not export vehicle report',
    // Status translations
    statusActive: 'Active',
    statusMaintenance: 'Maintenance',
    statusInactive: 'Inactive',
    statusRetired: 'Retired',
    // Functions
    functions: 'Functions',
    deactivate: 'Deactivate vehicle',
    activateVehicle: 'Reactivate vehicle',
    confirmActivate:
      'Reactivate vehicle {{reg}}?\n\nStatus will be set to active. If a GPS device is still linked, a new mount span is opened in device history so data can be attributed to the vehicle again.',
    activatedSuccess: 'has been reactivated',
    connectDevice: 'Connect device',
    connectKey: 'Connect key',
    subscribe: 'Subscribe to reports',
    milError: 'Engine fault - light on',
    milNoData: 'Data not available',
    milOk: 'No faults',
    fuelLevel: 'Fuel(%)',
    fuelEvTooltip: 'Fuel % (EV battery %)',
    evRange: 'Range',
    serviceDistance: 'Km to service',
    installedTires: 'Installed tires',
    imeiColumn: 'IMEI',
    imeiFirmware: 'Firmware',
    imeiConfig: 'Configuration',
    imeiUnknown: 'unknown',
    currentOdometer: 'Current odometer',
    flinkeyBoxColumn: 'flinkey box',
    transmissionColumn: 'Transmission',
    pickupPlacementColumn: 'Pickup location',
    businessResponsibilityColumn: 'Business responsibility',
    assignedUserColumn: 'Linked user',
    vehicleCategoryColumn: 'Vehicle category',
    alternativeFuelColumn: 'Alternative fuel',
    vehicleStatusColumn: 'Status',
    noUsageStatus: 'No status',
    columnSettings: {
      button: 'Columns',
      title: 'Table columns',
      description: 'Choose which columns to show and their order. Drag visible rows to reorder. Settings are saved to your user account.',
      fixedHint: 'The Vehicle column is always first and Functions always last.',
      searchPlaceholder: 'Search column...',
      noResults: 'No columns match your search',
      visibleColumns: 'Visible columns',
      hiddenColumns: 'Hidden columns',
      dragHint: 'Drag to reorder',
      reset: 'Reset to default',
      saveSuccess: 'Column settings saved',
      saveError: 'Could not save column settings',
      resetSuccess: 'Columns reset to default',
    },
    // Vehicle details
    make: 'Make',
    model: 'Model',
    year: 'Year',
    fuelType: 'Fuel Type',
    sitplatser: 'Seats',
    costCenter: 'Cost Center',
    initialOdometer: 'Initial Odometer (km)',
    initialOdometerHint: 'Odometer reading at registration',
    isPoolcar: 'Pool Vehicle',
    registrationNumber: 'Registration Number',
    // Fuel types
    fuelTypes: {
      petrol: 'Petrol',
      diesel: 'Diesel',
      electric: 'Electric',
      hybrid: 'Hybrid',
      pluginHybrid: 'Plug-in hybrid',
      gas: 'Gas',
      ethanol: 'Ethanol',
    },
    // Add vehicle
    addVehicle: {
      button: 'New Vehicle',
      title: 'Register New Vehicle',
      fetchCarInfo: 'Fetch from Car.info',
      fetched: 'Fetched',
      enterRegNumber: 'Enter registration number first',
      carInfoSuccess: 'Vehicle data fetched from Car.info',
      carInfoNotFound: 'Vehicle not found in Car.info',
      carInfoLoaded: 'Vehicle data loaded from Car.info',
      stolenWarning: 'WARNING: This vehicle is reported stolen!',
      drivingBanWarning: 'WARNING: This vehicle has a driving ban!',
      regRequired: 'Registration number is required',
      orgRequired: 'Please select an organization',
      selectOrg: 'Select organization',
      success: 'Vehicle registered!',
      error: 'Failed to create vehicle',
      save: 'Save Vehicle',
    },
  },

  // flinkey (digital key management)
  flinkey: {
    sectionTitle: 'flinkey (digital key)',
    attach: 'Connect flinkey',
    selectBox: '— Select box —',
    noBoxesForOrg: 'No Flinkey boxes registered for the organization',
    notLicensedForOrg: 'No Flinkey boxes registered for the organization',
    detach: 'Remove connection',
    assignKey: 'Assign key',
    boxIdPlaceholder: 'flinkey box id',
    assignModalTitle: 'Assign digital key',
    assignUser: 'User (ID)',
    validFrom: 'Valid from (optional)',
    validUntil: 'Valid until (optional)',
    assign: 'Assign',
    listLoadError: 'Could not load Flinkey boxes. Check network or permissions.',
    noBoxLinked: 'No Flinkey box linked to this vehicle',
    chooseBox: 'Choose box',
    changeBox: 'Change box',
    orgBoxCount: '{{count}} box(es) in the organization (including parent orgs)',
    searchBoxesPlaceholder: 'Search product id, serial number or registration…',
    searchNoHits: 'No boxes match your search',
    boxOnOtherVehicle: 'Linked to vehicle {{reg}} — select to move to this vehicle',
    boxUnassigned: 'Unassigned box',
    currentBoxBadge: 'This vehicle',
  },

  // Car.info Filter
  carInfoFilter: {
    title: 'Filter',
    addFilter: 'Add filter',
    noFilters: 'No filters selected',
    noFiltersAdded: 'No filters added',
    addFilterHint: 'Click "Add filter" to begin',
    enterValue: 'Enter value...',
    search: 'Search',
    clear: 'Clear',
    loadError: 'Failed to load filters',
    searchError: 'Search failed',
    vehiclesFound: 'vehicles found',
    filterButton: 'Car.info Filter',
    searchFields: 'Search fields...',
    searching: 'Searching...',
    searchButton: 'Search',
    cancel: 'Cancel',
    noFiltersConfigured: 'No filters configured',
    enableFiltersHint: 'Enable filters in SysAdmin → Car.info Filter',
    selectFilterValue: 'Select at least one filter value',
    vehiclesFoundCount: '{count} vehicles found',
    searchFailed: 'Search failed',
    allOption: '-- All --',
    otherCategory: 'Other',
    loadingValues: 'Loading values...',
  },

  // Car.info Filter Configuration (admin page)
  carInfoFilterConfig: {
    title: 'Car.info Filter Configuration',
    subtitle: 'Select which Car.info fields to show in the fleet filter.',
    activeFilters: 'Active filters',
    totalFields: 'Total fields',
    categories: 'Categories',
    showActiveOnly: 'Show active only',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    reload: 'Reload',
    fieldsCount: 'fields',
    noFieldsFound: 'No fields found',
    adjustSearch: 'Try changing the search or removing the filter',
    loadError: 'Could not load filter configuration',
    errorToggle: 'Could not update field',
    activated: 'Activated',
    deactivated: 'Deactivated',
    fields: 'fields',
    uncategorized: 'Uncategorized',
    examplePrefix: 'Ex',
  },

  // SMS
  sms: {
    title: 'Send SMS Command',
    message: 'Message',
    send: 'Send',
    sending: 'Sending...',
    sent: 'SMS sent!',
    sentSuccess: 'SMS sent!',
    sentDescription: 'The message has been sent to the device.',
    hint: 'Command starts with two spaces for authentication',
    noImei: 'Vehicle has no connected device (IMEI missing)',
    emptyMessage: 'Message cannot be empty',
    messageId: 'Message ID',
    status: 'Status',
    statusDelivered: 'Delivered',
    statusSent: 'Sent',
    statusPending: 'Pending',
    statusFailed: 'Failed',
    statusCancelled: 'Cancelled',
  },

  // Reports
  reports: {
    title: 'Reports',
    comingSoon: 'Reports coming soon...',
    bookingStats: {
      title: 'Booking Statistics',
      description: 'Volume, occupancy, request flow, average length, car classes and trend for bookings.',
      from: 'From',
      to: 'To',
      granularity: 'Granularity',
      granularityDay: 'Day',
      granularityWeek: 'Week',
      granularityMonth: 'Month',
      basis: 'Occupancy basis',
      basisCalendar: 'Calendar days',
      basisWorkdays: 'Workdays',
      pickupLocation: 'Pickup location',
      allPickupLocations: 'All locations',
      allOrganizations: 'All organizations',
      refresh: 'Refresh',
      loadError: 'Could not load booking statistics.',
      noData: 'No data for the period.',
      totalBookings: 'Total bookings',
      requestFlow: 'Request / assigned',
      requestSharePct: 'Request share: {{pct}}%',
      occupancy: 'Occupancy rate',
      avgLength: 'Avg length',
      uniqueBookers: 'Unique bookers',
      uniqueBookersSub: 'Account, RSID or name',
      totalKm: 'Distance (km)',
      vehicleCount: 'Vehicle count',
      noShow: 'No-show',
      noShowSub: 'Auto-released (not picked up in time)',
      serviceNotUsed: 'Service bookings unused',
      serviceNotUsedSub: 'Share of service bookings: {{pct}}%',
      byStatus: 'Volume by status',
      byCarClass: 'Distribution by car class',
      trend: 'Trend (volume)',
      topVehicles: 'Most booked vehicles',
    },
    energyConsumption: {
      title: 'Energy Consumption',
      description: 'Analyze fuel consumption by organization and vehicle',
    },
    speedCompliance: {
      title: 'Speed Compliance',
      description: 'Analyze vehicle adherence to speed limits',
    },
    vehicleWear: {
      title: 'Vehicle Wear',
      description: 'Analyze how driving behavior affects vehicle wear',
    },
    underutilized: {
      title: 'Underutilized Vehicles',
      description: 'Identify vehicles with low utilization',
    },
    co2: {
      title: 'CO₂ Emissions',
      description: 'Calculate and analyze CO₂ emissions',
    },
    geofenceZone: {
      title: 'Geofence Zone Report',
      description: 'Analyze how often vehicles are within zones',
    },
    fuelAnalysis: {
      title: 'Fuel Analysis',
      description: 'Analyze fuel costs and consumption from imported transactions',
      totalCost: 'Total cost',
      totalLiters: 'Total volume',
      costPerMil: 'Cost per 10 km',
      avgPricePerLiter: 'Average price',
      cost: 'Cost',
      liters: 'Liters',
      fuelType: 'Fuel type',
      costByOrg: 'Cost per organization',
      byFuelType: 'By fuel type',
      costTrend: 'Cost trend (per 10 km)',
      costVolumeTrend: 'Cost and volume per month',
    },
    fleetTco: {
      title: 'Fleet Cost Analysis (TCO)',
      description: 'Total cost of ownership per vehicle: fuel, service, leasing and insurance',
      totalCost: 'Total cost',
      costPerKm: 'Cost per km',
      costPerMonth: 'Cost per month',
      totalDistance: 'Total distance',
      fuel: 'Fuel',
      service: 'Service',
      leasing: 'Leasing',
      insurance: 'Insurance',
      tax: 'Tax/malus',
      total: 'Total',
      breakdown: 'Cost breakdown',
      byFuelType: 'Cost per vehicle type',
      vehicle: 'Vehicle',
      tabs: {
        overview: 'Overview',
        byType: 'By vehicle type',
      },
    },
    executive: {
      title: 'Executive Dashboard',
      statisticsTitle: 'Statistical overview',
      description: 'Fleet-wide KPIs with costs, utilization and CO₂',
      period: 'Period',
      periodWeek: 'Week',
      periodMonth: 'Month',
      periodQuarter: 'Quarter',
      periodYear: 'Year',
      totalDistance: 'Total distance',
      totalFuelCost: 'Fuel cost',
      totalTco: 'Total cost of ownership',
      utilization: 'Utilization',
      co2: 'CO₂ emissions',
      activeVehicles: 'Active vehicles',
      costTrend: 'Cost trend',
      co2Trend: 'CO₂ trend',
      topDeviations: 'Top 5 deviations',
      exportPdf: 'Export PDF',
      noDeviations: 'No deviations for the period',
    },
    vehicleKpi: {
      title: 'Vehicle KPIs',
      description: 'Summary of key metrics per vehicle, month and year',
      tabs: {
        overview: 'Organization Overview',
        vehicleYear: 'Year Report/Vehicle',
        vehicleMonth: 'Vehicle/Month',
      },
      filters: {
        organization: 'Organization',
        year: 'Year',
        vehicle: 'Vehicle',
        month: 'Month',
      },
      summary: {
        totalVehicles: 'Total Vehicles',
        totalDistance: 'Total Distance',
        totalFuel: 'Total Fuel',
        avgFuelPer100km: 'Avg L/100km',
        avgElectricShare: 'Avg electric share',
        totalCo2: 'Total CO₂',
        totalDuration: 'Total Drive Time',
        totalIdling: 'Total Idle Time',
        daysDriven: 'Days Driven',
      },
      columns: {
        registrationNumber: 'Reg. No.',
        organization: 'Group',
        make: 'Make',
        model: 'Model',
        period: 'Period',
        date: 'Date',
        month: 'Month',
        distanceKm: 'Distance (km)',
        fuelL: 'Fuel (L)',
        fuelPer100km: 'Avg (L/100km)',
        electricSharePct: 'Electric share',
        co2Kg: 'CO₂ (kg)',
        daysDriven: 'Days Driven',
        kmPerDay: 'Km/day',
        duration: 'Drive Time',
        avgSpeed: 'Avg Speed',
        idling: 'Idle Time',
        status: 'Status',
        fuelType: 'Fuel Type',
        trend: 'Trend',
      },
      status: {
        active: 'Active',
        inactive: 'Inactive',
        sold: 'Sold',
      },
      breadcrumb: {
        overview: 'Overview',
        backToOverview: 'Back to overview',
        backToYear: 'Back to year report',
      },
      export: 'Export CSV',
      noData: 'No data found for selected period',
      selectOrganization: 'Select an organization to show vehicles',
      selectVehicleForYear: 'Year summary per vehicle. Click a vehicle for monthly breakdown.',
      selectVehicleForMonth: 'Select a vehicle in the list to see daily breakdown for the selected month.',
    },
    dtc: {
      title: 'DTC Fault Report',
      description: 'View and analyze diagnostic trouble codes (DTC) per vehicle',
      activeDtc: 'Active DTCs',
      vehiclesWithDtc: 'Vehicles with DTC',
      highSeverity: 'High/Critical',
      lowSeverity: 'Low/Medium',
      byCategory: 'By category',
      bySeverity: 'By severity',
      topCodes: 'Most common fault codes',
      code: 'DTC Code',
      category: 'Category',
      severity: 'Severity',
      firstSeen: 'First seen',
      lastSeen: 'Last seen',
      resolvedAt: 'Resolved',
      active: 'Active',
      resolved: 'Resolved',
      codeSearch: 'DTC Search',
      searchTitle: 'Search DTC code',
      whatDoesItMean: 'What does it mean?',
      symptoms: 'Symptoms',
      causes: 'Possible causes',
      repairSteps: 'Diagnosis & repair',
      notAvailable: 'Information not available',
      notAvailableDetailed: 'DQFleet does not have this information available. The code exists in the database but lacks a description.',
      noDetailedInfo: 'No detailed information available for this fault code.',
    },
    drivingLog: {
      title: 'Driving Log',
      description: 'Driving log according to Swedish Tax Authority guidelines for tax documentation',
      selectVehicle: 'Select a vehicle',
      fetchError: 'Could not load driving log',
      purposeUpdateError: 'Could not update trip purpose',
      noteUpdateError: 'Could not save note',
      noDataToSubmit: 'There is no driving log to submit',
      submitConfirm:
        'Submit the driving log for the selected period to the organization forwarding address? Editing will be locked after submission.',
      submitSuccess: 'Driving log submitted and locked.',
      submitError: 'Could not submit driving log',
      noExportData: 'No data to export',
      csvColType: 'Type (Private/Business)',
      csvColPurposeShort: 'Purpose (B/P)',
      csvColNote: 'Note',
      business: 'Business',
      private: 'Private',
      undefined: 'Undefined',
      totalDistanceLabel: 'Total distance (km)',
      privateDistanceLabel: 'Private distance (km)',
      businessDistanceLabel: 'Business distance (km)',
      undefinedDistanceLabel: 'Undefined distance (km)',
      undefinedDistanceShort: 'Undefined distance',
      csvSummaryHeading: 'SUMMARY',
      tripCountLabel: 'Number of trips',
      periodLabel: 'Period',
      exportSuccess: 'CSV exported',
      filtersTitle: 'Select vehicle and period',
      vehicleLabel: 'Vehicle',
      fromDate: 'From date',
      toDate: 'To date',
      fetch: 'Load driving log',
      print: 'Print',
      submit: 'Submit',
      submitting: 'Submitting...',
      periodLockedHint: 'This period has been submitted and cannot be edited.',
      tripCountShort: 'Number of trips',
      totalDistanceShort: 'Total distance driven',
      privateDistanceShort: 'Private distance',
      businessDistanceShort: 'Business distance',
      colDate: 'Date',
      colTime: 'Time',
      colFrom: 'From',
      colTo: 'To',
      colOdoStart: 'Odometer start',
      colOdoEnd: 'Odometer end',
      colDistance: 'Distance',
      colDuration: 'Duration',
      colToll: 'Toll (SEK)',
      tollSummaryTitle: 'Toll (period)',
      csvTollBusinessLabel: 'Toll business (total SEK)',
      csvTollPrivateLabel: 'Toll private (total SEK)',
      csvTollUndefinedLabel: 'Toll undefined (total SEK)',
      tollFollowsTripPurposeHint:
        'Toll is linked to this trip. Choose business or private for the trip — the same purpose applies to distance and toll.',
      colTripType: 'Business/Private',
      colPurpose: 'Purpose',
      colNote: 'Purpose / note',
      adjustmentPurposeDisabledTitle:
        'Odometer adjustment — business/private purpose does not apply to this row',
      correctionPurposeLabel: 'Correction',
      adjustmentDistanceShort: 'Odometer adjustment',
      adjustmentDistanceLabel: 'Odometer adjustment (correction, not driven)',
      togglePrivateTitle: 'Switch to business',
      toggleBusinessTitle: 'Switch to private',
      toggleUnsetTitle: 'Set private or business',
      notePlaceholder: 'Describe the purpose of the trip…',
      footerSum: 'Total distance',
      emptyTitle: 'No trips in this period',
      emptyBody: 'Select vehicle and dates, then click Load driving log.',
      infoTitle: 'Documentation tips',
      infoLine1:
        'Set trip purpose (toggle) for each driving row—the Business/Private column updates and is saved. Total distance = private + business + undefined (actual driving only; see note on odometer corrections).',
      infoLine2: 'Use the note field when you need to describe the trip.',
      infoLine3: 'Export or print for your records.',
      infoLine4: 'Submit sends to the top-node forwarding email and locks the period.',
      infoLine5: 'Addresses appear when available in the system.',
      infoLine6:
        '«Total distance driven» and private/business/undefined sums include actual driving only. Odometer corrections are shown separately and are not included in those totals.',
    },
    odometerReport: {
      title: 'Odometer Report',
      description: 'View odometer readings per vehicle at a specific date',
    },
    rentalReport: {
      title: 'Rental Report',
      description: 'Complete rental report with trips, costs and status',
      tabs: {
        overview: 'Overview',
        trips: 'Trips',
        geofences: 'Geofences',
        warnings: 'Warnings',
        environment: 'Environment',
        status: 'Status',
      },
      vehicle: 'Vehicle',
      period: 'Period',
      startDate: 'Start Date',
      endDate: 'End Date',
      fetchReport: 'Fetch Report',
      downloadPdf: 'Download PDF',
      sendEmail: 'Send via Email',
      totalDistance: 'Total Distance',
      totalTrips: 'Total Trips',
      totalWarnings: 'Total Warnings',
      geofenceCosts: 'Geofence Costs',
      fuelStatus: 'Fuel Status',
      tankFull: 'Tank is full',
      tankMissing: 'Missing approx',
      milAlarm: 'MIL Alarm',
      noMilAlarms: 'No MIL alarms during the period',
      milActive: 'MIL alarm active',
      co2Emissions: 'CO₂ Emissions',
      fuelConsumption: 'Fuel Consumption',
      fuel: 'Fuel',
      fuelKinds: 'Fuel types',
      consumption: 'Consumption',
      perKm: 'Per km',
      electricShare: 'Electric share',
      electricUsed: 'Electricity used',
      electricPerKm: 'Electricity per km',
      emailSent: 'Report sent!',
      recipientEmail: 'Email Address',
      recipientName: 'Recipient Name',
      message: 'Message',
    },
    decodeBinError: 'Could not decode bin file',
  },

  // CO₂ Report
  co2: {
    title: 'CO₂ Emissions',
    description: 'Calculate and analyze CO₂ emissions for the vehicle fleet',
    totalCO2: 'Total CO₂ Emissions',
    avgPerKm: 'Avg CO₂/km',
    electricVehicles: 'Electric Vehicles',
    fossilVehicles: 'Fossil Vehicles',
    co2Saved: 'CO₂ Saved (EVs)',
    trendTitle: 'CO₂ Emissions Over Time',
    fuelTypeDistribution: 'Fuel Type Distribution',
    vehicleDetails: 'CO₂ Per Vehicle',
    fuelType: 'Fuel',
    fuelUsed: 'Used',
    totalCO2Short: 'CO₂ (kg)',
    wltpCO2: 'WLTP',
  },

  // Geofence Zone Report
  geofenceZone: {
    title: 'Geofence Zone Report',
    description: 'Analyze how often vehicles are within selected zones',
    selectZone: 'Select zone',
    totalZones: 'Total zones',
    totalVehicles: 'Vehicles',
    totalVisits: 'Total visits',
    totalTime: 'Total time',
    totalCost: 'Total cost',
    visitsOverTime: 'Visits over time',
    zoneSummary: 'Summary by zone',
    vehicleDetails: 'Vehicles per zone',
    zone: 'Zone',
    vehicles: 'Vehicles',
    visits: 'Visits',
    costColumn: 'Cost',
    totalDuration: 'Total time',
    avgDuration: 'Avg/visit',
    lastVisit: 'Last visit',
    noData: 'No data for selected period',
    fetchError: 'Could not fetch report',
    globalLabel: 'Global',
    filteredLabel: 'Filtered',
    showingRowsCount: 'Showing {{count}} of {{total}} rows',
  },

  // Underutilized Report
  underutilized: {
    title: 'Underutilized Vehicles',
    description: 'Identify vehicles with low utilization and generate warnings',
    totalVehicles: 'Total Vehicles',
    activeVehicles: 'Active Vehicles',
    underutilizedVehicles: 'With Warnings',
    avgUtilization: 'Avg Utilization',
    utilizationOverview: 'Utilization Overview',
    utilizationTrend: 'Utilization Trend',
    showOnlyWarnings: 'Show only warnings',
    vehiclesWithWarnings: 'Vehicles with warnings',
    allVehicles: 'All vehicles',
    activeDays: 'Active days',
    utilization: 'Utilization',
    warnings: 'Warnings',
  },

  // Vehicle Wear Report
  vehicleWear: {
    title: 'Vehicle Wear',
    description: 'Analyze how driving behavior affects vehicle wear',
    totalVehicles: 'Total Vehicles',
    avgWearScore: 'Average Wear Score',
    worstCategory: 'Worst Category',
    vehiclesAtRisk: 'Vehicles at Risk',
    wearByVehicle: 'Wear by Vehicle',
    wearProfile: 'Wear Profile',
    selectVehicle: 'Select a vehicle for details',
    clickBarToSelect: 'Click on a vehicle in the chart to see details',
    recommendations: 'Recommendations for',
    brakes: 'Brakes',
    engine: 'Engine',
    tires: 'Tires',
    fuel: 'Fuel',
    overall: 'Overall',
  },

  // Login
  login: {
    title: 'DQFleet Login',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    passwordRequired: 'Password is required',
    emailPlaceholder: 'your@email.com',
    passwordPlaceholder: '••••••••',
    forgotPassword: 'Forgot password?',
    loggingIn: 'Logging in...',
    loginButton: 'Login',
    noAccount: "Don't have an account?",
    createAccount: 'Create account',
    welcomeMessage: 'Welcome! You are now logged in.',
    loginError: 'Invalid email or password. Please try again.',
    verifyEmailTitle: 'Email address must be verified',
    verifyEmailBody:
      'You must verify your email address before you can sign in. A verification email has been sent.',
    verifyEmailBodyWithEmail:
      'You must verify your email address before you can sign in. A verification email has been sent to {email}.',
    verifyOrgLinkedNote:
      'Your domain is linked to an organization. After verification you will have Viewer access in that organization.',
    verifyManualOrgNote:
      'If your organization does not recognize your email domain, an administrator may need to link your account before you see full functionality.',
    resendVerification: 'Send new verification email',
    resendingVerification: 'Sending...',
    resendVerificationSuccess: 'A new verification email has been sent to your address.',
    resendVerificationError: 'Could not send verification email',
  },


  // Sidebar
  sidebar: {
    user: 'User',
    logout: 'Logout',
    noFeatures: 'No available features',
    expandMenu: 'Expand menu',
    collapseMenu: 'Collapse menu (icons only)',
  },

  temperatureMonitoring: {
    overviewTitle: 'Temperature monitoring',
    overviewDescription: 'Status for registered EYE sensors linked to GPS trackers.',
    sensorsTitle: 'Sensors & linking',
    sensorsDescription: 'Register EYE Sensor and link to tracker (slot 1–4). Choose interval and measurements — config is sent automatically.',
    configTitle: 'BLE/EYE config (internal)',
    configDescription: 'Troubleshooting and manual override. Customers should use Sensors & linking.',
    configInternalBannerTitle: 'Internal view — not for customer self-service',
    configInternalBannerBody:
      'Parameters are set automatically in code when linking or editing. This page is for DeviaQ/support when automated provisioning needs debugging.',
    recipientsTitle: 'Alarm recipients',
    schedulesTitle: 'Report schedules',
    schedulesComingSoon: 'Scheduled weekly and monthly reports coming in a next step.',
    reportsTitle: 'Cold chain reports',
    reportsDescription: 'Select a vehicle with a linked EYE sensor. The report shows temperature and humidity for the last 7 days.',
    selectSensorForReport: 'Select reg. no. / sensor',
    noLinkedSensorsForReport: 'No vehicles with linked sensors. Link a sensor under Sensors & links.',
    temperature: 'Temperature',
    humidity: 'Humidity',
    battery: 'Battery',
    lowBatteryLabel: 'Low battery',
    tracker: 'Tracker (IMEI)',
    lastReading: 'Last reading',
    noSensors: 'No sensors registered yet.',
    recentExcursions: 'Recent excursions',
    noExcursions: 'No recorded excursions.',
    sensor: 'Sensor',
    excursionType: 'Type',
    started: 'Started',
    registerSensor: 'Register sensor',
    organizationRequired: 'Select a customer/organization before creating the sensor.',
    registeredSensors: 'Registered sensors',
    editName: 'Edit name',
    sensorRenamed: 'Display name updated',
    noSerial: 'No serial number',
    sensorName: 'Name (display name)',
    serialNumber: 'Serial number (SN)',
    product: 'Product',
    macAddress: 'MAC address (optional, colons not required)',
    invalidMac: 'MAC address must be 12 hex characters, e.g. 7CD9F4142349',
    linkToTracker: 'Link to tracker',
    configuredSlot: 'Active slot in DQFleet',
    slotChangeHint: 'Saving configures the selected slot on the tracker. If you change slot, the previous one is disabled automatically.',
    willConfigure: 'Will send to tracker',
    scanInterval: 'Scan interval',
    scanIntervalOptions: {
      30: '30 seconds',
      60: '1 minute',
      120: '2 minutes',
      300: '5 minutes',
    },
    measurements: 'Measurements',
    measurement: {
      temperature: 'Temperature',
      humidity: 'Humidity',
      magnet: 'Magnet (door/hatch)',
    },
    editLinkTitle: 'Edit link',
    linkSettingsSaved: 'Settings saved',
    saveAndSendConfig: 'Save and send to tracker',
    saveWithoutSend: 'Save without sending',
    saveOnlyHint: 'Changes are sent to the tracker only when you click «Save and send to tracker». If the tracker is offline, config is queued and sent on the next connection.',
    settingsSavedNotSent: 'Saved in DQFleet. Config has not been sent to the tracker yet — click «Save and send» or «Send BLE config».',
    configSentQueuedBanner: 'Config is queued — tracker is offline. It will be sent automatically on the next connection. See status and send log below.',
    configSentAppliedBanner: 'Config received by tracker. Latest status and send log updated below.',
    configSendLogEmpty: 'No config sends yet.',
    configSyncStatus: 'Config status',
    configLastSent: 'Last sent',
    configSendLog: 'Send log',
    linkSyncStatus: {
      applied: 'Received by tracker',
      pending: 'Queued (tracker offline)',
      applying: 'Sending…',
      failed: 'Failed',
      unknown: 'Unknown',
    },
    syncLogStatus: {
      applied: 'Received',
      queued: 'Queued',
      failed: 'Failed',
    },
    selectSensor: 'Select sensor',
    selectVehicle: 'Select vehicle',
    selectSensorFirst: 'Select a sensor first',
    vehiclePlaceholder: 'Search reg. no., make or model…',
    imeiFromVehicle: 'Select vehicle to fetch IMEI',
    noDeviceOnVehicle: 'Vehicle has no linked GPS device (IMEI)',
    deviceId: 'Device ID (devices.id)',
    slot: 'Slot',
    link: 'Link',
    activeLinks: 'Active links',
    noLinks: 'No links.',
    loadError: 'Failed to load data',
    saveError: 'Failed to save',
    saveForbidden: 'You do not have permission to save. Sign in as the customer and ensure you can create sensors.',
    sensorCreated: 'Sensor created',
    linkCreated: 'Link created',
    linkAndConfigSent: 'Link created and BLE config sent to tracker',
    sendTrackerConfig: 'Send BLE config',
    provisionError: 'Could not send BLE config to tracker',
    configSaved: 'Config saved in DQFleet',
    configApplied: 'Config sent to device',
    configQueued: 'Device offline — config must be sent when online',
    syncStatusLabel: 'Sync status',
    saveInCloud: 'Save in DQFleet',
    sendToDevice: 'Send to device',
    imeiRequired: 'Enter IMEI to send config',
    recipientName: 'Name',
    email: 'Email',
    phone: 'Phone (SMS)',
    recipientCreated: 'Recipient created',
    noRecipients: 'No recipients.',
    exportCsv: 'Export CSV',
    eyeDeviceId: 'Sensor ID',
    showReport: 'Show report',
    reportLoadError: 'Could not load report',
    noReportReadings: 'No temperature readings in the selected period.',
    reportPeriod: 'Period: {{from}} – {{to}}',
    reportReadingCount: '{{count}} readings',
    status: {
      ok: 'OK',
      no_signal: 'No signal',
      out_of_range: 'Out of range',
      low_battery: 'Low battery',
      sensor_error: 'Sensor error',
      disconnected: 'Disconnected',
    },
  },

  // Drivers
  drivers: {
    schedule: {
      title: 'Work Schedule',
      description: 'Set work hours to automatically classify trips as business or private. Trips outside the schedule are marked as private.',
      modeLabel: 'Usage',
      mode: {
        privateManual: 'Private – mark business trips manually',
        privateManualHelp: 'Vehicle is hidden on the map. All trips are private until you mark them as business in the driving log.',
        workHours: 'Company car with work hours',
        workHoursHelp: 'Vehicle is visible on the map during work hours. Trips are classified automatically as business or private.',
        companyCar: 'Fleet vehicle – always visible',
        companyCarHelp: 'Vehicle is always visible on the map. All trips are classified as business.',
        alwaysBusiness: 'Always business',
      },
      sameAllDays: 'Same time all days',
      commonTime: 'Work hours (applies to selected days)',
      noWorkTime: 'Private (no work time)',
      noSchedule: 'No schedule set - all trips are classified as private',
      activeInfo: 'days with work hours set',
      weekdays: 'Weekdays',
      weekend: 'Weekend',
      weekdaysActive: 'weekdays',
      weekendActive: 'weekend days',
      saveSuccess: 'Work schedule saved',
      saveDraft: 'Done – saved when the user is created',
      saveDraftSuccess: 'The schedule is included when you click Create user.',
      applyOrgDefaults: 'Use organization default (weekdays)',
      orgDefaultsReady: 'Organization defaults are applied. Click Save to persist to the server.',
      emptyPrefilledFromOrg:
        'No schedule was stored on the server. Below is the organization default—click Save to use it, or change the times for a custom schedule.',
      applyOrgDefaultsHelp: 'Fills Monday–Friday with default service hours from the customer top node driving log settings. Save to persist.',
      applyOrgDefaultsMissing: 'No organization default is configured on the top node.',
      applyOrgDefaultsNoOrg: 'Select the user organization first.',
      days: {
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
        sunday: 'Sun',
      },
    },
    assignment: {
      title: 'Vehicle Assignment',
      selectVehicle: 'Select vehicle',
      searchVehicle: 'Search registration number...',
      noVehiclesFound: 'No vehicles found',
      typeToSearch: 'Type to search vehicles',
      hasDriver: 'Has driver',
      selectDate: 'Start date',
      selectTrip: 'Select start trip (optional)',
      currentVehicle: 'Current vehicle',
      since: 'Since',
      currentDriverWarning: 'Vehicle already has a driver: {{name}}. That assignment will be ended automatically.',
      newAssignment: 'New assignment',
      assign: 'Assign vehicle',
      endAssignment: 'End assignment',
      confirmEnd: 'Are you sure you want to end this assignment?',
      assignSuccess: 'Driver assigned to vehicle',
      endSuccess: 'Assignment ended',
      history: 'Assignment history',
      noUnassignedVehicles: 'No available vehicles — all already have an active driver assignment',
      noTrips: 'No trips on this date',
      modePickUnassigned: 'Available vehicle in organization',
      modeLinkDevice: 'Link device to existing vehicle',
      modeRegisterNew: 'Register new vehicle',
      modeHelpPick: 'Choose a vehicle without an active driver assignment in your customer tree.',
      modeHelpLink: 'Search by registration, pick an unmounted device and start date. An existing driver on the vehicle will be replaced when you assign.',
      modeHelpRegister: 'Fetch data from Car.info, enter odometer, pick a device and date. The vehicle is created in the selected organization.',
      orgRequired: 'Organization is required for this action.',
      searchVehicleInOrg: 'Search vehicle (reg. no.) in organization',
      searchDevice: 'Search device (IMEI)…',
      selectDevice: 'Select device',
      noDevicesFound: 'No devices found',
      deviceGlobalSearch: 'All unassigned devices in the system (global admin)',
      existingDevicePolicy: 'Vehicle already has a device',
      unmountExisting: 'Unmount existing',
      swapDevices: 'Swap devices',
      linkDeviceAndAssign: 'Link device and assign driver',
      linkDeviceSuccess: 'Device linked and driver assigned',
      registerRegPlaceholder: 'ABC123',
      fetchCarInfo: 'Fetch from Car.info',
      initialOdometerKm: 'Odometer at start (km)',
      createVehicleAndAssign: 'Create vehicle, link device and assign',
      registerSuccess: 'Vehicle created and driver assigned',
      scheduleApplied: 'Driver schedule updated from organization',
      scheduleApplyWarn: 'Could not apply organization schedule (check top node driving log settings).',
      regLabel: 'Reg. no.',
      makePlaceholder: 'Make',
      modelPlaceholder: 'Model',
      yearPlaceholder: 'Year',
      fuelPlaceholder: 'Fuel',
      fuelPetrol: 'Petrol',
      fuelDiesel: 'Diesel',
      fuelElectric: 'Electric',
      fuelHybrid: 'Hybrid',
    },
  },

  // Profile Modal
  profile: {
    title: 'My Profile',
    edit: 'Edit',
    personalInfo: 'Personal Information',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Mobile Phone',
    role: 'Role',
    organization: 'Organization',
    cabinetPin: 'PIN code',
    cabinetPinHint: 'Your key cabinet PIN.',
    noCabinetPin: 'No PIN yet',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    saveSuccess: 'Profile updated successfully',
    driverInfo: 'Driver Information',
    licenseNumber: 'License Number',
    licenseExpiry: 'License Expiry',
    licenseCategories: 'License Categories',
    employeeId: 'Employee ID',
    department: 'Department',
    costCenter: 'Cost Center',
    currentPasswordRequired: 'Current password is required to change password',
    passwordTooShort: 'Password must be at least 6 characters',
    passwordMismatch: 'Passwords do not match',
    passwordMinLength: 'At least 6 characters',
    roleDriver: 'Driver',
    roleViewer: 'Viewer',
    defaultPickupLocation: 'Default pickup location',
    defaultPickupLocationHint: 'The pickup location selected automatically for you, e.g. when booking.',
    selectPickupLocation: '— Select pickup location —',
    selectPickupLocationRequired: 'Please select a pickup location',
    noDefaultPickupLocation: 'None selected',
    pickupPromptTitle: 'Choose your default pickup location',
    pickupPromptDescription: 'You must choose a default pickup location before continuing. Pick the location you use most often and it will be pre-filled for you going forward. You can change this anytime under My Profile.',
    pickupPromptLater: 'Later',
  },

  // SysAdmin
  sysAdmin: {
    title: 'System Administrator',
    manageRoles: 'Manage roles and permissions',
    manageOrganizations: 'Manage organizations',
    dataMigration: 'Data Migration',
    migrateData: 'Migrate data from old database',
    deviceCommands: 'Device Commands',
    deviceCommandsDescription: 'Send commands to devices',
    auditLog: 'Audit Log',
    auditLogDescription: 'View log of sent commands',
    generalAuditLog: 'Change Log',
    generalAuditLogDescription: 'View all changes (CREATE, UPDATE, DELETE)',
    errorLog: {
      name: 'Error Log',
      title: 'Error Log',
      description: 'Log of all system errors and exceptions',
      viewDetails: 'Error – details',
      close: 'Close',
      resolutionNotes: 'Resolution notes',
      resolutionNotesPlaceholder: 'Optional notes...',
      saveStatus: 'Save status',
      saveStatusSuccess: 'Status updated',
      loading: 'Loading...',
      noErrorsFound: 'No errors found',
      noResultsMatchFilters: 'No errors match the current filters. Clear filters or change Severity/Status to see errors.',
      status: 'Status',
      clearFilters: 'Clear filters',
      searchPlaceholder: 'Search in error message...',
      searchLabel: 'Search',
      severityLabel: 'Severity',
      statusLabel: 'Status',
      clearLog: 'Clear error log',
      clearLogTitle: 'Clear error log',
      clearLogDescription: 'Choose to delete all errors or only those older than a certain number of days.',
      clearOlderThanLabel: 'Delete entries older than (days)',
      clearOlderThanPlaceholder: 'Leave blank to delete all',
      clearOlderThanHint: 'Deletes entries older than {days} days.',
      clearAllHint: 'Leave blank to delete ALL entries.',
      deleteAll: 'Delete all',
      deleteOlderThan: 'Delete older than {days} days',
      deleting: 'Deleting...',
      deletedSuccess: '{count} errors deleted from the error log.',
      deletedError: 'Could not clear the error log',
      showing: 'Showing {from} - {to} of {total}',
      previous: 'Previous',
      next: 'Next',
      totalLabel: 'Total',
      messageLabel: 'Message',
      errorTypeLabel: 'Error type',
      errorCodeLabel: 'Code',
      serviceLabel: 'Service',
      moduleLabel: 'Module',
      functionLabel: 'Function',
      timeLabel: 'Time',
      requestLabel: 'Request',
      resolvedLabel: 'Resolved',
    },
    generalAuditLogSection: {
      title: 'Change Log',
      description: 'Log of all changes in the system',
      total: 'Total',
      entityTypeLabel: 'Entity type',
      entityTypePlaceholder: 'e.g. vehicle, user...',
      actionLabel: 'Action',
      searchLabel: 'Search',
      searchPlaceholder: 'Search user, entity...',
      clearFilters: 'Clear filters',
      loading: 'Loading...',
      noChanges: 'No changes found',
      colTime: 'Time',
      colUser: 'User',
      colAction: 'Action',
      colEntity: 'Entity',
      colChanges: 'Changes',
      system: 'System',
      fieldsCount: '{count} fields',
      showing: 'Showing {from} - {to} of {total}',
      previous: 'Previous',
      next: 'Next',
      allActions: 'All',
    },
    errorLogDescription: 'View all system errors and exceptions',
    systemMonitor: {
      title: 'System Monitoring',
      description: 'Monitor CPU, memory, disk, databases and queues',
      docker: {
        title: 'Docker Containers',
        notAvailable: 'Docker is not available on this server',
        total: 'Total',
        running: 'Running',
        stopped: 'Stopped',
        healthy: 'Healthy',
        unhealthy: 'Unhealthy',
        container: 'Container',
        image: 'Image',
        status: 'Status',
        health: 'Health',
        cpu: 'CPU',
        memory: 'Memory',
        swarm: {
          title: 'Docker Swarm',
          nodes: 'Nodes',
          services: 'Services',
          host: 'Host',
          manager: 'Manager',
          service: 'Service',
          replicas: 'Replicas',
        },
      },
      scheduledJobs: {
        title: 'Scheduled Jobs',
        jobStatus: 'Job Status',
        jobs: 'Jobs',
        runningNow: 'Running',
        successLast24h: 'OK (24h)',
        failedLast24h: 'Failed (24h)',
        schedule: 'Schedule',
        nextRun: 'Next',
        recentRuns: 'Recent Runs',
        noHistory: 'No run history yet',
        job: 'Job',
        startTime: 'Start time',
        duration: 'Duration',
        processed: 'OK/Failed',
        success: 'OK',
        failed: 'FAILED',
        running: 'RUNNING',
      },
    },
    slowQueries: {
      title: 'Slow Queries',
      description: 'Analyze slow SQL queries',
    },
    apiMetrics: {
      title: 'API metrics',
      cardDescription: 'p50/p95 per endpoint, heap and Redis before performance changes',
      description:
        'Phase 0 baseline. Latency is stored in Redis per normalized route. p50/p95 are the upper edge of the duration bucket, not an exact percentile. This page refreshes every minute.',
      loadError: 'Could not load API metrics',
      empty: 'No requests recorded yet. Use the system for a while and refresh.',
      hourTitle: 'This hour',
      todayTitle: 'All day',
      requests: 'requests',
      colRoute: 'Route',
      colStatus: 'Status',
      colCount: 'Count',
      colP50: 'p50',
      colP95: 'p95',
      colAvg: 'Avg',
      colMax: 'Max',
      apiHeap: 'API heap',
      redisMemory: 'Redis memory',
      redisMax: 'Cap {{value}}',
      tripState: 'trip:state keys',
      tripStateSample: 'Sample average {{value}}',
      gpsWorker: 'GPS worker heap',
      gpsWorkerWaiting: 'Waiting for worker snapshot',
      gpsPackets: '{{count}} packets, avg {{avg}}',
    },
    dataReplay: {
      title: 'Data Replay',
      description: 'Replay historical GPS data',
    },
    importSources: {
      title: 'Import Sources',
      description: 'Manage mailbox and SFTP sources for import sync',
      loadError: 'Could not load import sources',
      saveError: 'Could not save import source',
      deleteError: 'Could not delete import source',
      testError: 'Connection test failed',
      testSuccess: 'Connection OK',
      created: 'Import source created',
      updated: 'Import source updated',
      deleted: 'Import source deleted',
      newSource: 'New source',
      createSource: 'Create source',
      editSource: 'Edit source',
      create: 'Create',
      saveChanges: 'Save changes',
      sourcesList: 'Import sources',
      noSources: 'No import sources created yet',
      confirmDelete: 'Delete this import source?',
      testConnection: 'Test connection',
      syncNow: 'Sync now',
      syncSuccess: 'Sync completed',
      syncError: 'Sync failed',
      type: 'Type',
      mailbox: 'Mailbox',
      sftp: 'SFTP',
      local: 'Local directory',
      localDirectoryPath: 'Directory path (on the server)',
      localDirectoryHint: 'Absolute path to the directory the backend reads files from (e.g. an SFTP upload directory).',
      name: 'Name',
      namePlaceholder: 'E.g. Shared mailbox Circle K',
      mailboxEmail: 'Mailbox email',
      sftpHost: 'Host',
      sftpPort: 'Port',
      sftpUsername: 'Username',
      sftpPassword: 'Password (optional)',
      sftpPrivateKeyPath: 'Private key path (optional)',
      sftpPassphrase: 'Key passphrase (optional)',
      sftpBasePath: 'Base path',
      allowedExtensions: 'Allowed extensions (comma separated)',
      filenameExclusions: 'Filename exclusions (comma separated)',
      minDate: 'Min date (mailbox, YYYY-MM-DD)',
      pathPattern: 'Path pattern (SFTP)',
      retentionDays: 'Retention (days)',
      isActive: 'Active source',
      inactive: 'Inactive',
      lastFetchAt: 'Last fetch',
      lastFetchError: 'Last error',
      lastFetchFilesCount: 'Files in source',
      totalFilesInSource: 'files in source',
      validation: {
        nameRequired: 'Name is required',
        mailboxEmailRequired: 'Mailbox email is required for mailbox source',
        sftpRequired: 'Host, username and base path are required for SFTP source',
        localDirectoryRequired: 'Directory path is required for local source',
      },
    },
    tripValidation: {
      title: 'Trip Validation',
      description: 'Validate trips between MongoDB and MariaDB',
      resolveTitle: 'Handle validation error',
      errorType: 'Error type',
      date: 'Date',
      binAnalysisTitle: 'Bin file analysis',
      analyzeBinFile: 'Analyze bin file',
      analyzing: 'Analyzing...',
      binAnalysisFailed: 'Could not analyze the bin file',
      binAnalysis502Hint: 'Server did not respond (502). Bin file analysis may time out or the bin file is not on the server (gps-data). Check backend logs.',
      binSegmentsExcludedNote:
        '{{count}} speed segments from the bin file are not listed (under {{minDuration}} s or under {{minDistance}} m — same minimums as for a saved trip).',
      tryAgain: 'Try again',
      clearLog: 'Clear entire log',
      clearLogConfirm: 'Are you sure? All entries in the trip validation log will be deleted. This cannot be undone.',
      clearLogSuccess: 'Log cleared. {{count}} entries removed.',
      clearLogFailed: 'Could not clear the log',
      replayAllDatesQueue: 'Replay all dates (queue)',
      replayAllDatesQueueTitle: 'Run replay via queue (gps_worker_*_replay) for all unique dates in the list',
      bulkClean: 'Clean extra MariaDB trips',
      bulkCleanTitle: 'Removes MariaDB trips without a matching MongoDB trip and syncs missing trips. Handles all sync-mismatch entries.',
      bulkCleanSuccess: '{{deleted}} extra MariaDB trips removed, {{synced}} MongoDB trips synced.',
      bulkCleanFailed: 'Bulk clean failed',
      syncMismatchInfo: 'Sync mismatch means the number of trips differs between MongoDB and MariaDB. Bin file analysis is not available for this error type. Run a replay to synchronize data.',
      syncMismatchWhyTitle: 'Why two trips in MariaDB but the correct number in MongoDB?',
      syncMismatchWhyMongo: 'has duplicate checks: before saving, findDuplicateTrip is used (same vehicle, start time ±10 s, distance ±200 m). If a matching trip already exists, the new one is not saved – only one logical trip per time window is stored in Mongo.',
      syncMismatchWhyMaria: 'had no equivalent check: each sync call did an INSERT (with its own trip_id). If sync was previously called even when the trip was not saved in Mongo (e.g. for a rejected duplicate with a different trip_id), MariaDB got an extra row. So you may see two rows in MariaDB (two different trip_ids) but only one in MongoDB.',
      syncMismatchToday: 'Today, MariaDB sync is only called when the trip has first been saved in MongoDB, and we have added duplicate checks at MariaDB insert too (same vehicle + start time ±10 s, distance ±200 m). New duplicates of this kind should therefore not occur.',
      syncMismatchAction: 'Action: Run "Replay day data" to fill MongoDB from the bin file, or remove the extra MariaDB row (the one missing in MongoDB) manually if it is a true duplicate.',
      syncMismatchMariaMore: 'MariaDB has more trips than MongoDB. The "MariaDB trips" list above shows which also exist in MongoDB (✓ MongoDB) and which exist only in MariaDB – these may have been created by an earlier sync bug or manually. Analyse which trip is missing in MongoDB before running replay; after replay, extra trips that still exist only in MariaDB may need to be removed manually.',
      currentPoints: 'Current points',
      pointsInBinFile: 'Points in bin file',
      totalInFile: 'Total in file',
      tripTime: 'Time period',
      updateMongoDB: 'Update MongoDB with {{count}} points',
      repairing: 'Repairing...',
      repairSuccess: 'Trip repaired with {{count}} GPS points',
      repairFailed: 'Could not repair trip',
      noPointsFound: 'No GPS points found in the bin file for this trip',
      tooFewPoints: 'Only {{count}} point found, at least 2 required',
      manualResolveTitle: 'Mark manually',
      notes: 'Notes (optional)',
      notesPlaceholder: 'Describe how the problem was resolved...',
      markResolved: 'Mark as resolved',
      mongoTrips: 'MongoDB trips (can be deleted)',
      deleteTripTitle: 'Delete trip',
      deleteTripWarning: 'You are about to delete a trip with {{count}} points. This cannot be undone.',
      deleteReason: 'Reason for deletion *',
      deleteReasonPlaceholder: 'Describe why this trip is being deleted (at least 5 characters)...',
      deleteReasonError: 'At least 5 characters required',
      deleteTrip: 'Delete trip',
      deletingTrip: 'Deleting...',
      deleteTripSuccess: 'Trip {{tripId}} deleted',
      deleteTripFailed: 'Could not delete trip',
      createInMariaDB: 'Create in MariaDB',
      creatingInMariaDB: 'Creating...',
      createInMariaDBSuccess: 'Trip created in MariaDB',
      createInMariaDBFailed: 'Could not create trip in MariaDB',
      replayDay: 'Replay day data',
      replayDaySuccess: 'Replay started',
      systematicErrorHint: 'Many errors of the same type may indicate a systematic issue – e.g. sync to MariaDB failed during a period. Check the Error report (Administration → Error report) for "TripSyncService" or "syncTripToMariaDB", and consider running Replay for the affected dates.',
    },
    tripSync: {
      title: 'Trip Sync',
      description: 'Background job that syncs trips from MongoDB to MariaDB',
      jobEnabled: 'Background job is enabled',
      jobDisabled: 'Background job is disabled',
      intervalInfo: 'Runs every {{minutes}} minute(s). Trips that exist in MongoDB but are missing in MariaDB are synced automatically.',
      lastRun: 'Last run',
      lastRunTime: 'Run time',
      synced: 'Synced',
      failed: 'Failed',
      duration: 'Duration',
      runManualSync: 'Run sync manually',
      syncing: 'Syncing...',
      jobRunning: 'A sync job is running.',
      missingTripsTitle: 'Missing trips (sample)',
      missingTripsDescription: 'Number of trips that exist in MongoDB but are missing in MariaDB (max 100 shown). The next run will sync these.',
      manualSyncSuccess: 'Sync complete: {{synced}} synced, {{failed}} failed, {{skipped}} skipped.',
      manualSyncError: 'Manual sync failed',
    },
    carinfoFilter: {
      title: 'Car.info Filter',
      description: 'Select which Car.info fields to show in filter',
    },
    deviceQueues: {
      title: 'Device queues',
      cardDescription: 'Jasper sessions until the chain is complete',
      description:
        'Only devices from Jasper SESSION_START. getvin N/A: wait until the vehicle is registered manually, then VIN is taken from the registry and P&P continues. A device with no connection for 30 days is removed and returns on the next session. Yellow rows = STUCK (>24h without progress).',
      refresh: 'Refresh',
      empty: 'No active devices in the queue.',
      emptyDone: 'No completed events (last 14 days).',
      viewLabel: 'View',
      viewQueue: 'In queue',
      viewDone: 'Completed',
      countsShort: '{{queue}} in queue · {{done}} done',
      countsHint: '{{queue}} in queue, {{done}} completed (current organization filter)',
      colCompleted: 'Completed',
      loadError: 'Failed to load device queues',
      searchPlaceholder: 'Search reg. no, make/model, IMEI, group…',
      typeSession: 'Jasper',
      typeCommand: 'Command queue',
      typePnp: 'OBD P&P',
      typeForceNonObd: 'Non-OBD',
      colType: 'Phase',
      colReg: 'Reg. no',
      colMakeModel: 'Make / model',
      colGroup: 'Group',
      colImei: 'Device ID',
      colFw: 'FW',
      colLastSeen: 'Last connection',
      colAdded: 'Added',
      colStatus: 'Status',
      colActions: 'Actions',
      cmdCount: '{{count}} command(s)',
      actionForce: 'Resume / force',
      actionRemove: 'Cancel',
      actionGetver: 'Ask FW (getver)',
      confirmForce: 'Resume pipeline / force next step for this device?',
      confirmRemove: 'Cancel pipeline for this device? (device/vehicle is not deleted)',
      forceOk: 'Run completed',
      forceQueued: 'Device offline — parameters queued for next connection',
      forceFail: 'Could not force run',
      removeOk: 'Cancelled / removed from queue',
      removeFail: 'Could not remove from queue',
      getverTitle: 'Firmware from device',
      getverOk: 'getver received',
      getverQueued: 'Device offline — getver queued',
      getverFail: 'Could not query firmware',
      markInteresting: 'Mark interesting',
      markOk: 'Marked as interesting',
      markFail: 'Could not mark',
      exportEmpty: 'No data to export',
      exportOk: 'Exported {{count}} rows',
    },
    gpsDataLag: {
      title: 'GPS dump queue',
      cardDescription: 'Devices sending lagging GPS data',
      description:
        'Shows only devices with an active dump queue (received data lags behind the device GPS time). When a device catches up it leaves the list. Flush sends a Teltonika command via Jasper SMS.',
      refresh: 'Refresh',
      loading: 'Loading…',
      empty: 'No device is lagging right now.',
      loadError: 'Failed to load GPS dump queue',
      searchPlaceholder: 'Search reg. no, org, IMEI, ICCID…',
      summary: '{{count}} in dump queue · updated {{snapped}}',
      colReg: 'Reg. no',
      colOrg: 'Organization',
      colImei: 'IMEI',
      colIccid: 'ICCID',
      colDumpLag: 'Dump lag',
      colDeviceData: 'Device data',
      colReceived: 'Received',
      colLastRecs: 'Last recs',
      colActions: 'Actions',
      flush: 'Flush',
      flushing: 'Sending…',
      flushTitle: 'Send flush via SMS (empty flash)',
      confirmFlush: 'Send flush to {{reg}}?',
      flushOk: 'Flush sent to {{reg}}',
      flushFail: 'Could not send flush',
      smsBadge: 'SMS',
    },
    certMonitor: {
      title: 'Certificate Monitor',
      cardDescription: 'Track expiring certificates and how to renew them',
      description: 'Tracks expiring certificates – OIDC client certs are auto-discovered, HTTPS hosts are probed live on Refresh, others can be registered manually with renewal instructions.',
      refresh: 'Refresh',
      addManual: 'Add certificate',
      editTitle: 'Edit certificate',
      empty: 'No certificates are being monitored yet.',
      colName: 'Name',
      colStatus: 'Status',
      colExpires: 'Expires',
      colSource: 'Source',
      colRenewal: 'Renewal',
      sourceAuto: 'Auto (OIDC)',
      sourceManual: 'Manual',
      statusOk: 'OK ({{days}} days left)',
      statusExpiring: 'Expires in {{days}} days',
      statusExpired: 'Expired',
      statusUnknown: 'Unknown expiry date',
      bannerSummary: '{{expired}} certificate(s) expired and {{expiring}} expiring soon.',
      bannerLink: 'Open certificate monitor',
      fieldName: 'Name',
      fieldOrg: 'Organization',
      fieldCheckHost: 'HTTPS host (auto)',
      fieldCheckHostHelp: 'E.g. dqfleet.deque.se – on Refresh, expiry is fetched automatically via TLS.',
      fieldPem: 'Certificate (PEM)',
      fieldPemHelp: 'Paste the public certificate to automatically read expiry date, issuer and thumbprint.',
      fieldValidTo: 'Expiry date',
      fieldWarningDays: 'Warn this many days before',
      fieldRenewal: 'Renewal instructions',
      fieldContact: 'Contact (email)',
      fieldNotes: 'Notes',
      fieldActive: 'Active (monitor this certificate)',
      nameRequired: 'Name is required',
      created: 'Certificate added',
      updated: 'Certificate updated',
      deleted: 'Certificate removed',
      confirmDelete: 'Remove "{{name}}"?',
      refreshed: 'Scanned {{count}} certificates',
      refreshedDetail: 'Updated {{oidc}} OIDC and {{host}} HTTPS certs ({{errors}} errors)',
      loadError: 'Could not load certificates',
      saveError: 'Could not save certificate',
      deleteError: 'Could not remove certificate',
      refreshError: 'Could not refresh certificates',
    },
    security: {
      title: 'Security',
      description: 'CrowdSec events on this host, unban IPs and safelist trusted addresses',
      loadError: 'Could not load security status',
      hostctlMissing: 'The fail2ban host helper is not installed on this server. The safelist is stored in the database, but bans and logs appear only after the ctl script is in place.',
      fail2ban: 'fail2ban',
      crowdsec: 'CrowdSec',
      crowdsecMissing: 'CrowdSec is not installed',
      eventsHint: 'Active local bans are listed first. The community blocklist (thousands of IPs) is not shown here.',
      localBans: '{{count}} active local bans',
      activeBansHeading: 'Active bans',
      recentAlertsHeading: 'Recent alerts',
      bansMissingInList: 'CrowdSec has active bans that are missing from the list. Refresh the page.',
      noEvents: 'No local CrowdSec events right now',
      banActive: 'Active',
      banExpired: 'Expired',
      alertOnly: 'Alert (no ban)',
      colWhen: 'When',
      colReason: 'Reason',
      colCountry: 'Country',
      colDetail: 'Network',
      colUntil: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      noBans: 'No banned IPs right now',
      colIp: 'IP',
      colJail: 'Jail',
      unban: 'Unban',
      unbanOk: 'IP unbanned',
      unbanError: 'Could not unban IP',
      safeList: 'Safelist (trusted IPs)',
      comment: 'Comment',
      addSafe: 'Add as safe',
      noSafe: 'No trusted IPs saved',
      safeAddOk: 'IP added as safe',
      safeAddError: 'Could not add IP',
      safeRemoveOk: 'IP removed from safelist',
      safeRemoveError: 'Could not remove IP',
      noLogFile: 'No log file found for that source',
      emptyLog: 'Empty log',
      log: {
        fail2ban: 'fail2ban',
        ufw: 'UFW',
        crowdsec: 'CrowdSec',
        lifeline: 'Lifeline',
      },
      scenario: {
        'ssh-bf': 'SSH brute-force',
        'ssh-slow-bf': 'SSH slow brute-force',
        'ssh-time-based-bf': 'SSH time-based brute-force',
        'http-bad-user-agent': 'HTTP bad user-agent',
        'http-sensitive-files': 'HTTP sensitive files',
        'http-probing': 'HTTP probing',
        'http-crawl-non_statics': 'HTTP crawl',
        'http-path-traversal': 'HTTP path traversal',
        'http-admin-interface-probing': 'HTTP admin probing',
        'http-technology-probing': 'HTTP technology probing',
        'http-cve-probing': 'HTTP CVE probing',
        'http-generic-test': 'HTTP generic test',
      },
    },
    ssoLog: {
      title: 'SSO log',
      cardDescription: 'Failed and abandoned SSO attempts, by customer and device',
      description: 'See failed SSO logins: who tried, which customer, why it stopped, and whether they came from a phone or a computer.',
      loadError: 'Could not load the SSO log',
      empty: 'No SSO attempts match the filters',
      period: 'Period',
      days: '{{count}} days',
      customer: 'Customer',
      allOrgs: 'All customers',
      user: 'User',
      userPlaceholder: 'Email or name',
      channel: 'Channel',
      deviceLabel: 'Device',
      statusLabel: 'Status',
      colCustomer: 'Customer',
      colWhen: 'When',
      colEmail: 'Sign-in address',
      colSentToIdp: 'Sent to Microsoft',
      colUser: 'User',
      unknownEmail: 'Unknown (no email before Microsoft)',
      nothingSentToIdp: 'Nothing sent to Microsoft',
      sentToIdp: 'Sent to Microsoft: {{value}}',
      sentToIdpNone: 'Sent to Microsoft: nothing',
      idpUsernameFilledThere: 'The user types the username at the IdP (Skåne: rsid@skane.se)',
      nothingPrefillsIdp: 'No username prefilled at Microsoft',
      exportError: 'Could not export CSV',
      colOrg: 'Customer',
      colChannel: 'Channel',
      colDevice: 'Device',
      colReason: 'Reason',
      showing: 'Showing {{from}}–{{to}} of {{total}}',
      client: {
        web: 'Computer (web)',
        mobile: 'Mobile app',
        unknown: 'Unknown channel',
      },
      device: {
        phone: 'Phone',
        tablet: 'Tablet',
        computer: 'Computer',
        unknown: 'Unknown device',
      },
      os: {
        ios: 'iOS',
        android: 'Android',
        windows: 'Windows',
        macos: 'macOS',
        linux: 'Linux',
        unknown: 'unknown OS',
      },
      status: {
        unsuccessful: 'Failed and abandoned',
        failed: 'Failed',
        abandoned: 'Abandoned',
        in_progress: 'In progress',
        success: 'Succeeded',
      },
      reason: {
        none: '–',
        idp_error: 'Rejected at Microsoft / IdP',
        callback_error: 'Error after returning from login',
        initiate_error: 'Could not start SSO',
        app_no_exchange: 'The app did not collect the session',
        abandoned_at_idp: 'Abandoned at Microsoft (no return)',
        abandoned_after_callback: 'Abandoned after return',
        unknown_error: 'Unknown error',
      },
    },
    vehicleCarinfoSync: {
      title: 'Sync Vehicles from Car.info',
      description: 'Sync vehicle information from the Car.info service',
    },
    ecoSettings: {
      title: 'ECO Settings',
      description: 'Configure thresholds for ECO driving',
      selectOrganization: 'Select organization',
      globalDefaults: 'Global defaults',
      harshDriving: 'Harsh Driving',
      harshDrivingDescription: 'Thresholds for detecting harsh driving',
      maxAcceleration: 'Max acceleration (m/s²)',
      maxBraking: 'Max braking (m/s²)',
      maxCornering: 'Max cornering (m/s²)',
      excessiveIdling: 'Excessive idling (seconds)',
      overspeedPercent: 'Overspeed threshold (%)',
      greenDriving: 'Green Driving',
      greenDrivingDescription: 'Thresholds for rewarding smooth driving',
      greenMaxAcceleration: 'Max acceleration for green driving (m/s²)',
      greenMaxBraking: 'Max braking for green driving (m/s²)',
      greenMinSpeed: 'Min speed (km/h)',
      greenMinDuration: 'Min duration (seconds)',
      save: 'Save settings',
      saved: 'Settings saved',
      saveError: 'Could not save settings',
      infoTitle: 'About thresholds',
      infoText: 'These thresholds are used to detect ECO driving events. Teltonika standard values are based on manufacturer recommendations. You can customize values per organization or use global defaults.',
    },
  },

  // Systems & Modules
  systems: {
    noModules: 'No modules available',
    selectModule: 'Select a module to continue',
  },

  // Device Commands
  deviceCommands: {
    title: 'Send Commands to Device',
    description: 'Select commands and send to a device',
    availableCommands: 'Available Commands',
    selectedCommands: 'Selected Commands',
    addCommand: 'Add Command',
    removeCommand: 'Remove',
    deviceImei: 'Device IMEI',
    deviceImeiPlaceholder: 'Enter IMEI (e.g. 869616066816375)',
    searchDevicePlaceholder: 'Search IMEI or vehicle...',
    searchCommands: 'Search commands...',
    noCommands: 'No commands found',
    noCommandsSelected: 'Select commands from catalog',
    noDevicesFound: 'No devices found',
    loadingDevices: 'Loading devices...',
    loading: 'Loading...',
    sendCommands: 'Send Commands',
    sendCount: 'Send {count} command(s)',
    sending: 'Sending...',
    commandsSent: 'Command(s) sent',
    errorSending: 'Could not send commands',
    errorLoading: 'Could not load commands',
    requiredParameter: 'Parameter required',
    enterImei: 'Enter device IMEI',
    selectAtLeastOne: 'Select at least one command',
    vehicleLabel: 'Vehicle',
    selectOption: 'Select...',
    booleanYes: 'Yes',
    booleanNo: 'No',
  },

  // Audit Log
  auditLog: {
    title: 'My Sent Commands',
    description: 'Log of all commands and queries you have sent to devices',
    dateTime: 'Date/Time',
    device: 'Device (IMEI)',
    command: 'Command',
    parameters: 'Parameters',
    status: 'Status',
    response: 'Response',
    showParameters: 'Show parameters',
    showResponse: 'Show response',
    received: 'Received',
    noCommands: 'No commands found',
    loading: 'Loading...',
    total: 'Total',
    successful: 'Successful',
    pending: 'Pending',
    failed: 'Failed',
    devices: 'Devices',
    commandTypes: 'Command Types',
    filterImei: 'Search IMEI...',
    filterCommand: 'Search command...',
    filterStatus: 'All statuses',
    filterDateFrom: 'From date',
    filterDateTo: 'To date',
    statusPending: 'Pending',
    statusSent: 'Sent',
    statusAcknowledged: 'Acknowledged',
    statusFailed: 'Failed',
    statusTimeout: 'Timeout',
    showing: 'Showing',
    of: 'of',
    previous: 'Previous',
    next: 'Next',
    queueStatus: 'Queue status',
    lastSeen: 'Last connected',
    inQueue: 'In queue',
    showCount: 'Show ({count})',
    none: 'None',
    deleteCommand: 'Delete command',
    justNow: 'Just now',
    minutesAgo: '{count} min ago',
    hoursAgo: '{count} hrs ago',
    daysAgo: '{count} days ago',
    never: 'Never',
  },

  // Migration
  migration: {
    title: 'Data Migration',
    description: 'Migrate data from old databases to new system',
    selectDatabase: 'Select Database',
    selectMigrations: 'Select what to migrate',
    migrationOrder: 'Migrations run in the order listed. Check the ones you want to run.',
    testConnection: 'Test Connection',
    testing: 'Testing...',
    connected: 'Connected',
    disconnected: 'Disconnected',
    connectionSuccess: 'Connection successful',
    connectionFailed: 'Connection failed',
    connectionError: 'Connection test error',
    errorLoadingDatabases: 'Error loading databases',
    selectDatabaseFirst: 'Please select a database first',
    selectMigrationsFirst: 'Please select at least one migration to run',
    runSelected: 'Run Selected',
    dryRun: 'Dry Run',
    dryRunDescription: 'Show what would be migrated without saving data',
    run: 'Run Migration',
    rerun: 'Run Again',
    running: 'Running',
    completed: 'Completed',
    failed: 'Failed',
    success: 'Migration succeeded: {succeeded} of {processed} records',
    error: 'Migration error',
    runAll: 'Run All Migrations',
    records: 'records',
    processed: 'Processed',
    succeeded: 'Succeeded',
    errors: 'Errors',
    warnings: 'Warnings',
    users: 'Users',
    usersDescription: 'Migrate users from old database',
    organizations: 'Organizations',
    organizationsDescription: 'Migrate organizations from old database (must run first)',
    vehicles: 'Vehicles',
    vehiclesDescription: 'Migrate vehicles from old database',
    deviceVehicleHistory: 'Device–vehicle history',
    deviceVehicleHistoryDescription: 'Import when IMEI was connected to which vehicle (obd_config_logger). Requires vehicle migration to be run first.',
    deviceOrganizations: 'Devices to organizations',
    deviceOrganizationsDescription: 'Link IMEI devices to organizations from tobd. Sets status CUSTOMER on devices not already mounted in a vehicle.',
    mongoTripsVehicleBackfill: 'Mongo: sync trip vehicleId',
    mongoTripsVehicleBackfillDescription:
      'Updates MongoDB trips.vehicleId from active devices.vehicle_id per IMEI (same idea as trip-sync IMEI remap). Run after vehicle migration; affects the whole database, not only selected orgs.',
    step1: 'Select database',
    step2: 'Select organizations',
    step3: 'Run migrations',
    selectOrganizations: 'Select organizations to import',
    selectOrganizationsDescription: 'Check the organizations you want to import. Sub-organizations are included automatically.',
    loadingOrganizations: 'Loading organizations...',
    noOrganizationsFound: 'No organizations found in database',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    selectedCount: '{count} selected',
    next: 'Next',
    back: 'Back',
    searchOrganizations: 'Search organizations...',
    selectOrganizationsFirst: 'Please select at least one organization to import',
    children: 'children',
    topNode: 'Top node',
    preview: 'Preview / Validate',
    previewing: 'Validating...',
    previewTitle: 'Preview / validation',
    previewDescription: 'Check name collisions, structure conflicts and vehicle-to-group before running. Does not change any data.',
    previewError: 'Preview failed',
    previewOrgs: 'Organizations',
    previewCollisions: 'Name collisions',
    previewConflicts: 'Structure conflicts',
    previewVehicles: 'Vehicles (OK/total)',
    previewVehicleIssues: 'Vehicles skipped',
    previewColName: 'Name',
    previewColPath: 'Source o_path',
    previewColExisting: 'Existing in target',
    previewColCurrentParent: 'Current parent',
    previewColSourceParent: 'Source parent',
    previewColReg: 'Reg. no.',
    previewColImei: 'IMEI',
    previewColStatus: 'Status',
    previewStatusUnmapped: 'Org not mapped (skipped)',
    previewStatusNoImei: 'Missing IMEI (skipped)',
    previewCollisionNote: 'The same name already exists in the target (different structure/source). Import creates a SEPARATE node – no merge by name.',
    previewNoIssues: 'No issues found.',
    previewGroups: 'Groups (existing/total)',
    previewNewShort: 'new',
    previewGroupOverview: 'Groups – exist in system?',
    previewColInSystem: 'In system',
    previewGroupExists: 'Exists',
    previewGroupNameCollision: 'Name exists (other group)',
    previewGroupNew: 'Missing – will create',
    previewGroupWillCreate: 'Created on import',
    previewGroupNote: 'Groups marked "Missing – will create" do not exist in the system and are created on import. "Name exists (other group)" = the same name already exists but on a different node.',
    previewVehiclesOtherGroup: 'Vehicles in another group',
    previewVehiclesOtherGroupNote: 'These vehicles already exist in the system but are in a different group than the one they would be mapped to.',
    previewColCurrentGroup: 'Currently in',
    previewColTargetGroup: 'Would map to',
  },

  // Devices
  devices: {
    imei: 'IMEI',
    model: 'Model',
    currentFirmware: 'Current Firmware',
    fotaStatus: 'FOTA Status',
    currentConfiguration: 'Current Configuration',
    gsmNumber: 'GSM Number',
    searchPlaceholder: 'Search by IMEI (full or partial), model, firmware, configuration or GSM...',
    showingResults: 'Showing {count} of {total} devices',
    lastSync: 'Last synced',
    lastSeen: 'Last seen',
    seenAt: 'Seen at',
    online: 'Online',
    offline: 'Offline',
    noResults: 'No devices matched the search',
    noDevices: 'No devices found',
    loadCommandsError: 'Could not load commands',
    loadDevicesError: 'Could not load devices',
    sendCommandsError: 'Could not send commands',
    simStatus: 'SIM STATUS',
    simStatusChanged: 'SIM status changed',
    couldNotChangeStatus: 'Could not change status',
    statusChangeError: 'Error changing status',
    noSim: 'No SIM',
    deviceDetailsTitle: 'Device Details',
    firmware: 'Firmware',
    configuration: 'Configuration',
    simCardJasper: 'SIM Card (Jasper)',
    fetchingSimStatus: 'Fetching SIM status...',
    ratePlan: 'Rate Plan',
    commPlan: 'Comm. Plan',
    usageThisPeriod: 'Usage this period',
    sessions: 'Sessions',
    couldNotFetchSimStatus: 'Could not fetch SIM status',
    simStatusActivated: 'Activated',
    simStatusReady: 'Ready',
    simStatusInactive: 'Inactive',
    simStatusInventory: 'Inventory',
    simStatusRetired: 'Retired',
    simStatusTest: 'Test',
    syncNow: 'Sync devices now',
    syncStarted: 'Device sync started',
    syncStartedBody: 'Teltonika and Jasper updates are running in the background (may take several minutes). FOTA fields and last synced update when the job finishes.',
    syncForbidden: 'You do not have permission to start device sync.',
    syncFailed: 'Could not start device sync',
  },

  // Vehicles
  vehicles: {
    loadError: 'Could not load vehicles',
    deviceLink: {
      modalTitle: 'Link device to {{reg}}',
      successTitle: 'Device linked',
      successMessage: 'Device linked to {{reg}}',
      successAssigned: 'The device is now assigned to {{org}}',
      existingDeviceWarning: 'The vehicle already has a device: ',
      unmountToStock: 'Unmount existing (to stock)',
      swap: 'Swap',
      searchPlaceholder: 'Search IMEI, model, ICCID...',
      filterAvailable: 'Available (not mounted)',
      filterStock: 'Stock',
      filterCustomer: 'Customer',
      filterReturned: 'Returned',
      countAvailable: 'Showing {{count}} available devices',
      countFilteredOrg: 'Filtered to your organization',
      assignedToOrg: 'Will be assigned to "{{org}}" when linked',
      noDevicesFound: 'No available devices found',
      loadingDevices: 'Loading devices...',
      statusCurrent: 'Current',
      selectedDevice: 'Selected device: ',
      deviceMountedElsewhere: 'Note: Device is mounted on {{reg}} — it will be unmounted from there',
      linking: 'Linking...',
      linkButton: 'Link device to vehicle',
      statusStock: 'Stock',
      statusCustomer: 'Customer',
      statusVehicle: 'Mounted',
      statusReturned: 'Returned',
    },
  },

  // SIM Cards
  simCards: {
    title: 'SIM Card Management',
    description: 'Manage SIM cards, view usage and create reports',
    overview: 'Overview',
    simCards: 'SIM Cards',
    reports: 'Reports',
    totalSimCards: 'Total SIM Cards',
    totalDataUsage: 'Total Data Usage',
    totalSms: 'Total SMS',
    billingCycle: 'Billing Cycle',
    ratePlans: 'Rate Plans',
    refresh: 'Refresh',
    fetch: 'Fetch',
    allStatuses: 'All statuses',
    searchPlaceholder: 'Search ICCID, MSISDN, IMEI...',
    ratePlan: 'Rate Plan',
    dataMB: 'Data (MB)',
    activated: 'Activated',
    noSimCards: 'No SIM cards found',
    loading: 'Loading...',
    createReport: 'Create Report',
    creatingReport: 'Creating report...',
    reportCreated: 'Report created',
    latestReport: 'Latest Report',
    deviceCount: 'Device Count',
    totalData: 'Total Data',
    period: 'Period',
    invoiceNote: 'Invoices cannot be fetched automatically. Log in to Jasper Control Center to download invoices.',
    // Status
    statusActivated: 'Activated',
    statusActivationReady: 'Ready',
    statusDeactivated: 'Inactive',
    statusInventory: 'Inventory',
    statusRetired: 'Retired',
    statusTestReady: 'Test',
    statusDistribution: 'Status Distribution',
    activatedCount: 'Activated',
    deactivatedCount: 'Deactivated',
    otherCount: 'Other',
    lastSynced: 'Last synced',
    showingRange: 'Showing {start}-{end} of {total}',
    previous: 'Previous',
    next: 'Next',
    createUsageReport: 'Create Usage Report',
    createUsageReportDesc: 'Generate an aggregated report of all SIM card usage for the current billing cycle.',
    noteLabel: 'Note:',
    // Errors
    fetchError: 'Could not fetch data',
    createReportError: 'Could not create report',
  },

  // Geofences
  geofences: {
    title: 'Geofences',
    subtitle: 'Manage geographic areas',
    list: 'Geofences',
    create: 'Create geofence',
    edit: 'Edit geofence',
    delete: 'Delete geofence',
    noGeofences: 'No geofences created',
    searchPlaceholder: 'Search geofences...',
    showSystemGeofences: 'Show system geofences',
    area: 'Area',
    polygonWith: 'Polygon with',
    points: 'points',
    editPoints: 'Edit points',
    redraw: 'Redraw',
    drawPolygon: 'Draw polygon on map',
    editModeActive: 'Editing mode active',
    dragPoints: 'Drag',
    redPointsToMove: 'red points to move them',
    clickGreenPoints: 'Click green points',
    toAdd: 'to add new points',
    rightClickRedPoint: 'Right-click',
    onRedToRemove: 'on a red point to remove it',
    doneBackToForm: 'Done - Back to form',
    name: 'Name',
    description: 'Description',
    scope: 'Scope',
    scopeGlobal: 'Global (all organizations)',
    scopeOrganization: 'Specific organization',
    organization: 'Organization',
    isActive: 'Active',
    alarmOnEnter: 'Alarm on entry',
    alarmOnExit: 'Alarm on exit',
    filterCoordinates: 'Hide coordinates within area',
    isToll: 'Toll area',
    costPerEntry: 'Cost per entry (SEK)',
    costPerMinute: 'Cost per minute (SEK)',
    polygonDrawn: 'Polygon drawn',
    finishDrawing: 'Finish',
    cancelDrawing: 'Cancel',
    minPoints: 'A polygon must have at least 3 points',
    hide: 'Hide',
    show: 'Show',
    inactive: 'Inactive',
    alarmIn: 'Alarm in',
    alarmOut: 'Alarm out',
    activated: 'Geofence activated',
    deactivated: 'Geofence deactivated',
    created: 'Geofence created',
    updated: 'Geofence updated',
    deleted: 'Geofence deleted',
    errorLoading: 'Could not load geofences',
    errorSaving: 'Could not save geofence',
    errorDeleting: 'Could not delete geofence',
    confirmDelete: 'Do you want to delete geofence',
    minPointsRequired: 'At least 3 points required',
    pointRemoved: 'Point removed',
    pointAdded: 'New point added',
    loadError: 'Could not load geofences',
    events: {
      title: 'Geofence Events',
      subtitle: 'View all entry and exit events with location, time and cost',
      filter: 'Filter',
      vehicle: 'Vehicle',
      allVehicles: 'All vehicles',
      geofence: 'Geofence',
      searchGeofence: 'Search geofence...',
      allGeofences: 'All geofences',
      geofencesMatch: 'geofences match',
      eventType: 'Event Type',
      allTypes: 'All types',
      enter: 'Enter',
      exit: 'Exit',
      fromDate: 'From date',
      toDate: 'To date',
      clearFilters: 'Clear filters',
      refresh: 'Refresh',
      vehicleColumn: 'Vehicle',
      geofenceColumn: 'Geofence',
      eventTypeColumn: 'Type',
      locationColumn: 'Location',
      timeColumn: 'Time',
      durationColumn: 'Duration',
      costColumn: 'Cost',
      totalCost: 'Total cost',
      noEvents: 'No events found',
      loading: 'Loading events...',
      error: 'Could not load geofence events',
      totalEvents: 'Total events',
      enterEvents: 'Enter events',
      exitEvents: 'Exit events',
      error404: 'Geofence events endpoint not found (404)',
      error401: 'Authentication required (401)',
      searchVehicle: 'Search vehicle...',
      vehicleFallback: 'Vehicle',
      eventColumn: 'Event',
      durationInside: 'Duration inside',
      showing: 'Showing',
      of: 'of',
      events: 'events',
      page: 'Page',
      vehiclesMatch: 'vehicles match',
    },
  },

  // Trips
  tripAnalysis: {
    title: 'Analyze Trips',
    description: 'Compare registered trips against the vehicle bin file to identify discrepancies',
    analyze: 'Analyze',
    replay: 'Replay',
    replayHint: 'Re-run trip detection from the bin file (creates/updates trips)',
    replayStarted: 'Replay started',
    replayFailed: 'Could not start replay',
    selectVehicleFirst: 'Select a vehicle to start',
    registered: 'Registered',
    fromBin: 'From bin file',
    diff: 'Difference',
    records: 'points',
    registeredTrips: 'Registered trips',
    binTrips: 'Trips from bin file',
    noRegistered: 'No registered trips',
    noBinTrips: 'No trips in the bin file (see warnings above)',
    loadBinHint: 'Analyze to load the bin file',
    maxSpeed: 'max',
    gpsPoints: 'GPS points',
    noAvl250: 'No AVL250',
    warnNoRecords: 'No GPS points in the bin file',
    warnNoSpeed: 'Speed = 0 in all points (no driving log)',
    warnNoAvl250: 'AVL250 (trip signal) is missing entirely',
    warnNoGpsFix: 'GPS fix often missing (sat=0)',
    warnSparse: 'Sparse points – heartbeats only',
    reconstructed: 'Reconstructed',
    reconstructedHint: 'Reconstructed trip (GPS missing at cold start) - distance from OEM odometer or OSRM estimate',
    warnNoBinFile: 'No bin file exists for this vehicle/date on the gateway',
    underDetectedTitle: 'Fewer bin trips than registered - likely detection miss',
    underDetectedBody:
      'The bin file contains driving (speed > 0) but batch/replay detection found fewer trips than the registered ones. The raw data is correct - it is the trip detection (same as replay uses) that under-segments, e.g. for devices with sparse AVL250. Do NOT run replay for this vehicle: it would replace correct trips with fewer.',
    replayDestructiveConfirm:
      'Warning: bin/replay detection found fewer trips than the registered ones even though the bin file contains driving. A replay would likely REPLACE correct trips with fewer. Continue anyway?',
  },
  obdVehicles: {
    title: 'OBD vehicle list',
    description: 'Search Teltonika OBD OEM support by make and model. The list is for FMX001/FMM00A/FMX003, not FMM003 certification.',
    deviceFamilyNote: 'Source: Teltonika wiki for FMX001, FMM00A and FMX003. DQFleet P&P runs FMM003 — use the list as an OEM reference.',
    checkNow: 'Check now',
    import: 'Import',
    checkFailed: 'Could not check the wiki',
    searchFailed: 'Search failed',
    syncFailed: 'Could not import the list',
    detailFailed: 'Could not load details',
    alreadyLatest: 'Already the latest version',
    imported: 'Imported {{count}} rows',
    wikiDate: 'Wiki date',
    ourDate: 'Our version',
    none: 'None',
    rows: 'Rows',
    newerAvailable: 'Newer file available',
    allMakes: 'All makes',
    allParameters: 'All parameters',
    make: 'Make',
    model: 'Model',
    year: 'Year',
    fuelType: 'Fuel',
    resultCount: '{{count}} matches',
    scrollHint: 'Scroll sideways for all parameters',
    deviceFamily: 'Source devices',
    relatedKb: 'Related wiki pages',
    fleetMatches: 'Vehicles in the fleet',
    forceNonObd: 'forced Non-OBD',
  },
  obdKnowledge: {
    title: 'OBD Knowledge',
    description: 'Searchable copy of Teltonika OBD Solutions. Always links back to the wiki.',
    sources: 'Sources',
    sync: 'Sync now',
    loadFailed: 'Could not load sources',
    searchFailed: 'Search failed',
    syncFailed: 'Sync failed',
    pageMissing: 'Page not fetched yet — run sync',
    synced: 'Sync done: {{updated}} updated of {{checked}}',
    notFetched: 'not fetched',
    openWiki: 'Open on Teltonika',
    searchPlaceholder: 'Search e.g. Non-OBD, EV, Kia…',
    vehicleHits: '{{count}} vehicles in the list',
    openVehicleList: 'Open vehicle list',
    vehicleListPageNote:
      'The wiki page is a large table. Use the OBD vehicle list to search make, model and parameters.',
    noVehicleHits: 'No rows in the imported vehicle list. Import the xlsx under OBD vehicle list.',
    askAi: 'Ask AI',
    askPlaceholder: 'E.g. Which OEM parameters are supported for Kia Niro and how do I debug if they are missing?',
    ask: 'Ask',
    askFailed: 'AI could not answer',
  },
  trips: {
    private: 'Private trip',
    business: 'Business trip',
    privateHidden: 'Private - details hidden',
    privateNoMap: 'Map view not available for private trips',
    title: 'Trips',
    description: 'View and analyze trips for your vehicles',
    selectVehicle: 'Select Vehicle',
    selectVehiclePlaceholder: '-- Select Vehicle --',
    tripsList: 'Trips',
    loading: 'Loading trips...',
    noTrips: 'No trips found for this vehicle',
    selectVehicleFirst: 'Select a vehicle to view trips',
    fromDate: 'From date',
    toDate: 'To date',
    clearDateFilter: 'Clear date filter',
    selectTripToView: 'Select a trip to view on map',
    selectVehicleToView: 'Select a vehicle to start',
    completed: 'Completed',
    alarms: 'Alarms',
    alarmCount: '{{count}} alarms',
    alarmCountTitle: 'Driving behavior alarms on this trip',
    noAlarms: 'No alarms',
    electricShare: 'Electric {{pct}} %',
    electricDistance: '{{km}} km electric',
    electricShareTitle: 'Share of distance in electric drive ({{km}} km electric)',
    errorLoadingVehicles: 'Failed to load vehicles',
    errorLoadingTrips: 'Failed to load trips',
    speedChart: 'Speed Chart',
    showSpeedChart: 'Show Speed Chart',
    hideSpeedChart: 'Hide Speed Chart',
    vehicleSpeed: 'Vehicle Speed',
    speedLimit: 'Speed Limit',
    speedUnit: 'km/h',
    playTrip: 'Play',
    pauseTrip: 'Pause',
    resetTrip: 'Reset',
    jammingLegend: 'Jamming (AVL 249) – {count} GPS points',
    speed: 'Speed',
    routePointCount: 'Route Points',
    noSpeedLimitData: 'Speed limit data is missing for all points in this trip. Data may be missing for older trips or if the OSM service could not fetch speed limits.',
    speedLimitRefreshHint: 'Speed limits are added in the background after a trip ends. Reload the trip (switch trip and back, or refresh the page) after a few seconds if they are still missing.',
    partialSpeedLimitData: 'Speed limit is available for only {withLimit} of {total} points.',
    loadRouteError: 'Could not load trip route',
    loadingRoute: 'Loading route...',
    showPointsTable: 'Show points table',
    hidePointsTable: 'Hide points table',
    pointsTableTitle: 'Points (time, speed, distance driven)',
    avl199: 'Distance driven (km)',
    time: 'Time',
    binData: 'Bin data',
    binDataModalTitle: 'Bin data – trip',
    binDataLoading: 'Loading bin data...',
    binDataFetchError: 'Could not load bin data',
    saveAsCsvColon: 'Save as CSV (semicolon-separated)',
    close: 'Close',
    binDataTimestamp: 'Timestamp',
    binDataAvl: 'AVL',
    binDataParameter: 'Parameter',
    binDataDescription: 'Description',
    binDataValue: 'Value',
  },

  // ECO Driving
  eco: {
    title: 'ECO Driving',
    description: 'Analyze driving habits and environmental impact',
    score: 'ECO Score',
    overview: 'Overview',
    events: 'Events',
    trends: 'Trends',
    comparison: 'Comparison',
    totalEvents: 'Total Events',
    harshAcceleration: 'Harsh Acceleration',
    harshBraking: 'Harsh Braking',
    harshCornering: 'Harsh Cornering',
    overspeed: 'Overspeed',
    excessiveIdling: 'Excessive Idling',
    coasting: 'Coasting',
    greenDriving: 'Green Driving',
    fuelWaste: 'Fuel Waste',
    energyLoss: 'Energy Loss',
    fuelSaved: 'Fuel Saved',
    energySaved: 'Energy Saved',
    estimatedExtraCo2: 'Estimated extra CO₂ emissions',
    estimatedFuelLoss: 'Estimated fuel loss',
    noTailpipeEmissions: 'Electric vehicle — no tailpipe emissions',
    comparisonWithinOrg: 'Comparison within your organization',
    fuelLossPerDay: 'Fuel loss per day (ml)',
    energyLossPerDay: 'Energy loss per day (kWh)',
    co2Emission: 'CO₂ Emission',
    scoreImpact: 'Score Impact',
    eventsPerTrip: 'Events per Trip',
    environmentImpact: 'Environmental Impact',
    distributionByType: 'Distribution by Type',
    eventsPerDay: 'Events per Day',
    noEvents: 'No events during selected period',
    basedOnTrips: 'Based on {trips} trips and {events} events',
    of100: 'of 100',
    totalScoreImpact: 'Total Score Impact',
  },

  speedCompliance: {
    title: 'Speed Compliance',
    description: 'Analyze speed compliance by organization and vehicle',
    overview: 'Overview',
    organizations: 'By organization',
    vehicles: 'By vehicle',
    trend: 'Trend',
    complianceRate: 'Compliance',
    overspeeds: 'Violations',
    trips: 'Trips',
    distance: 'Distance',
    timeOverLimit: 'Time over limit',
    exportExcel: 'Export Excel',
    topOffenders: 'Most violations (vehicles)',
    complianceByOrg: 'Compliance by organization',
    trendOverTime: 'Compliance over time',
    newSystem: 'DQFleet (New System)',
    oldSystem: 'Old System',
    noLegacyConnection: 'Vehicle has no connection to old system',
    tripsFound: 'trips found',
    noTripsForDate: 'No trips for selected date',
    selectVehicle: 'Select a vehicle',
    loadingTrips: 'Loading trips...',
    loadingOldTrips: 'Loading trips from old system...',
    source: 'Source',
    oldId: 'Old ID',
    notMigrated: 'Not migrated',
    gpsPoints: 'GPS points',
    totalDistance: 'Total distance',
  },

  // Energy Consumption
  energyConsumption: {
    title: 'Energy Consumption',
    description: 'Analyze fuel consumption by organization and vehicle',
    totalVehicles: 'Total vehicles',
    totalDistance: 'Total distance',
    totalFuel: 'Total fuel used',
    avgConsumption: 'Average consumption',
    fuel: 'Fuel (L)',
    wltp: 'WLTP Combined',
    consumption: 'Consumption',
    avgSpeed: 'Average speed',
    fuelByOrg: 'Fuel by organization',
    consumptionDist: 'Consumption distribution',
    consumptionTrend: 'Consumption trend',
    distanceTrend: 'Distance trend',
    lowConsumption: 'Low consumption',
    mediumConsumption: 'Medium consumption',
    highConsumption: 'High consumption',
  },

  // Heatmap
  jamDetection: {
    title: 'Jam Detection',
    from: 'From',
    to: 'To',
    refresh: 'Refresh',
    markers: 'Markers',
    heatmap: 'Heatmap',
    totalPoints: 'Jamming points',
    affectedVehicles: 'Affected vehicles',
    vehicle: 'Vehicle',
    truncated: 'Showing max 20,000 points – narrow the date range',
    noData: 'No jamming in the selected period',
    loadError: 'Could not load jam detection data',
  },
  heatmap: {
    title: 'Heatmap',
    description: 'Visualize GPS data and speed violations',
    organization: 'Organization',
    selectOrganization: 'Select organization...',
    vehicles: 'Vehicles',
    selectVehicle: 'Select vehicle...',
    selectVehicleFromOrg: 'Select vehicle from organization...',
    allVehicles: 'All',
    allVehiclesInOrg: 'All vehicles in organization',
    vehiclesInOrg: 'vehicles in organization',
    fromDate: 'From date',
    toDate: 'To date',
    mode: 'Mode',
    modeAll: 'All points',
    modeOverspeed: 'Speed violations',
    modeIdling: 'Idling/Idle Time',
    modeParking: 'Parking',
    modeEco: 'ECO-events/Harsh Driving',
    modeTimePattern: 'Time-based patterns',
    modeGeofence: 'Geofence violations',
    modeIncidents: 'Accidents/Incidents',
    modeCharging: 'Charging stations',
    modeRiskScore: 'Combined risk score',
    timePattern: 'Time pattern',
    timePatternAll: 'All times',
    timePatternMorning: 'Morning (06:00-09:00)',
    timePatternDay: 'Day (09:00-17:00)',
    timePatternEvening: 'Evening (17:00-22:00)',
    timePatternNight: 'Night (22:00-06:00)',
    loadData: 'Load data',
    loading: 'Loading...',
    loadingData: 'Loading heatmap data...',
    dataLoaded: '{count} points loaded',
    tripCount: '{count} trips, {points} points',
    errorLoadingVehicles: 'Failed to load vehicles',
    errorLoadingData: 'Failed to load heatmap data',
    showInfo: 'Show information about analysis modes',
    infoTitle: 'Information about analysis modes',
    infoAll: 'Shows all GPS points from selected vehicles during the selected time period. Intensity is based on the number of points per area.',
    infoOverspeed: 'Shows where vehicles have exceeded the speed limit. Intensity is based on the number of violations and maximum excess speed.',
    infoIdling: 'Shows where vehicles have been stationary with ignition on. Intensity is based on total idling time per location.',
    infoParking: 'Shows where trips end (parking locations). Each pin represents a parking location with a 100 meter radius. Color is based on the number of times parked: Green (1-2), Yellow (3-5), Orange (6-10), Red (11+).',
    infoEco: 'Shows harsh driving (harsh acceleration, braking, cornering). Intensity is based on the number of events and severity.',
    infoTimePattern: 'Shows traffic patterns based on time of day. Select morning, day, evening, or night to see when vehicles are most active.',
    infoGeofence: 'Shows where vehicles have left or entered geofence areas. Intensity is based on the number of violations.',
    infoIncidents: 'Shows serious events such as panic alarms, accidents, or crashes. Intensity is based on severity and number of incidents.',
    infoCharging: 'Shows where vehicles have been charged (EV). Based on long stops (at least 5 minutes). Intensity is based on the number of charges and unique vehicles.',
    infoRiskScore: 'Combines multiple risk factors (speed violations, ECO events, incidents) to show areas with high risk. Intensity is based on combined risk score.',
  },
  // Heatmap Import
  heatmapImport: {
    title: 'Heatmap Import',
    selectFile: 'Select CSV file to import',
    fileFormat: 'Format: timestamp;latitude;longitude;registration_number;speed;speed_limit',
    selectFileButton: 'Select file',
    uploading: 'Uploading...',
    uploadSuccess: '{valid} of {total} rows imported',
    invalidRows: '{count} rows could not be imported',
    uploadError: 'Failed to upload file',
    invalidFileType: 'Only CSV files are allowed',
    fileTooLarge: 'File is too large (max 100MB)',
    importComplete: 'Import complete',
    importStats: '{valid} points, {vehicles} vehicles',
    clear: 'Clear',
    cleared: 'Import data cleared',
    clearError: 'Failed to clear data',
    selectVehicle: 'Select vehicle...',
  },

  // Realtime
  realtime: {
    loading: 'Loading positions...',
    fetchError: 'Failed to fetch realtime positions',
    speed: 'Speed',
    heading: 'Heading',
    ignition: 'Ignition',
    movement: 'Movement',
    voltage: 'Voltage',
    battery: 'Battery',
    on: 'On',
    off: 'Off',
    moving: 'Moving',
    still: 'Still',
  },

  // Support Admin
  supportAdmin: {
    title: 'Support Administration',
    subtitle: 'Manage support tickets and system messages',
    openKeywin: 'KeyWin administration',
    openKeywinTitle: 'Open KeyWin administration in a new tab',
    openProvisioning: 'Cabinet provisioning',
    openProvisioningTitle: 'Open cabinet provisioning (internal/VPN only)',
    queue: 'Support Queue',
    broadcasts: 'Broadcasts',
    stats: 'Statistics',
    allTickets: 'All tickets',
    assignToMe: 'Assign to me',
    internalNote: 'Internal note',
    sendReply: 'Send reply',
    sendBroadcast: 'Send broadcast',
    targetAll: 'All users',
    targetOrg: 'Specific organization',
    includeChildren: 'Include sub-organizations',
    pinMessage: 'Pin message',
    newBroadcast: 'Send new broadcast',
    sentBroadcasts: 'Sent broadcasts',
    readBy: 'read',
    selectOrg: 'Select organization',
    priorityInfo: 'Info (blue)',
    priorityWarning: 'Warning (yellow)',
    priorityCritical: 'Critical (red)',
    faq: 'FAQ',
    faqQuestion: 'Question',
    faqAnswer: 'Answer',
    faqQuestionPlaceholder: 'Question...',
    faqAnswerPlaceholder: 'Answer...',
    addToFaq: 'Add to FAQ',
    addToFaqFromTicket: 'Add this ticket as FAQ',
    saveToFaq: 'Save to FAQ',
    newFaq: 'New FAQ',
    faqList: 'FAQ list',
    sortOrder: 'Sort order',
    fillQuestionAnswer: 'Fill in question and answer',
    deleteFaqConfirm: 'Are you sure you want to delete this FAQ?',
    deleteBroadcastConfirm: 'Do you want to delete this broadcast?',
    searchTickets: 'Search tickets...',
    noTickets: 'No tickets',
    messageTitlePlaceholder: 'Title of the message',
    categoryPlaceholder: 'e.g. Installation and setup',
    queueTab: 'Support Queue',
    broadcastTab: 'Broadcasts',
    broadcastSendTitle: 'Send new broadcast',
    broadcastSentTitle: 'Sent broadcasts',
    noBroadcasts: 'No broadcasts sent',
    broadcastMessage: 'Message *',
    broadcastMessagePlaceholder: 'Write your message...',
    broadcastTitle: 'Title *',
    broadcastPinned: 'Pin message (shown at the top)',
    broadcastScope: 'Target audience',
    broadcastPriority: 'Priority',
    broadcastOrg: 'Organization',
    broadcastSelectOrg: 'Select organization',
    broadcastScopeAll: 'All users',
    broadcastScopeOrg: 'Specific organization',
    sendBroadcastButton: 'Send broadcast',
    statusAll: 'All statuses',
    statusOpen: 'Open',
    statusInProgress: 'In progress',
    statusWaiting: 'Waiting customer',
    statusResolved: 'Resolved',
    statusClosed: 'Closed',
    priorityAll: 'All priorities',
    priorityCritical2: 'Critical',
    priorityHigh: 'High',
    priorityMedium: 'Medium',
    priorityLow: 'Low',
    ticketCountsOpen: 'Open',
    ticketCountsInProgress: 'In progress',
    ticketCountsWaiting: 'Waiting',
    ticketCountsResolved: 'Resolved',
    unknownUser: 'Unknown',
    assignedTo: 'Assigned',
    selectCategory: 'Select category',
    categoryLabel: 'Category',
    assignLabel: 'Assign',
    notAssigned: 'Not assigned',
    internalNoteLabel: 'Internal note',
    internalNotePlaceholder: 'Write internal note...',
    replyPlaceholder: 'Write reply to customer...',
    replyEmailPlaceholder: 'Write reply (sent as email)...',
    viaEmail: 'Via email',
    viaChat: 'Via chat',
    replyAfterStatus: 'After reply',
    replyStatusWaiting: 'Waiting for reply',
    replyStatusResolved: 'Resolved',
    sendEmailAria: 'Send email',
    sendReplyAria: 'Send reply',
    replySent: 'Reply saved',
    replySentEmail: 'Reply sent by email',
    internalNoteSaved: 'Internal note saved',
    internalNoteBadge: '🔒 Internal note',
    editInternalNote: 'Edit internal note',
    internalNoteUpdated: 'Internal note updated',
    editInternalNoteError: 'Could not update internal note',
    editedLabel: 'edited',
    statsOverview: 'Overview',
    statsTotalTickets: 'Total tickets',
    statsOpenTickets: 'Open tickets',
    statsResolvedTickets: 'Resolved tickets',
    statsAvgResponse: 'Avg. time to response',
    statsMin: 'min',
    statsPerCategory: 'By category',
    statsPerPriority: 'By priority',
    statsPerOrg: 'By organization',
    statsPerAssignee: 'By assignee',
    statsPerCategoryTab: 'By category',
    statsOrgColumn: 'Organization',
    statsPersonColumn: 'Person',
    statsCategoryColumn: 'Category',
    statsTotal: 'Total',
    statsOpen: 'Open',
    statsResolved: 'Resolved',
    statsAvgTime: 'Avg. time',
    statsSolvePct: 'Resolution rate',
    statsLoading: 'Loading statistics...',
    addCategoryTitle: 'Add category',
    categoryNameLabel: 'Name (internal) *',
    categoryNamePlaceholder: 'e.g. installation',
    categoryDescLabel: 'Description',
    confirmLabel: 'Confirm',
    createTicketButton: 'New ticket',
    createTicketTitle: 'Create ticket manually',
    createTicketSubtitle: 'For calls, walk-ins or mail that did not sync automatically',
    createTicketName: 'Contact person',
    createTicketNamePlaceholder: 'Name of the person to reply to',
    createTicketReplyTo: 'Reply to',
    createTicketReplyToHint: 'Enter phone and/or email. At least one is required.',
    createTicketPhone: 'Phone',
    createTicketPhonePlaceholder: '070-123 45 67',
    createTicketRegNumber: 'Registration no.',
    createTicketRegNumberPlaceholder: 'ABC123',
    createTicketBookingNumber: 'Booking number',
    createTicketBookingNumberPlaceholder: 'e.g. 1812',
    createTicketEmail: 'Email',
    createTicketEmailPlaceholder: 'customer@example.com',
    createTicketOrg: 'Organization',
    createTicketOrgNone: 'None / unknown organization',
    createTicketSubject: 'Subject',
    createTicketMessage: 'Description',
    createTicketMessagePlaceholder: 'What is the issue? Note the call here.',
    createTicketAssignMe: 'Assign the ticket to me',
    createTicketSubmit: 'Create ticket',
    createTicketSuccess: 'Ticket created',
    createTicketError: 'Could not create ticket',
  },

  // Chat & Support
  chat: {
    title: 'Messages & Support',
    support: 'Support',
    chat: 'Chat',
    broadcasts: 'Info',
    newTicket: 'New ticket',
    noTickets: 'No tickets',
    noBroadcasts: 'No messages',
    subject: 'Subject',
    phoneOptional: 'Phone (optional)',
    emailOptional: 'Email (optional)',
    regNumberOptional: 'Registration no. (optional)',
    bookingNumberOptional: 'Booking number (optional)',
    message: 'Describe your issue...',
    send: 'Send',
    typeReply: 'Type reply...',
    fillAllFields: 'Fill in all fields',
    ticketCreated: 'Ticket created',
    justNow: 'Just now',
    statusOpen: 'Open',
    statusInProgress: 'In progress',
    statusWaiting: 'Awaiting reply',
    statusResolved: 'Resolved',
    statusClosed: 'Closed',
    priorityLow: 'Low',
    priorityMedium: 'Medium',
    priorityHigh: 'High',
    priorityCritical: 'Critical',
    chatComingSoon: 'Internal chat coming soon!',
    chatDescription: 'Chat with colleagues in your organization',
    broadcastTitle: 'System messages',
    markAsRead: 'Mark as read',
    newConversation: 'New conversation',
    noConversations: 'No conversations',
    startConversation: 'Start a new conversation',
    searchUsers: 'Search users (name, email or RSID)...',
    noUsers: 'No users found',
    noMessages: 'No messages yet',
    typeMessage: 'Type message...',
    closeTicket: 'Mark as resolved',
    ticketClosed: 'Ticket has been closed',
    ticketClosedByUser: 'Ticket was closed by the user.',
    ticketIsClosed: 'This ticket is closed',
    faq: 'FAQ',
    faqSearchPlaceholder: 'Search FAQ...',
    noFaqFound: 'No matches',
    noFaqYet: 'No FAQ added yet',
    closeConversation: 'Close conversation',
    conversationClosed: 'Conversation closed',
    couldNotClose: 'Could not close conversation',
    minChars: 'Type at least 2 characters to search',
    noUsersFound: 'No users found',
    searchToFind: 'Start typing to search for users',
  },

  // Version
  version: {
    title: 'Version',
    fleetManagement: 'Fleet Management System',
    currentRelease: 'Current Release',
    viewChangelog: 'View Changelog',
    releaseHistory: 'Release History',
    current: 'Current',
    upcoming: 'Upcoming',
    release: {
      upcoming: {
        drivingLogApp: 'Driving log app',
      },
      v500: {
        newPlatform: 'Deque new platform',
        dashboard: 'Customizable dashboard with drag-and-drop',
        fleet: 'Fleet overview with full vehicle management',
        liveTracking: 'Real-time tracking with OSM maps',
        reports: 'Reports (trips, driving log, eco-driving, speed, energy, wear, CO2)',
        analysis: 'Analysis tools (route analysis, heatmap, crash detection)',
        crm: 'CRM with customer management and hierarchy',
        geofences: 'Geofence management with events',
        homecare: 'Homecare module',
        deviceManagement: 'Device management with SIM cards',
        admin: 'Administration (users, roles, organizations)',
        rbac: 'Role-based access control (RBAC)',
        osmMaps: 'OpenStreetMap integration',
      },
    },
  },
  // SCB Testing
  scbTesting: {
    title: 'SCB API Testing',
    description: 'Test SCB API endpoints and view results',
    selectEndpoint: 'Select Endpoint',
    parameters: 'Parameters',
    execute: 'Execute Endpoint',
    executing: 'Executing...',
    result: 'Result',
    success: 'Success',
    error: 'Error',
    responseData: 'Response Data',
    noResult: 'No result yet. Select an endpoint and click "Execute Endpoint".',
  },
  vehicleCard: {
    refresh: 'Refresh from Car.info',
    vehicleInfo: 'Vehicle Data',
    engineInfo: 'Engine & Performance',
    dates: 'Dates',
    deviceInfo: 'Device',
    specifications: 'Specifications',
    notes: 'Notes',
    tags: 'Extra Information',
    vin: 'VIN',
    brand: 'Make',
    model: 'Model',
    year: 'Model Year',
    vehicleType: 'Vehicle Type',
    fuelType: 'Fuel Type',
    engine: 'Engine',
    engineType: 'Engine Type',
    gearbox: 'Transmission',
    horsepower: 'Power',
    mileage: 'Mileage',
    registerDate: 'Registered',
    manufacturedDate: 'Manufactured',
    lastUpdated: 'Last Updated',
    externalVoltage: 'External Voltage',
    batteryLevel: 'Battery',
    fuelLevel: 'Fuel Level',
    stolenWarning: 'THIS VEHICLE IS REPORTED STOLEN!',
    drivingBanWarning: 'THIS VEHICLE HAS A DRIVING BAN!',
    statusActive: 'Active',
    statusMaintenance: 'Maintenance',
    statusInactive: 'Inactive',
    statusDeregistered: 'Deregistered',
    systemStatus: 'System status:',
    tabGeneral: 'General information',
    tabEconomy: 'Economy',
    tabCards: 'Fuel Cards',
    tabSpec: 'Specifications',
    tabProperties: 'Properties',
    tabPlacement: 'Placement',
    tabHistory: 'History',
    tabBooking: 'Booking information',
    tabBookingBadge: 'NEW',
    tabDevicePnP: 'Device / P&P',
    tabNotes: 'Notes',
    tabAttachments: 'Attachments',
    pnp: {
      globalOff:
        'Plug & Play is globally off (OBD_PROVISIONING_ENABLED≠true). Configure the FMM003 baseline under Device Management, mark devices for P&P, then enable in .env.',
      baselineMissing:
        'No FW/config file set for this model. Fill in the baseline under Device Management before enabling P&P.',
      noDevice: 'No device linked to this vehicle.',
      sectionDevice: 'Device & firmware',
      sectionProfile: 'Fuel profile',
      sectionEye: 'EYE parameters',
      sectionParams: 'Applied parameters',
      paramsHint:
        'Parameters P&P sets after approved FW/cfg. Applied = delivered in the last successful step. Planned = in the profile but not confirmed yet.',
      paramId: 'ID',
      paramValue: 'Value',
      paramLabel: 'Meaning',
      paramGroup: 'Package',
      paramStatus: 'Status',
      groupIce: 'ICE',
      groupEv: 'EV / PHEV / ID.x',
      groupProblem: 'Problem list',
      groupVin: 'VIN',
      statusApplied: 'Applied',
      statusPlanned: 'Planned',
      statusSkipped: 'Skipped',
      onProblemList: 'On problem list (Renault Trafic/Master – Non-OBD)',
      onProblemListDisable: 'On problem list (OBD-Disable 40000:0 / VW ID.x / BMW i4 / Mercedes)',
      problemListPending: 'Settings not delivered yet — the vehicle stays on the list until they are.',
      problemListApplied: 'Settings delivered {when}. The vehicle stays on the problem list.',
      notOnProblemList: 'Not on problem list',
      imei: 'IMEI',
      model: 'Model',
      currentFw: 'Current firmware',
      targetFw: 'Target firmware',
      targetConfig: 'Target config',
      status: 'P&P status',
      fuelType: 'Fuel (vehicle)',
      profile: 'Profile',
      profileSource: 'Profile source',
      profileIce: 'ICE',
      profilePheV: 'PHEV',
      profileEv: 'EV',
      eyeParams: 'EYE on device',
      eyeYes: 'Yes (active links)',
      eyeNo: 'No',
      eyeReset: 'Reset by P&P',
      eyeLinks: 'Active EYE links',
      enablePnp: 'Mark for P&P',
      disablePnp: 'Unmark P&P',
      pnpEnabled: 'Device marked for Plug & Play',
      pnpDisabled: 'P&P unmarked for device',
      simulate: 'Simulate chain',
      simulateDone: 'Simulation done',
      profileSaved: 'Profile saved',
      loadError: 'Could not load P&P status',
      saveError: 'Could not save',
      runError: 'Could not run provisioning',
    },
    placement: {
      description:
        'Save where the vehicle is normally parked (placement location). Used for area filters on the live map. Enter an address, pick a point on the map, or use the vehicle’s current GPS position.',
      pickOnMap: 'Pick on map',
      mapHelp: 'Click on the map to set placement. One point is required.',
      geocodeFromAddress: 'Get coordinates from address',
      geocodeSuccess: 'Coordinates retrieved',
      geocodeNotFound: 'Address not found',
      geocodeError: 'Geocoding failed',
      addressRequired: 'Enter at least address, postal code, or city',
      coordinates: 'Coordinates',
      clear: 'Clear placement',
      fromGps: 'Use GPS position',
      fromGpsSuccess: 'Placement taken from the vehicle GPS. Click Save.',
      fromGpsMissing: 'No current GPS position for this vehicle (no device, private trip, or no data yet)',
      fromGpsError: 'Coordinates set from GPS, but the address could not be resolved',
      gpsTime: 'Last GPS position: {{time}}',
    },
    booking: {
      banner:
        'Booking-related settings when the vehicle is in a pickup location pool. Edit the location and pool under Booking.',
      sectionDescription: 'Booking description',
      descriptionHint:
        'Editable text for booking staff (max 50 characters). If empty, registration number and transmission are used instead.',
      charCount: '{{current}}/{{max}} characters',
      charCountLong: '{{current}}/{{max}} characters',
      sectionEmailNote: 'Message in booking confirmation (email)',
      emailNoteHint:
        'Only included in the email to the user when you send the calendar invite / booking confirmation. Up to 2000 characters.',
      emailNotePlaceholder: 'Optional information for the person who booked …',
      previewForStaff: 'Preview (as seen by booking staff)',
      saveBookingDesc: 'Save booking details',
      bookingDescSaved: 'Booking details saved',
      noDescription: '—',
      sectionSlot: 'Slot time',
      slotHint:
        'Default slot length, min/max booking, buffer and bookable hours are managed in the booking module per location and calendar.',
      restMinutesLabel: 'Rest time between bookings',
      restMinutesHint:
        'Minutes this vehicle should rest between bookings (e.g. an EV that needs charging). Empty = inherit pool/organization. 0 = no rest. This value overrides pool/org.',
      restMinutesPlaceholder: 'Inherit',
      restMinutesUnit: 'minutes',
      restMinutesSave: 'Save rest time',
      restMinutesSaved: 'Rest time saved',
      restMinutesInvalid: 'Enter a positive number of minutes (or leave empty)',
      sectionPoolLocker: 'Pool and key cabinet',
      poolTitle: 'Pool / pickup location',
      lockerTitle: 'Key cabinet (Flinkey)',
      keyTagTitle: 'Key tag',
      keyTagNone: 'No key tag linked to this vehicle.',
      keyTagInHome: 'In cabinet',
      keyTagTaken: 'Taken out',
      keyTagInOther: 'In another cabinet',
      keyTagUnknown: 'Status unknown',
      keyTagHomeCabinet: 'Home cabinet: {{name}}',
      keyTagSlot: 'Slot {{slot}}',
      keyTagOtherAllowed: 'The tag may sit in another cabinet.',
      goToPickupLocations: 'Pickup locations',
      goToVehiclePools: 'Vehicle pools',
      available: 'Available for booking',
      unavailable: 'Not available for booking',
      noLockerLinked: 'No Flinkey box linked to this vehicle.',
      sectionBlock: 'Block vehicle for booking',
      blockHint:
        'Set start and end below. The vehicle appears as a grey block in the booking calendar. Overlapping bookings are kept but without an assigned vehicle and appear under Collision bookings (previous pool and vehicle are saved).',
      blockStart: 'Start',
      blockEnd: 'End',
      blockCreate: 'Block',
      blockRemove: 'Remove',
      blockRemoveConfirm: 'Remove this block?',
      blockSaved: 'Block created',
      blockRemoved: 'Block removed',
      blockListTitle: 'Upcoming blocks',
      blockNone: 'No upcoming blocks.',
      blockCollisionLink: 'Collision bookings →',
      openCalendar: 'Booking calendar',
      regHint: 'Current registration: {{reg}}',
    },
    general: {
      sectionVehicleAttributes: 'Vehicle details',
      alternativeFuel: 'Alternative fuel',
      noAlternativeFuel: 'None selected',
      usageStatus: 'Status',
      noUsageStatus: 'No status',
      businessResponsibility: 'Business responsibility',
      noBusinessResponsibility: 'None selected',
      businessResponsibilitySearch: 'Search responsibility ID or text…',
      businessResponsibilityIkpart: 'Business area',
      businessResponsibilityShowHistory: 'Show history',
      businessResponsibilityHideHistory: 'Hide history',
      businessResponsibilityNoHistory: 'No history yet.',
      businessResponsibilityFrom: 'Previous',
      businessResponsibilityCleared: 'Cleared',
      tempFordonsansvarigSection: 'Vehicle manager',
      tempFordonsansvarigRsid: 'RSID',
      tempFordonsansvarigName: 'Name',
      tempFordonsansvarigEmail: 'Email',
      tempFordonsansvarigPhone: 'Phone',
      sectionInspection: 'Inspection',
      sectionTax: 'Tax',
      sectionReferences: 'Reference numbers',
      lastInspection: 'Last inspection',
      inspectionResult: 'Result',
      odometerAtInspection: 'Odometer at inspection',
      nextInspectionDue: 'Next inspection due by',
      inspectionStation: 'Inspection station',
      registeredForUse: 'Registered for use / in traffic',
      emergencyVehicle: 'Emergency vehicle',
      hasDrivingBan: 'Driving ban',
      ternaryYes: 'Yes',
      ternaryNo: 'No',
      ternaryNA: 'N/A',
      annualVehicleTax: 'Annual vehicle tax',
      malus: 'Malus',
      taxationMonths: 'Payment month/s',
      nextTaxDue: 'Next tax due by',
      taxDueHint: 'Tax must be paid by the last day of the payment month. Reminder 45 days before.',
      taxPeriod: 'Tax period',
      noCarInfo: 'No Car.info data for this vehicle.',
      noTaxAttrs: 'No tax fields in Car.info for this vehicle.',
      noEconomyRefs: 'No economy reference fields are filled in yet.',
      ownership: 'Owned or leased',
      leasingAgreementNo: 'Agreement number (leasing)',
      contractNo: 'Contract number',
      costCenter: 'Cost center',
    },
    color: 'Color:',
    changeDevice: 'change',
    selectDevice: 'select',
    removeDeviceLink: 'Remove device link',
    searchImei: 'Search IMEI...',
    odometerFromOem: 'Odometer is fetched from the vehicle OEM cluster',
    distanceDriven: 'Distance driven:',
    currentOdometer: 'Current odometer:',
    saveOdometer: 'Save odometer',
    tripsRecalculated: 'Trips are recalculated automatically after saving',
    noSpecifications: 'No specifications available',
    propertiesSearchPlaceholder: 'Search all properties (name or value)…',
    propertiesSearchClear: 'Clear search',
    propertiesSearchNoResults: 'No properties match your search.',
    propertiesSearchMatchCount: '{{count}} matches',
    propertiesSearchMatchesInCategory: '{{count}} matches',
    propertiesAttributeCount: '{{count}} attributes',
    propertiesBasicCategory: 'Basic information (Car.info)',
    propertiesExtendedCategory: 'Other information (Car.info)',
    noHistory: 'No history available',
    ownerChange: 'Owner change',
    writeNotesHere: 'Write notes here...',
    unsavedChanges: 'Unsaved changes',
    updateFromCarInfo: 'Update from Car.info',
    tryAgain: 'Try again',
    vehicleReportedStolen: 'THE VEHICLE IS REPORTED STOLEN!',
    userId: 'User (ID)',
    userIdPlaceholder: 'User ID',
    validFrom: 'Valid from (optional)',
    costCenter: 'Cost center',
    organizationGroup: 'Organization (group)',
    vehicleCategory: 'Vehicle category',
    noVehicleCategory: 'No category',
    replacesVehicleReg: 'Replaces vehicle (reg. no.)',
    replacesVehicleRegPlaceholder: 'Registration number this vehicle replaces',
    viewDequeVehicleLog: 'View in Deque log',
    orgMoveTitle: 'Move vehicle',
    orgMoveSameTopBody:
      'The vehicle is moved to the selected organisation within the same customer. History (trips, GPS) can follow the vehicle or stay on the old record. The mounted device stays with the vehicle.',
    crossTopTitle: 'Move to another customer (top node)',
    crossTopBody:
      'The vehicle is moved to the selected organisation under a different top node. Flinkey and active fuel cards are unlinked. Choose whether history should follow, and whether the device should be moved to the new top node.',
    crossTopMoveDeviceLabel: 'Should the device be moved to the new top node?',
    crossTopMoveDeviceYes: 'Yes, move the device',
    crossTopMoveDeviceYesHelp: 'The device is added to the new organisation and stays mounted on the vehicle.',
    crossTopMoveDeviceNo: 'No, leave the device behind',
    crossTopMoveDeviceNoHelp: 'The device is unmounted and stays with the old organisation.',
    crossTopMoveDeviceRequired: 'Choose whether the device should follow.',
    crossTopKeepDataLabel: 'Keep data with the vehicle',
    crossTopKeepDataHelp:
      'The same vehicle record and history (trips, GPS) stay with the vehicle, including moves within the same top node.',
    crossTopStartFreshLabel: 'Start fresh',
    crossTopStartFreshHelp:
      'A new vehicle record is created. History stays on the old record.',
    crossTopNoteLabel: 'Note (optional)',
    crossTopConfirm: 'Confirm transfer',
  },

  dequeVehicleLog: {
    title: 'Deque — Vehicle log',
    description: 'In-customer org moves, replacements, top-node transfers, and vehicle status (active/inactive).',
    scopeNote:
      'Activation/deactivation appears under «Status» or «All» (from audit). Superadmins see more in the change log. Device history (device_change_log) requires a mounted GPS device.',
    scopeNoteUpdated:
      'Activation/deactivation (status changes) appear under the «Status» tab or in «All». Superadmins see the same events with more context in the change log.',
    auditLogLink: 'Open change log',
    tabAll: 'All',
    tabOrgMove: 'Org move',
    tabReplacement: 'Replacement',
    tabCrossTop: 'Top-node transfer',
    tabVehicleStatus: 'Status',
    vehicleActivated: 'Activated',
    vehicleInactivated: 'Deactivated',
    vehicleId: 'Vehicle ID',
    registrationNumber: 'Registration number',
    registrationNumberPlaceholder: 'ABC123',
    dateFrom: 'From date',
    dateTo: 'To date',
    colWhen: 'Time',
    colType: 'Type',
    colVehicle: 'Vehicle',
    colDetail: 'Detail',
    colUser: 'Performed by',
    empty: 'No events for the selected filters.',
    loadError: 'Could not load log',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
    replaces: 'Replaces',
    replacedReg: 'Replaced reg.',
  },

  replacementChain: {
    title: 'Replacement chain',
    description: 'Search by registration number to see which vehicles were replaced in a chain.',
    searchPlaceholder: 'Registration number…',
    emptyState: 'Search for a registration number to show the replacement chain.',
    loadError: 'Could not load replacement chain',
    resultHint: 'Older replaced vehicles on the left, later replacements on the right.',
    noChain: 'No replacement chain linked to this vehicle.',
    legendLeft: 'Left: vehicles this one replaces (the «Replaces vehicle» field).',
    legendRight: 'Right: vehicles that replaced the searched vehicle.',
    legendCurrent: 'Selected vehicle',
    openVehicle: 'Open vehicle',
    notInSystem: 'Not registered as a vehicle in the system',
    noAccess: 'You do not have permission to view replacement chains.',
  },

  vehicleCategories: {
    title: 'Vehicle categories',
    description: 'Manage selectable vehicle categories for your top-node organization.',
    newCategoryPlaceholder: 'New vehicle category',
    categoryName: 'Category name',
    empty: 'No vehicle categories have been added yet.',
    saved: 'Vehicle category saved',
    loadError: 'Could not load vehicle categories',
    saveError: 'Could not save vehicle category',
  },

  alternativeFuels: {
    title: 'Alternative fuels',
    description: 'Manage selectable alternative fuels for your top-node organization.',
    newFuelPlaceholder: 'New fuel, e.g. HVO',
    fuelName: 'Fuel',
    empty: 'No alternative fuels have been added yet.',
    saved: 'Alternative fuel saved',
    loadError: 'Could not load alternative fuels',
    saveError: 'Could not save alternative fuel',
  },

  nordeaLeasing: {
    title: 'Nordea leasing import',
    description: 'Import monthly TXT file from Nordea. Updates vehicle economy, archives file and payment history per period.',
    uploadTitle: 'Upload file',
    uploadHint:
      'Select semicolon-separated .txt file (e.g. 2026-04.TXT). The same file is only imported once. Next rent is divided by the Per column (invoice period in months) to get monthly cost.',
    selectFile: 'Choose file',
    importHistory: 'Previous imports',
    noImports: 'No imports yet',
    colFilename: 'Filename',
    colPeriodEnd: 'Period end',
    colRows: 'Rows',
    colUpdated: 'Updated',
    colSkipped: 'Skipped',
    colImportedAt: 'Imported at',
    loadError: 'Could not load import list',
    importError: 'Import failed',
    importSuccess: 'Import complete: {{updated}} vehicles updated, {{skipped}} skipped',
    duplicateFile: 'File already imported – no new storage or history',
    skippedRegistrations: 'Reg. numbers without matching vehicle',
  },

  protectorInsurance: {
    title: 'Protector insurance import',
    description:
      'Import insurance files (Excel) from Protector. Matches vehicles on reg. no (column F), updates vehicle insurance and builds history per period.',
    uploadTitle: 'Upload files',
    uploadHint:
      'Select one or more .xls/.xlsx files. Reg. no (column F) is the key. Monthly cost = Amount (column V) / 12. The same file is only imported once.',
    selectFiles: 'Choose files',
    importHistory: 'Previous imports',
    noImports: 'No imports yet',
    colFilename: 'Filename',
    colPeriodEnd: 'Period end',
    colRows: 'Rows',
    colUpdated: 'Updated',
    colSkipped: 'Skipped',
    colImportedAt: 'Imported at',
    loadError: 'Could not load import list',
    importError: 'Import failed',
    importSuccess: 'Import complete: {{updated}} vehicles updated, {{skipped}} skipped',
    duplicateFile: 'File already imported – no new storage or history',
    skippedRegistrations: 'Reg. numbers without matching vehicle',
  },

  vehicleUsageStatuses: {
    title: 'Vehicle status',
    description: 'Manage selectable statuses per top node (leasing, decommissioning, etc.).',
    newStatusPlaceholder: 'New status, e.g. Under decommissioning',
    statusName: 'Status',
    empty: 'No statuses have been added yet.',
    saved: 'Vehicle status saved',
    loadError: 'Could not load vehicle statuses',
    saveError: 'Could not save vehicle status',
  },

  // Impersonation (Switch User)
  impersonation: {
    title: 'Switch to User',
    switchUser: 'Switch User',
    searchPlaceholder: 'Search users...',
    partnerSearchHint: 'Shows users in your customers\' organizations',
    noUsers: 'No users found',
    viewingAs: 'Viewing as',
    returnToAdmin: 'Return to your account',
    returnButton: 'Return',
    fetchError: 'Failed to fetch users',
    startError: 'Failed to switch user',
    stopError: 'Failed to return to admin',
    showingFirst: 'Showing first {{count}}',
  },

  // Track Playback
  track: {
    title: 'Track Playback',
    subtitle: 'Historical GPS tracking for multiple vehicles',
    addVehicle: 'Add vehicle',
    selectVehicle: 'Select vehicle...',
    selectedVehicles: 'Selected vehicles',
    timePeriod: 'Time period',
    interval: 'Interval',
    search: 'Search',
    noData: 'Select vehicles and time period to display tracks',
    play: 'Play',
    pause: 'Pause',
    stop: 'Stop',
    reset: 'Reset',
    speed: 'Speed',
    currentTime: 'Current time',
    pointsLoaded: 'GPS points loaded',
    maxVehicles: 'Maximum 10 vehicles can be tracked simultaneously',
    vehicleAlreadyAdded: 'Vehicle is already added',
    jammingLegend: 'Jamming (AVL 249) – {count} GPS points',
  },

  // AI Analysis
  ai: {
    title: 'AI Assistant',
    pageTitle: 'AI Analysis',
    pageDescription: 'Use AI to analyze the fleet and get recommendations. All data is processed locally.',
    welcomeMessage: 'Hi! I can help you analyze the fleet, explain metrics and provide recommendations.',
    inputPlaceholder: 'Ask a question about the fleet...',
    clearChat: 'Clear chat',
    selectOrganization: 'Select an organization to start the analysis',
    allOrganizations: 'All organizations',
    organization: 'Organization',
    analyzing: 'Analyzing...',
    runAnalysis: 'Run AI Analysis',
    aiAnalysis: 'AI Analysis',
    analysisResult: 'Analysis Result',
    analysisError: 'Analysis failed',
    fleetOverview: 'Fleet Overview',
    topVehicles: 'Vehicles',
    vehicleAnalysis: 'Vehicle Analysis',
    noVehicles: 'No vehicles with data found',
    status: {
      online: 'Online',
      offline: 'Offline',
      checking: 'Checking...',
    },
    period: {
      label: 'Period',
      week: 'Week',
      month: 'Month',
      quarter: 'Quarter',
    },
    stats: {
      vehicles: 'Total vehicles',
      activeVehicles: 'Active vehicles',
      distance: 'Total distance',
      totalKm: 'Total distance (km)',
      fuelConsumption: 'Avg consumption',
      fuelCost: 'Fuel cost',
      ecoScore: 'Eco score',
    },
    log: {
      title: 'AI Log',
      description: 'View all AI requests made in the system',
      question: 'Question',
      response: 'Response',
      user: 'User',
      organization: 'Organization',
      requestType: 'Type',
      responseTime: 'Response time',
      success: 'Status',
      createdAt: 'Timestamp',
      noLogs: 'No AI requests found',
      viewDetails: 'View details',
      types: {
        chat: 'Chat',
        analyze_vehicle: 'Vehicle analysis',
        analyze_driver: 'Driver analysis',
        analyze_fleet: 'Fleet analysis',
        compare_vehicles: 'Vehicle comparison',
        explain_metric: 'Metric explanation',
      },
    },
  },

  // Route Analysis (verification: actual vs route-optimized)
  routeAnalysis: {
    title: 'Route Analysis',
    pageTitle: 'Route analysis – verification',
    description: 'Select a vehicle and date. See driving time and distance – compare with road-optimized route (same stops, same order).',
    
    // Select trips
    selectTrips: 'Select trips to analyze',
    selectVehicle: 'Select vehicle',
    selectDate: 'Select date',
    tripsOnDate: 'Trips on this day',
    noTripsFound: 'No trips found for selected date',
    selectedTrips: 'Selected trips',
    selectAll: 'Select all',
    selectMorning: 'Select morning',
    selectAfternoon: 'Select afternoon',
    
    // Analysis
    analyze: 'Analyze',
    analyzing: 'Analyzing...',
    analyzeSelected: 'Analyze selected trips',
    quickCompare: 'Quick compare',
    
    // Results – verification
    result: 'Comparison',
    actualRoute: 'Actual',
    actualRouteSubtitle: 'as the vehicle drove',
    optimalRoute: 'Route-optimized',
    optimalRouteSubtitle: 'same stops, same order',
    comparison: 'Difference',
    comparisonIntro: 'Difference between actual and route-optimized driving.',
    emptyState: 'Select vehicle and date, select at least 2 trips and click Analyze.',
    routeMapPerVehicle: 'Route comparison per vehicle',
    routeMapClickHint: 'Click a vehicle to see maps with driven and optimal route',
    
    // Metrics
    totalDistance: 'Total distance',
    drivingTime: 'Driving time',
    stopTime: 'Stop time',
    sameStopTime: 'same',
    serviceTime: 'Service time',
    stopCount: 'Number of stops',
    routeOrder: 'Visit order',
    
    // Freed potential (NOT "saved")
    freedPotential: 'Freed potential',
    freedTime: 'Freed time',
    freedTimeDescription: 'Time freed up for other tasks',
    reducedDistance: 'Reduced distance',
    reducedDrivingTime: 'Reduced driving time',
    distanceSimilar: 'Distance similar',
    
    // What freed time can be used for
    freedTimeUsage: 'Freed time can be used for',
    potentialExtraVisits: 'Extra customer visits',
    potentialExtraTimePerStop: 'Extra time per customer',
    betterService: 'Better service',
    bufferTime: 'Buffer for unexpected',
    
    // Economic effect
    economicEffect: 'Economic effect',
    fuelSavings: 'Lower fuel cost',
    co2Reduction: 'CO₂ reduction',
    
    // Annual potential
    annualPotential: 'Annual potential',
    annualFreedTime: 'Freed time per year',
    annualReducedDistance: 'Reduced distance per year',
    workDaysPerYear: 'work days',
    
    // Map
    showActualRoute: 'Show actual route',
    showOptimalRoute: 'Show optimal route',
    compareRoutes: 'Compare routes',
    
    // Export
    exportReport: 'Export report',
    shareAnalysis: 'Share analysis',
    
    // Status
    vroomAvailable: 'Route optimization available',
    vroomUnavailable: 'Route optimization not available',
    minTripsRequired: 'At least 2 trips required for comparison',
    noOptimalRoute: 'Showing actual driving data only (optimal route not available)',
    
    // Units
    minutes: 'min',
    hours: 'hrs',
    kilometers: 'km',
    liters: 'liters',
    sek: 'SEK',
    kg: 'kg',
    percent: '%',

    // Methodology
    methodologyDisclaimer: 'Important: "Driven route" is total trip time (from trip start to trip end) and may include time at each stop (e.g. visit time). "Optimal route" is estimated driving time only between stops in optimal order (A-to-B routing). The difference is therefore a theoretical upper bound – in home care, visit time cannot be "saved" by better routing. Realistic potential depends on time windows, visit durations, and how trips are defined.',
    methodologyNote: 'Per leg (start→stop): Actual = distance and time the vehicle took. Route-optimized = best road distance and estimated driving time from road network. Same points, comparable.',
    calculatingRoute: 'Optimal route is being calculated...',
    vroomRequired: 'VROOM optimization is required for this view',
    checkingVroom: 'Checking...',
    stopsCount: '{{count}} stops',
    stopsAnalyzed: '{{count}} stops analysed',
    showingTrip: 'Showing trip {{num}}',
    showAllTrips: 'Show all trips',
    showOnlyThisTrip: 'Show only this trip',
    showAll: 'Show all',
    vehicles: 'vehicles',
    trips: 'trips total',
    selected: 'selected',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    stopStart: 'Stop {{num}} (start)',
    stopEnd: 'Stop {{num}} (end)',
    start: 'Start',
    goal: 'Goal',
    actualRouteLabel: 'Driven route (GPS from MongoDB)',
    gpsPoints: '{{count}} GPS points',
    optimalRouteLabel: 'Optimal route (VROOM)',
    points: '{{count}} points',
  },

  mapServices: {
    title: 'Map Services',
    liveTracking: 'Live Tracking',
    liveTrackingDescription: 'Follow vehicles in real time on the map',
    track: 'Track',
    trackDescription: 'Historical GPS tracking with multi-vehicle playback',
    geofence: 'Geofence',
    geofenceDescription: 'Manage geographic zones and area monitoring',
    heatmap: 'Heatmap',
    heatmapDescription: 'Visualize activity patterns as a heat map',
    opensInNewWindow: 'Opens in new window',
  },

  analysis: {
    stopTime: {
      title: 'Stop Time Analysis – EV Charging Planning',
      description: 'Analysis of vehicle stop times for optimal placement of charging infrastructure',
      startDate: 'Start date',
      endDate: 'End date',
      organization: 'Organization',
      selectOrganization: 'Select organization',
      update: 'Update',
      loading: 'Loading...',
      totalLocations: 'Total locations',
      totalHours: 'Total stop time',
      totalStops: 'Total stops',
      vehiclesAnalyzed: 'Vehicles analysed',
      tabHeatmap: 'Heatmap',
      tabMap: 'Map',
      emptyState: 'Select date range and organization to display analysis',
    },
    trends: {
      title: 'Trends',
      description: 'Trend analysis over time and mileage versus lease contracts',
      distance: 'Distance',
      fuelCost: 'Fuel cost',
      vsPrevious: 'Vs previous period',
      forecastLiters: 'Forecast next period',
      forecastHint: 'Linear trend from history',
      costTrend: 'Distance and cost',
      seasonal: 'Seasonal variation (12 months)',
      behaviorTitle: 'Vehicles with changed behaviour',
      previousKm: 'Previous km',
      recentKm: 'Recent km',
      change: 'Change',
      reason: 'Reason',
      noBehavior: 'No large deviations vs previous period',
      tabTrends: 'Trends',
      tabLeasing: 'Lease mileage',
      leasing: {
        vehicle: 'Vehicle',
        selectVehicle: 'Select registration number (optional)',
        clearVehicle: 'Show all vehicles',
        vehicles: 'Vehicles',
        tableTitle: 'Mileage versus lease contract',
        allowed: 'Contracted km',
        measured: 'Measured km',
        estimatedUsed: 'Estimated usage',
        forecastEnd: 'Forecast at end',
        delta: 'Variance',
        extraCost: 'Extra cost',
        status: 'Status',
        statusOver: 'Risk of exceeding',
        statusUnder: 'Risk of underusing',
        statusOnTrack: 'On track',
        statusMissing: 'Missing data',
        budgetLine: 'Contract budget',
        measuredLine: 'Measured',
        forecastLine: 'Forecast (rate)',
        rateHint: 'The forecast uses measured km/day for the period the device has reported data — a start odometer is not required.',
        lateInstallHint: 'The device appears to have been installed after contract start. First driving data:',
        shortHistoryHint: 'Short measurement period — the forecast is uncertain.',
      },
    },
    benchmarks: {
      title: 'Comparisons',
      description: 'Compare performance between departments and organizations',
      bestPractice: 'Best practice',
      improve: 'Improvement areas',
      ranking: 'Ranking by organization',
      eco: 'ECO',
      lPer100: 'L/100 km',
      utilization: 'Utilization',
      drillHint: 'Click an organization to zoom in on its vehicles.',
      vehicles: 'Vehicles in selection',
    },
    crashDetection: {
      title: 'Crash detection',
      description: 'List and visualisation of crash events (Crash Trace from devices).',
      regNumber: 'Registration number',
      imei: 'IMEI',
      date: 'Date',
      samples: 'Samples',
      triggerCount: 'Triggers',
      timeSpan: 'Time span',
      triggerPositions: '{{count}} position(s)',
      fromDate: 'From date',
      toDate: 'To date',
      imeiPlaceholder: 'Filter by IMEI',
      noEvents: 'No crash events found.',
      detailTitle: 'Crash event data',
      speedAtCrash: 'Speed at crash',
      speedAtCrashHint: 'Speed is from the trigger at the time of the event.',
      xyzChart: 'Accelerometer X/Y/Z (mG)',
      xyzHint: 'Braking shows as negative spike (e.g. Z). Lateral movement shows as Y or X.',
      tripMap: 'Trip at crash time',
      mapTitle: 'Map',
      speedChart: 'Speed curve',
      speedChartHint: 'Speed (km/h). With trip data: ±2 min around event. Otherwise: each trigger.',
      brakeChart: 'Braking curve',
      brakeChartHint: 'Positive value = braking (m/s²). Derived from speed change.',
      noRouteForCharts: 'No trip data – speed and braking curves require trip with GPS points.',
      speedAtEventOnly: 'Speed at event: {{speed}} km/h. No trip data for curve.',
    },
  },

  // ============================================================
  // Service - Vehicle Service
  // ============================================================
  service: {
    title: 'Service',
    overview: 'Service Overview',
    servicePoints: 'Service Points',
    addPoint: 'Add Service Point',
    saveFailed: 'Could not save service point. Please try again.',
    noCreatePermission: 'You do not have permission to add service points.',
    syncCarInfo: 'Sync from Car.info',
    noServicePoints: 'No service points. Click "Sync from Car.info" to import.',
    noServiceDue: 'No service points to show',
    horizonHint: 'Only showing items due within 300 km or 60 days.',
    horizonListEmpty: 'No items due within 300 km or 60 days.',
    allPointsHint: 'Shows all service points on the vehicle and when each is next due.',
    totalActions: 'Total Actions',
    vehicles: 'vehicles',
    actions: 'actions',
    searchPlaceholder: 'Search vehicles, services...',
    allStatuses: 'All statuses',
    interval: 'Interval',
    nextDue: 'Next Due',
    lastPerformed: 'Last Performed',
    reminder: 'Reminder',
    booking: 'Booking',
    bookingInfo: 'Booking Information',
    bookingDate: 'Booking Date',
    book: 'Book',
    bookedFor: 'Booked',
    confirmBooking: 'Confirm Booking',
    markComplete: 'Mark Complete',
    confirmComplete: 'Mark as Completed',
    performedDate: 'Performed Date',
    performedKm: 'Odometer (km)',
    cost: 'Cost (SEK)',
    notes: 'Notes',
    history: 'History',
    editReminder: 'Reminder',
    updateReminder: 'Update Reminder',
    warningKm: 'Warn at (km before)',
    warningDays: 'Warn at (days before)',
    workshopLabel: 'Workshop',
    noWorkshop: '-- Select workshop --',
    days: 'days',
    intervalKm: 'Interval (km)',
    intervalMonths: 'Interval (months)',
    intervalHint: 'Enter at least one interval – km, time (months) or both.',
    existingType: 'Existing Type',
    customType: 'Custom Type',
    selectType: 'Select service type',
    chooseType: '-- Choose --',
    typeName: 'Service type name',
    categoryLabel: 'Category',
    categoryCustom: 'Custom',
    categoryMaintenance: 'Maintenance',
    lastPerformedDate: 'Last performed (date)',
    lastPerformedKm: 'Last performed (km)',
    status: {
      ok: 'OK',
      warning: 'Due Soon',
      overdue: 'Overdue',
      booked: 'Booked',
      completed: 'Completed',
    },
    attachments: {
      title: 'Attachments',
      noAttachments: 'No attachments uploaded',
      uploadHint: 'Click "Upload" to add files',
      upload: 'Upload',
      uploading: 'Uploading...',
      descriptionOptional: 'Description (optional)',
      descriptionPlaceholder: 'E.g. Inspection report 2026',
      all: 'All',
      categoryInsurance: 'Insurance',
      categoryInspection: 'Inspection',
      categoryDamage: 'Damage',
      categoryInvoice: 'Invoice',
      categoryContract: 'Contract',
      categoryService: 'Service',
      categoryOther: 'Other',
    },
    tires: {
      title: 'Tires',
      add: 'Add',
      addNew: 'New Tire Set',
      type: 'Tire Type',
      brand: 'Brand',
      model: 'Model',
      size: 'Size',
      treadDepth: 'Tread Depth',
      treadDepthVf: 'FL (Front Left)',
      treadDepthHf: 'FR (Front Right)',
      treadDepthVb: 'RL (Rear Left)',
      treadDepthHb: 'RR (Rear Right)',
      treadDepthMm: 'mm',
      treadDepthSection: 'Tread depth per wheel position',
      storageType: 'Storage Type',
      storageLocation: 'Location',
      storage: 'Storage',
      summer: 'Summer Tires',
      winterFriction: 'Winter Tires (friction)',
      winterStudded: 'Studded Tires',
      hotel: 'Tire Hotel',
      installed: 'Installed',
      stored: 'Stored',
      other: 'Other',
      installedSince: 'Installed since',
      lastChanged: 'Last changed',
      mount: 'Install',
      unmount: 'Remove',
      save: 'Save',
      none: 'No tires registered',
      noTireInfo: 'No tire information',
      manage: 'Manage tires',
      edit: 'Edit tire',
      currentTires: 'Installed tires',
      tireChangeSection: 'Tire information (recorded at change)',
      minTread: 'Minimum tread depth',
      changeHistory: 'Change history',
      noHistory: 'No tire changes logged yet',
      removed: 'Removed',
      installedNew: 'Installed',
      kmDriven: 'Km driven on removed tires',
      odometerAtChange: 'Odometer at change',
      treadWorn: 'Wear per wheel (mm)',
      treadWornAvg: 'Average wear',
      treadDepthAutoMin: 'The lowest value is automatically saved as the worst tread depth',
      treadDepthLabel: 'Tread depth',
      storageHotel: 'Tire hotel',
      storageStorage: 'Storage',
      storageVehicle: 'In the vehicle',
      storageOther: 'Other',
    },
    viewList: 'List',
    viewCalendar: 'Calendar',
    viewCosts: 'Costs',
    category: {
      all: 'All categories',
      tire: 'Tire change',
      maintenance: 'Service',
      inspection: 'Inspection',
      custom: 'Other',
    },
    filter: {
      allStatuses: 'All statuses',
    },
    bulk: {
      selected: 'selected',
      action: 'Act on selected',
      title: 'Service action',
      points: 'points',
      book: 'Book',
      complete: 'Mark complete',
      bookingDate: 'Booking date',
      performedDate: 'Performed date',
      totalCost: 'Total cost',
      costNote: 'applies to entire group',
      confirmBook: 'Confirm booking',
      confirmComplete: 'Mark as completed',
    },
    calendar: {
      title: 'Service calendar',
    },
    workshop: {
      create: 'Create workshop',
      edit: 'Edit workshop',
      name: 'Name',
      type: 'Type',
      extern: 'External',
      intern: 'Internal',
      internalPerson: 'Internal person (service staff)',
      selectPersonnel: '-- Select service staff --',
      noPersonnelHint: 'No service staff found. Add the role via Contact roles.',
      contactPerson: 'Contact person',
    },
    internal: 'Internal',
    invoice: {
      upload: 'Upload invoice (PDF/image) for AI parsing',
      uploadShort: 'Upload',
      parsed: 'AI parsed the invoice',
      supplier: 'Supplier',
      number: 'Invoice no.',
      total: 'Total',
      vat: 'VAT',
    },
    costs: {
      month: 'Month',
      category: 'Category',
      categoryName: 'Category name',
      totalSek: 'Cost (SEK)',
      serviceCount: 'Count',
      grandTotal: 'Total cost',
      monthlyChart: 'Cost per month',
      exportExcel: 'Export Excel',
      noData: 'No cost data for selected period',
      reportPageTitle: 'Service costs',
      reportSubtitle: 'Costs by category and period',
    },
    autoTire: {
      title: 'Automatic tire service',
      description: 'Automatically create service points for winter and summer tire changes based on Swedish regulations.',
      enable: 'Enable automatic tire service',
      enableHint: 'Creates service points for all vehicles within the specified number of days before the deadline.',
      winterDeadline: 'Winter tires – must be fitted by',
      summerDeadline: 'Studded/Winter tires – must be removed by',
      winterHint: 'Last date winter tires must be fitted (per Transportstyrelsen: December 1st)',
      summerHint: 'Last date studded and winter tires must be removed (per Transportstyrelsen: April 15th)',
      advanceDays: 'Days in advance',
      advanceDaysUnit: 'days',
      advanceDaysHint: 'Number of days before deadline that the service order is created. Default: 60 days (2 months).',
    },
    responsible: 'Service responsible',
    selectResponsible: 'Select service responsible...',
  },

  // Vehicle economy – leasing, insurance, disposal, export
  vehicleEconomy: {
    leasing: 'Leasing',
    ownershipType: 'Owned or leased',
    ownershipHint: 'Filled from Car.info (lease started/ended). You can change the status here.',
    insurance: 'Insurance',
    disposal: 'Disposal',
    disposalType: 'Type',
    disposalTypeSale: 'Sale',
    disposalTypeReturn: 'Return',
    disposalNotes: 'Notes',
    saveSuccess: 'Economy saved',
    agreementNo: 'Agreement no',
    contractNo: 'Contract no',
    leasingCompany: 'Leasing company',
    insuranceCompany: 'Insurance company',
    vehicleSupplier: 'Vehicle supplier',
    rentStart: 'Rent start',
    rentEnd: 'Rent end',
    termMonths: 'Contract term (months)',
    leasingProduct: 'Product',
    originalBasePrice: 'Original base price',
    bookValue: 'Book value',
    guaranteedResidualValue: 'Guaranteed residual value',
    importHistory: 'History (Nordea import)',
    readOnlyDeque: 'View only – you can see imported leasing data but do not have permission to edit economy.',
    historyPeriod: 'Period (Datum to)',
    historyInvoice: 'Invoice no.',
    historyPer: 'Per',
    costMonthLeasing: 'Monthly cost leasing',
    allowedDistance: 'Allowed km',
    costPerExtraKm: 'Cost per extra km',
    costCenter: 'Cost center',
    costCenterLeasing: 'Cost center (leasing)',
    costCenterInsurance: 'Cost center (insurance)',
    costCenterTax: 'Cost center (tax)',
    costCenterTaxMalus: 'Cost center (malus)',
    insuranceStartDate: 'Start date',
    insuranceEndDate: 'End date',
    insuranceAgreementNo: 'Agreement',
    costMonthInsurance: 'Monthly cost insurance',
    costMonthTax: 'Monthly cost tax',
    costMonthTaxMalus: 'Monthly cost tax malus',
    insuranceImportHistory: 'History (Protector import)',
    insHistoryPeriod: 'Period (end date)',
    insHistoryAgreement: 'Agreement',
    insHistoryTrafficTotal: 'Traffic total',
    insHistoryDelkasko: 'Partial casco',
    insHistoryVagnskada: 'Collision',
    insHistoryPremiumYear: 'Amount',
    insHistoryTotal: 'Total',
    insHistoryCostMonth: 'Monthly',
    disposalDate: 'Disposal date',
    disposalSaleAmount: 'Sale amount',
    disposalExtraCosts: 'Extra costs at disposal',
    exportTitle: 'Export vehicle economy',
    exportDescription: 'Select organisation and period. The export uses imported cost lines and builds a Unit4 file (account, responsibility, activity, counterpart).',
    period: 'Period (year-month)',
    exportBokforing: 'Basis for accounting (Unit4)',
    exportBokforingDesc: 'CSV in Unit4 code plan, aggregated by account and responsibility – for accounting import.',
    exportFakturering: 'Basis for invoicing (Unit4)',
    exportFaktureringDesc: 'CSV with internal invoice (debit/credit) per vehicle and cost type – for Unit4 import.',
    exportSelectOrg: 'Select an organisation to create the file.',
    exportError: 'Could not download export.',
    costInvoiceLines: 'Imported invoice lines',
    costInvoiceSummary: 'Imported costs',
    costInvoiceSummaryMeta: '{{count}} lines · period {{period}}',
    costInvoiceSummaryCount: 'Lines',
    costInvoiceSummaryTotal: 'Total',
    costInvoiceDate: 'Invoice date',
    costInvoiceNumber: 'Invoice no.',
    costInvoiceCategory: 'Cost type',
    costInvoiceDescription: 'Description',
    costInvoiceAmount: 'Amount excl. VAT',
  },

  costInvoice: {
    title: 'Cost invoice import',
    cardDescription: 'Import Ayvens invoices and map cost types',
    description:
      'Import mixed vehicle costs from leasing companies (e.g. Ayvens). Map cost codes and upload one or more files, or a whole folder. Each file is imported only once.',
    uploadTitle: 'Upload files',
    uploadHint:
      'Select Excel files (.xlsx) or a folder. The same file content is imported only once per organization. Fuel card lines are off by default.',
    selectFiles: 'Select files',
    selectFolder: 'Select folder',
    mappingTitle: 'Cost type mapping (Ayvens)',
    mappingHint:
      'The supplier code is mapped to a DQFleet cost type. Uncheck Include if the lines should not be stored (e.g. fuel already imported from Circle K).',
    saveMapping: 'Save mapping',
    mappingSaved: 'Mapping saved',
    mappingSaveError: 'Could not save mapping',
    importHistory: 'Previous imports',
    noImports: 'No imports yet',
    colFilename: 'Filename',
    colInvoiceDate: 'Invoice date',
    colRows: 'Rows',
    colImported: 'Imported',
    colSkipped: 'Skipped',
    colUnmapped: 'Unmapped',
    colUnmatched: 'Unknown plates',
    colImportedAt: 'Imported at',
    colSourceCode: 'Code',
    colSourceLabel: 'Supplier type',
    colCategory: 'DQFleet type',
    colInclude: 'Include',
    loadError: 'Could not load import or mapping',
    importError: 'Import failed',
    importTimeout: 'The import timed out. Try one file at a time.',
    fileTooLarge: 'The file is too large for the server (nginx). Ask an admin to raise client_max_body_size.',
    importSuccess: 'Import finished for {{count}} file(s)',
    duplicateFiles: '{{count}} file(s) were already imported',
    noSpreadsheetFiles: 'The folder contained no .xls/.xlsx files',
    unmappedCodesHint: 'Unmapped codes: {{codes}} — add them to the mapping and import again',
    autoMappedHint: 'New codes were mapped automatically: {{codes}}. Review the table if anything should change.',
    createdVehiclesHint: '{{count}} missing vehicles were created on the top node and fetched from car.info',
    carInfoFailedHint: '{{count}} vehicles were created but car.info could not be filled in',
  },

  ayvensFuel: {
    title: 'Ayvens fuel cards (manual import)',
    description:
      'Upload Ayvens "Fuel card, details". Transactions are stored as Ayvens. If the same fill already exists at Circle K, OKQ8 or another company the row is marked as a duplicate — that can mean double invoicing.',
    selectFiles: 'Select fuel file',
    importSuccess: '{{count}} fuel rows imported',
    duplicateHint: '{{count}} rows were marked as duplicates of another company',
    nothingImported: 'No new rows (the file may already be imported)',
    importError: 'Ayvens fuel import failed',
  },

  vehicleCostAnalysis: {
    title: 'Vehicle costs',
    cardDescription: 'Analyse imported costs by organisation, group and vehicle',
    description:
      'Select an organisation or group to see all vehicles summarised. Select one or more vehicles to analyse cost types and invoice lines.',
    totalCost: 'Total cost',
    vehiclesWithCost: 'Vehicles with cost',
    lineCount: 'Lines',
    unmatched: 'Unmatched (unknown plate)',
    groupBreakdown: 'Cost types in the group',
    vehiclesTitle: 'Vehicles in the group',
    onlyWithCosts: 'Show only vehicles with cost',
    clearSelection: 'Clear selection',
    noVehicles: 'No vehicles in the selected group',
    selectionTitle: 'Analysis of {{count}} selected vehicle(s)',
    selectedVehicles: 'Selected vehicles',
    selectionBreakdown: 'Cost types for the selection',
    linesTitle: 'Invoice lines',
    noLines: 'No invoice lines for the selection in this period',
    colReg: 'Plate',
    colVehicle: 'Vehicle',
    colOrg: 'Organisation',
    colLines: 'Lines',
    colTotal: 'Total excl. VAT',
    colCategory: 'Cost type',
    colInvoiceDate: 'Invoice date',
    colInvoiceNumber: 'Invoice no.',
    colDescription: 'Description',
    colAmount: 'Amount excl. VAT',
    loadError: 'Could not load the cost summary',
    tabs: {
      overview: 'Overview',
      vehicles: 'Vehicles',
      analysis: 'Analysis',
    },
    goToAnalysis: 'Analyse selected',
    selectVehiclesHint: 'Select one or more vehicles on the Vehicles tab to analyse them.',
  },

  contractDocument: {
    title: 'Contract confirmations (PDF)',
    description:
      'Upload Ayvens confirmations. AI reads the driver, contract and leasing fields. The PDF is attached to the vehicle and the values are written to Economy.',
    selectFiles: 'Select PDF files',
    selectFolder: 'Select folder',
    noPdfFiles: 'The folder contained no PDF files',
    noImports: 'No contract documents yet',
    loadError: 'Could not load contract documents',
    importError: 'Contract document import failed',
    importSuccess: 'Imported {{count}} contract document(s)',
    unmatchedHint: '{{count}} file(s) could not be matched to a vehicle',
    attachedHint: '{{count}} PDF(s) attached to vehicles',
    aiSkipped: 'The AI server did not respond — plate and basic fields were still read from the file',
    duplicateFile: '{{name}} was already imported',
    colFilename: 'Filename',
    colReg: 'Plate',
    colDriver: 'Driver',
    colContract: 'Contract',
    colStart: 'Start',
    colEnd: 'End',
    colAi: 'AI',
  },

  // Booking - Pickup locations, suppliers, destinations
  booking: {
    title: 'Booking',
    pickupLocations: 'Pickup locations',
    pickupLocationsTitle: 'Pickup locations',
    pickupLocationsDescription: 'Under Booking: places where vehicles are picked up and returned',
    addPickupLocation: 'Add pickup location',
    editPickupLocation: 'Edit pickup location',
    noPickupLocations: 'No pickup locations. Add one to get started.',
    noPickupLocationsCalendar: 'No pickup locations created. Open Booking and choose Pickup locations to create pools and add vehicles.',
    goToPickupLocations: 'Open Booking',
    managedBySupplier: 'Managed by car rental company',
    managedBySupplierNone: 'Own pool (no car rental company)',
    managedBySupplierHelp:
      'Select a car rental company to let them manage the pool and assign cars for requests. The vehicles in the pool are owned by the rental company.',
    managedBySupplierNoLinks:
      'No car rental links exist for this organization. Create a link under Car rental first.',
    pickupAllowedCarClasses: 'Car classes (bookable / requestable)',
    pickupAllowedCarClassesHelp:
      'Enter the car classes that can be booked or requested at this location. Leave empty to inherit the car rental link\'s car classes; if that is also empty, the booker can type a car class freely.',
    pickupLocationResponsibles: 'Responsible persons',
    pickupLocationResponsiblesHelp:
      'People responsible for this pickup location. They can manage Flinkey boxes for pool vehicles in the mobile app (FlinkyBoxes) if they have permission.',
    pickupLocationResponsiblesSearch: 'Search users',
    pickupLocationResponsiblesSearchPlaceholder: 'Name or email…',
    pickupLocationResponsiblesNone: 'No responsible persons selected',
    pickupLocationActiveLabel: 'Active (shown in booking and pools)',
    pickupLocationActiveHelp:
      'If you clear this, the location is deactivated. Future bookings with status Booked that still have a vehicle from this pool lose the assignment and appear under Collision bookings so you can assign another pool or cancel.',
    pickupBookingsLockedLabel: 'Visible but locked',
    pickupBookingsLockedHelp:
      'The pool is shown in the calendar and users can see their bookings, but nobody can create, change or cancel until the lock is released.',
    pickupBookingsLockedBadge: 'Locked',
    poolBookingsLockedHint:
      'This pool is locked. You can see bookings but cannot create, change or cancel until the lock is released.',
    pickupLocationOpens: 'opens {{date}}',
    pickupLocationNotYetOpenHint:
      'This pickup location opens {{date}}. You can see it in the list but cannot book until it is valid.',
    pickupValidFrom: 'Valid from',
    pickupValidTo: 'Valid to',
    pickupValidDatesHelp:
      'Controls when the location is shown and bookable. Leave empty for no restriction (applies immediately). Outside the period the location is hidden in booking and cannot be booked. Admins still see it here.',
    showBookerNameInResourceManager: 'Show booker name in resource manager',
    showBookerNameInResourceManagerHelp:
      'When enabled, the booker name (and fellow travelers on hover) is shown on booking blocks in the resource manager. Default is hidden.',
    fellowTravelers: 'Fellow travelers',
    pickupInactiveBadge: 'Inactive',
    pickupDeactivate: 'Deactivate',
    pickupActivate: 'Activate',
    pickupDelete: 'Delete',
    pickupExportPools: 'Download pool list',
    pickupExportPoolsLoading: 'Exporting…',
    pickupActivateConfirm: 'Activate "{{name}}" again?',
    pickupDeleteConfirm: 'Permanently delete "{{name}}"? This cannot be undone.',
    pickupDeleteHasBookings: 'This location has {{count}} booking(s) in history and cannot be deleted. Deactivate it instead.',
    pickupDeactivateSuccess: 'Pickup location deactivated',
    pickupActivateSuccess: 'Pickup location activated',
    pickupDeleteSuccess: 'Pickup location deleted',
    pickupDeactivateConfirm:
      'Deactivate "{{name}}"? All future bookings (Booked, not checked out) that still have a vehicle from this pool will lose the vehicle and appear under Collision bookings. Continue?',
    pickupDeactivateStripped: '{{count}} booking(s) moved to collision handling.',
    pickupLocationSaveError: 'Could not save the pickup location.',
    createUnderOrganization: 'Create under organization',
    loadingOrganizations: 'Loading organizations...',
    noTopNodesFound: 'No top nodes found. Check permissions or try again.',
    drawGeofenceArea: 'Draw area on map',
    startDrawing: 'Start drawing',
    finishArea: 'Done – save area',
    geofenceAreaHelp: 'Click on the map to set points. At least 3 points required. The area is used for enter/exit detection and to validate that booking ends at the correct location.',
    geofenceEnforceOnReturn: 'Require the car to be inside the area at check-in',
    geofenceEnforceOnReturnHelp: 'When enabled, the car must be inside the map area when the booking is checked in/finished via the app. Otherwise the return is blocked. Customer service with Return at another location can grant an exception (e.g. delayed GPS). Requires a drawn area.',
    nearbyPoolSearchRadiusM: 'Search nearby pools for available vehicles (meters)',
    nearbyPoolSearchRadiusMHelp:
      'When creating a booking, available vehicles from other pools whose parking area is within this distance from this pool\'s parking area are also shown. Default 2000 m. Set 0 to show only this pool.',
    nearbyPoolSearchRadiusMPlaceholder: '2000',
    pickupHasGeofenceArea: 'Has map area',
    pickupShowAreasOnMap: 'Show map areas',
    pickupAreasMapTitle: 'Map areas for pickup locations',
    pickupAreasMapNoAreas: 'No pickup locations have map areas drawn.',
    pickupAreasMapListHint: 'Click a name to zoom to the area.',
    pickupAreasMapListCount: '{{count}} locations with map area',
    pickupAreasMapShowAll: 'Show all areas',
    selectOrgGroup: 'Select group (organization)',
    vehiclesInPool: 'Vehicles in pool',
    bookingResourcesInPool: 'Resources in pool',
    bookingResourcesInPoolHelp: 'Bicycles, trailers and other customer resources. Resource type is inherited from the resource register.',
    addBookingResourceToPool: 'Add resource',
    selectBookingResource: 'Select resource',
    noUnassignedResources: 'No unassigned resources. Create resources under Booking resources.',
    bookingResourceAdded: 'Resource added to pool',
    bookingResourceRemoved: 'Resource removed from pool',
    vehiclesInGroup: 'Vehicles in group',
    pickupLinkedTotal: '{{count}} vehicles linked to this pickup location',
    pickupLinkedInList: 'of {{total}} vehicles are linked',
    pickupLinkedOtherOrgsTitle: '{{count}} linked vehicles are not in the list (different organization than selected)',
    pickupLinkedOtherOrgsHint: '{{count}} linked vehicles belong to a different organization than the selected group (marked in the list).',
    pickupLinkedOtherOrgBadge: 'Other org',
    pickupVehicleSearch: 'Search vehicles',
    pickupVehicleSearchPlaceholder: 'Registration, make or model…',
    pickupVehicleSearchResults: 'Showing {{shown}} of {{total}} vehicles',
    pickupVehicleSearchNoHits: 'No vehicles match your search.',
    pickupLinkedBadge: 'Linked',
    pickupVehiclesApplyOnSave:
      'Check or uncheck vehicles here. Changes take effect only when you click Save. Cancel leaves the list as it was.',
    pickupRemoveVehiclesConfirm:
      'Remove {{count}} vehicles from this pickup location? Upcoming bookings on these cars will be reassigned or become collisions.',
    pickupPoolResourceTypeHeading: 'Bookable resource type (per pool vehicle)',
    pickupPoolResourceTypeHelp: 'Empty counts as car in booking search. Choose e.g. Bicycle for pool bikes.',
    pickupPoolResourceTypeDefault: 'Default (car)',
    pickupBookingInfo: 'Info for the booker',
    pickupBookingInfoHelp: 'This text is included in the booking confirmation sent to the person who booked a vehicle from the pool.',
    pickupLocationMapShow: 'Show pickup location on map',
    pickupLocationMapTitle: 'Pickup location on map',
    pickupLocationMapNoAddress: 'No address entered.',
    pickupLocationMapNoCoords: 'No map is available for this location.',
    pickupTabSettings: 'Settings',
    pickupTabBooking: 'Booking',
    pickupTabVehicles: 'Vehicles',
    pickupTabResources: 'Resources',
    pickupTabKeyCabinet: 'Key cabinet',
    pickupTabKeyCabinetPlaceholder: 'Key cabinets will be linked to the pickup location here. This will be built out shortly.',
    pickupTabKeyCabinetHelp: 'Linked cabinets are shown here. You can also link cabinets that belong to the same organization but are not linked to any pickup location.',
    pickupKeyCabinetsConnected: 'Linked cabinets',
    pickupKeyCabinetsNone: 'No cabinets are linked to this pickup location.',
    pickupKeyCabinetsAvailable: 'Available cabinets in the organization',
    pickupKeyCabinetsNoneAvailable: 'No unlinked cabinets in the organization.',
    pickupKeyCabinetSelect: 'Select cabinet',
    pickupKeyCabinetConnect: 'Link cabinet',
    pickupKeyCabinetDisconnect: 'Remove link',
    pickupKeyCabinetConnectSuccess: 'The cabinet was linked to the pickup location.',
    pickupKeyCabinetDisconnectSuccess: 'The cabinet link was removed.',
    pickupKeyCabinetConnectError: 'Could not link the cabinet.',
    pickupKeyCabinetDisconnectError: 'Could not remove the cabinet link.',
    pickupKeyCabinetLoadError: 'Could not load key cabinets.',
    pickupTabResponsibles: 'Responsible persons',
    addVehiclesToPool: 'Add selected to pool',
    name: 'Name',
    address: 'Address',
    postalCode: 'Postal code',
    city: 'City',
    pickupMappedVehicleCount: 'Vehicles',
    nearbyPoolSearchRadiusMeters: 'Search nearby pools (meters)',
    nearbyPoolSearchRadiusPlaceholder: 'e.g. 5000',
    nearbyPoolSearchRadiusHelp:
      'When booking, also search for available vehicles at other pickup locations within this distance (from the map area center). Leave empty or 0 to use only this pool. Requires a drawn map area.',
    displayOrder: 'Display order',
    suppliers: 'Suppliers',
    suppliersTitle: 'Suppliers',
    suppliersDescription: 'Configure your suppliers and your own car group names. The same CRM car rental can be linked by multiple customers – each has their own list and naming.',
    addSupplier: 'Add supplier',
    addCarRentalFromCrm: 'Add car rental from CRM',
    selectCarRental: 'Select car rental company',
    noCarRentalCustomers: 'No customers with type Car rental in CRM. Create a customer with type Car rental under CRM → Customers first.',
    deliversToAllPools: 'Delivers to all pools',
    editSupplier: 'Edit supplier',
    noSuppliers: 'No suppliers. Add one to get started.',
    supplierType: 'Type',
    supplierTypeCarRental: 'Car rental',
    supplierTypeOther: 'Other',
    supplierEmail: 'Email',
    supplierPhone: 'Phone number',
    supplierEmailPlaceholder: 'e.g. booking@avis.se',
    supplierPhonePlaceholder: 'e.g. +46 11 123 45 67',
    supplierCarClasses: 'Car groups',
    supplierCarClassesHelp: 'Your own names for car groups with this supplier, e.g. Small car or Medium car. Used for booking requests.',
    supplierCarClassPlaceholder: 'e.g. Small car',
    supplierCarClassAdd: 'Add',
    supplierCarClassRemove: 'Remove {{name}}',
    rentalManagementTitle: 'Car rental',
    rentalManagementDescription: 'Link customer organizations to a car rental company for pool cars and request rentals. The access level controls whether the customer books directly or only sees statistics.',
    addRentalLink: 'Add link',
    editRentalLink: 'Edit link',
    noRentalLinks: 'No car rental links yet. Add one to get started.',
    rentalCompany: 'Car rental company',
    rentalCustomer: 'Customer organization',
    rentalSelectCompany: 'Select car rental company',
    rentalSelectCustomer: 'Select customer organization',
    rentalAccessLevel: 'Access level',
    rentalAccessFullBooking: 'Full booking (book/create request)',
    rentalAccessStatsOnly: 'Statistics only',
    rentalAccessFullBookingHelp: 'The customer\'s users book existing pool cars or create requests. The car rental company assigns vehicles. The customer cannot manage the vehicles.',
    rentalAccessStatsOnlyHelp: 'The customer\'s employees can book/create requests, but the customer does not manage the vehicles. Customer admins see booking statistics and the environmental report.',
    rentalLinkSelectBoth: 'Select both a car rental company and a customer organization.',
    rentalLinkSaveError: 'Could not save the link.',
    rentalLinkDeleteConfirm: 'Do you want to remove this car rental link?',
    requestedCarClass: 'Requested car group',
    requestedCarClassPlaceholder: 'Select car group',
    requestedCarClassFreePlaceholder: 'Enter requested car group',
    requestedCarClassNoOptionsHint: 'No car groups configured on your suppliers – enter manually or add them under Suppliers.',
    requestedCarClassRequired: 'Select or enter a requested car group',
    supplierCrmCustomer: 'Customer (car rental in CRM)',
    supplierCrmNone: 'No link – standalone supplier',
    supplierCrmCustomerHelp: 'Optional link to a car rental in CRM. The same CRM record can be used by multiple customers – each configures their own car groups and contact details.',
    supplierNameFromCrm: 'Name is taken from the CRM customer.',
    destinations: 'Destinations',
    destinationsTitle: 'Destinations',
    destinationsDescription: 'Predefined destinations for bookings',
    addDestination: 'Add destination',
    editDestination: 'Edit destination',
    noDestinations: 'No destinations. Add one to get started.',
    vehiclesAtLocation: 'Vehicles at location',
    addVehicle: 'Add vehicle',
    calendarTitle: 'Booking calendar',
    calendarDescription: 'Gantt view of pool occupancy – see free slots and create bookings',
    weekTitle: 'Week {{week}} ({{start}} - {{end}})',
    calendarViewAll: 'All days',
    calendarViewWorkdays: 'Workdays only',
    viewWeek: 'Week',
    viewDay: 'Day',
    today: 'Today',
    prevDay: 'Previous day',
    nextDay: 'Next day',
    openDayView: 'Open day view',
    vehicleColumn: 'Vehicle',
    redDay: 'Red day (public holiday)',
    status: 'Status',
    statusBooked: 'Booked',
    statusCheckedOut: 'Checked out',
    statusReturned: 'Returned',
    statusCancelled: 'Cancelled',
    statusCancelledBySystem: 'Cancelled by system',
    statusNoShow: 'No-show (not picked up)',
    statusOverdue: 'Overdue',
    checkedOutAt: 'Checked out at',
    returnedAt: 'Returned at',
    checkoutButton: 'Check out',
    checkoutDialogTitle: 'Check out booking',
    checkoutTimeLabel: 'Check-out time',
    ksMarkPickedUp: 'Mark as picked up',
    ksMarkPickedUpTitle: 'Mark as picked up',
    ksMarkPickedUpConfirm: 'Do you want to mark this booking as picked up?',
    ksReturnTitle: 'Return booking',
    ksReturnConfirm: 'Do you want to return this booking?',
    returnButton: 'Return',
    changeResource: 'Change resource',
    changeResourceTitle: 'Change resource',
    changeResourceCurrent: 'Current resource',
    changeResourceNew: 'New resource',
    changeResourceSearchPlaceholder: 'Search vehicle (e.g. 197, GNB563, Manual)…',
    changeResourceSelected: 'Selected',
    changeResourceNoneAvailable: 'No vehicles available in the pool for this period',
    changeResourceSave: 'Change resource',
    changeResourceSuccess: 'The resource has been changed.',
    changeResourceError: 'Could not change resource.',
    vehicleMapShow: 'Show on map',
    vehicleMapTitle: 'Vehicle position',
    vehicleMapSourceLive: 'Current position',
    vehicleMapSourceStored: 'Last saved position',
    vehicleMapUpdatedAt: 'Updated',
    vehicleMapNoPosition: 'No position is stored for this vehicle.',
    vehicleMapLoadError: 'Could not load the vehicle position.',
    legendVehicleBlock: 'Vehicle block (admin)',
    legendImportedBooking: 'Imported booking (JSON)',
    vehicleBlockShort: 'Blocked',
    noVehicleAssigned: 'No vehicle assigned',
    ownCar: {
      formTitle: 'Create trip with own car',
      listTitle: 'Own-car trips',
      listIntro: 'Your own trips and those you are allowed to review.',
      listEmpty: 'No trips to show.',
      listLoadError: 'Could not load trips.',
      loadError: 'Could not load the form.',
      toList: 'Show all trips',
      newTrip: 'New trip',
      bookedFor: 'Booked for',
      phone: 'Phone number',
      car: 'Car',
      selectCar: 'Select your car',
      newCar: 'New car…',
      lookupCar: 'Look up in car.info',
      regRequired: 'Enter a registration number first',
      carInfoSuccess: 'Vehicle details fetched from car.info',
      carInfoError: 'Could not fetch the vehicle from car.info',
      model: 'Model',
      year: 'Model year',
      color: 'Color',
      fuel: 'Fuel type',
      co2PerKm: 'CO₂',
      reimbursement: 'Compensation per kilometre',
      odometerStart: 'Odometer – trip start',
      odometerEnd: 'Odometer – trip end',
      directKm: 'Or enter the trip length directly (whole km)',
      totalReimbursement: 'Total compensation',
      from: 'Booking from',
      to: 'Booking to',
      costCenter: 'Cost centre',
      selectCostCenter: 'Select cost centre',
      project: 'Project number',
      emission: 'Emission',
      km: 'Km',
      total: 'Total',
      co2: 'CO₂',
      travelFrom: 'Trip – From',
      travelTo: 'Trip – To',
      purpose: 'Purpose',
      ownCarReason: 'Reason for using a private car',
      save: 'Save',
      saveAndSubmit: 'Save and submit',
      saveSuccess: 'The trip was saved and can still be edited.',
      submitSuccess: 'The trip was submitted for review.',
      saveError: 'Could not save the trip.',
      approve: 'Approve',
      reject: 'Reject',
      approveSuccess: 'The trip was approved.',
      rejectSuccess: 'The trip was rejected.',
      reviewError: 'Could not review the trip.',
      sendPayroll: 'Send to payroll',
      payrollSuccess: 'The trip was marked as sent to payroll.',
      payrollError: 'Could not send to payroll.',
      export: 'Export CSV',
      exportError: 'Could not export.',
      searchPlaceholder: 'Search name, plate, from/to…',
      allStatuses: 'All statuses',
      statusLabel: 'Status',
      status: {
        draft: 'Draft',
        submitted: 'Submitted',
        approved: 'Approved',
        rejected: 'Rejected',
        sent_to_payroll: 'Sent to payroll',
      },
    },
    collisionTitle: 'Collision bookings',
    collisionIntro:
      'Bookings without an assigned vehicle (e.g. after a vehicle was blocked for booking). Previous pool and vehicle are kept so you can assign another vehicle or cancel.',
    collisionBack: 'Back to booking',
    collisionEmpty: 'No collision bookings right now.',
    collisionLoadError: 'Could not load collision bookings.',
    collisionDetailLoadError: 'Could not open the booking.',
    collisionColPool: 'Pool',
    collisionColCustomer: 'Booked for',
    collisionColTime: 'Time',
    collisionColPriorVehicle: 'Previous vehicle',
    collisionColAvailableResource: 'Available resource',
    collisionAssignVehicle: 'Assign',
    collisionAssignSuccess: 'Vehicle assigned.',
    collisionAssignError: 'Could not assign vehicle.',
    collisionCancelBySystem: 'Cancel booking',
    collisionCancelConfirm:
      'No available resource. Cancel this booking? Email will be sent to the booker and passengers.',
    collisionCancelSuccess: 'Booking was cancelled by the system.',
    collisionCancelError: 'Could not cancel the booking.',
    collisionColStatus: 'Status',
    collisionColAction: 'Action',
    collisionOpenBooking: 'Open booking',
    assignVehicleTitle: 'Assign pool vehicle',
    assignVehicleHint:
      'Pick a free vehicle for this time slot. You can also remove the booking or send the customer the cancellation link below.',
    assignVehicleSelectPlaceholder: 'Select vehicle…',
    assignVehicleButton: 'Assign',
    assignVehicleSuccess: 'Vehicle assigned.',
    assignVehicleError: 'Could not assign vehicle.',
    assignVehicleNoneAvailable:
      'No free vehicles in the pool for this time (check admin blocks and other bookings).',
    staffCancelLinkHint: 'Customer self-service cancellation (new tab):',
    staffCancelLinkOpen: 'Cancellation page',
    newBooking: 'New booking',
    newBookingSubtitle: 'Fill in vehicle booking with purpose, destination and recurring options.',
    organization: 'Organization',
    pool: 'Pool',
    date: 'Date',
    vehicle: 'Vehicle',
    selectPoolAndDate: 'Select pool and date to see occupancy.',
    noVehiclesInPool: 'No vehicles in this pool. Add vehicles under Booking → Pickup locations.',
    start: 'Start',
    end: 'End',
    customerName: 'Booked by / for',
    customerNamePlaceholder: 'Name or unit',
    bookedFor: 'Booked for',
    searchUserPlaceholder: 'Search name, email or RSID...',
    createdBy: 'Booked by',
    createdChannel: 'Booked from',
    createdChannelApp: 'App',
    createdChannelWeb: 'Computer',
    bookingTypeSingle: 'Single booking',
    bookingTypeGroup: 'Group booking',
    bookingTypePool: 'Vehicle pool',
    tripPurpose: 'Purpose of trip',
    tripPurposePlaceholder: 'e.g. Customer visit',
    notes: 'Notes',
    estimatedKm: 'Estimated distance (km)',
    passengerCount: 'Number of passengers',
    destination: 'Destination',
    destinationPlaceholder: 'Select or enter place',
    destinationTypeFree: 'Enter free text if no match',
    driverLicenseConfirmed: 'I have a valid driving licence',
    bookingPolicyCheckbox: 'I have read and accept the booking policy',
    bookingPolicyCheckboxHelp: 'Tick the box to open the policy. Confirm at the bottom of the window when you have finished reading.',
    isServiceBooking: 'Service',
    isServiceBookingHelp: 'Check if the booking is for workshop/service on the vehicle. Such bookings are not affected by the no-show rule that otherwise releases the slot after the configured time.',
    bookingPolicyTitle: 'Booking policy',
    bookingPolicyReadConfirm: 'I have read the policy',
    approvedBy: 'Approved by (attestation)',
    approvedByPlaceholder: '— Optional —',
    noAvailableVehicles: 'No vehicles available for the selected period',
    recurrence: 'Recurring',
    recurrenceDaily: 'Day',
    recurrenceWeekly: 'Weekly',
    recurrenceMonthly: 'Monthly',
    recurrenceCount: 'Number of occurrences',
    loadingAvailableVehicles: 'Loading available vehicles...',
    filterByEquipment: 'Filter by equipment',
    filterAutomaticTransmission: 'Automatic transmission only',
    filterAutomaticTransmissionHelp: 'When checked, only vehicles with an automatic transmission are listed.',
    filterByResourceType: 'Resource type',
    resourceTypeFilterAll: 'All types',
    resourceTypeFilterCar: 'Car',
    resource: 'Resource',
    loadingAvailableResources: 'Loading available resources…',
    noAvailableResources: 'No available resources for the selected time',
    noAutomaticTransmissionVehicles: 'No available automatic-transmission vehicles for the selected period. Clear the filter or pick another time.',
    noVehicleCreateRental: 'No available vehicle – create rental from supplier',
    createRentalAndBooking: 'Create rental and booking',
    externalDisplayName: 'Display name (e.g. rental car)',
    externalDisplayNamePlaceholder: 'e.g. Rental car A',
    selectSupplier: 'Select supplier',
    costCenter: 'Cost center',
    project: 'Project',
    costCenterPlaceholder: '— Select cost center —',
    costCentersTitle: 'Cost centers',
    costCentersDescription: 'Cost centers per organization – selected when booking. Can be deactivated but never deleted.',
    addCostCenter: 'Add cost center',
    editCostCenter: 'Edit cost center',
    noCostCenters: 'No cost centers. Add one to get started.',
    costCenterCode: 'Code (max 15 chars)',
    costCenterDescription: 'Description (max 50 chars)',
    costCenterValidFrom: 'Valid from',
    costCenterActive: 'Active',
    costCenterInactive: 'Inactive',
    saveError: 'Could not save booking',
    resourceTypesTitle: 'Resource types per customer',
    resourceTypesDescription: 'Resource types (Car, Bicycle, Conference room, etc.) are defined at system level. Here you mark which types each customer (organization) can use for booking.',
    resourceTypesOrganization: 'Organization (customer)',
    resourceTypesSelectOrg: 'Select organization',
    resourceTypesSelectOrgFirst: 'Select an organization to set which resource types can be used.',
    resourceTypesMyOrg: 'My organization',
    resourceTypesForOrg: 'Active resource types for',
    resourceTypeLabel: 'Resource type',
    resourceTypeEnabled: 'Enabled for this customer',
    resourceTypeDisabled: 'Disabled for this customer',
    resourceTypesSaved: 'Resource types saved',
    resourceTypesSaveError: 'Could not save resource types',
    bookingCreated: 'Booking created',
    sendCalendarInvitePrompt: 'Do you want to send a calendar invite to the customer? An email with a calendar attachment will be sent to the customer\'s address.',
    noEmailForCalendarInvite: 'The customer has no email address – calendar invite cannot be sent.',
    sendCalendarInvite: 'Send calendar invite',
    done: 'Done',
    calendarInviteSent: 'Calendar invite sent to the customer\'s email.',
    calendarInviteError: 'Could not send calendar invite.',
    bookingDetailsTitle: 'Booking details',
    notesShow: 'Show notes',
    notesHide: 'Hide notes',
    cancelBookingTitle: 'Cancel booking',
    cancelBookingConfirm: 'Do you want to cancel this booking? It will not be deleted, only marked as inactive.',
    cancelBookingSuccess: 'The booking has been cancelled (inactive).',
    cancelBookingInvalid: 'Invalid or already used cancel link.',
    cancelBookingError: 'Could not cancel booking.',
    cancelBookingPast: 'The booking has already ended and cannot be cancelled.',
    cancelBookingAlready: 'The booking is already cancelled.',
    cancelBookingButton: 'Cancel booking',
    importLockedHint: 'Imported booking is locked on the vehicle until it is linked to a person. After that it is handled as a normal booking.',
    cancelBookingHelp: 'Cancelling marks the booking as inactive (it is not deleted) and frees up the time. "Close" closes this window.',
    keyCabinetAccessActive: 'Key cabinet access active during booking period.',
    noShowReleasePoolOverride: 'No-show release (pool)',
    noShowReleasePoolOverridePlaceholder: 'Empty = inherit from org, 0 = disabled, e.g. 30 = minutes',
    noShowReleasePoolOverrideHelp: 'Override the organization rule for this pool. Empty = use org (top node). 0 = rule disabled here. Otherwise = minutes after start.',
    poolBookingRulesHeading: 'Booking rules (override org)',
    poolOverrideInheritHelp: 'Empty = inherit from organization (top node). 0 = no restriction.',
    poolClosuresHeading: 'Closure periods',
    poolClosuresEmpty: 'No closure periods.',
    closureFrom: 'Closed from',
    closureToOptional: 'Closed until (optional)',
    closureOpenEnded: 'Until further notice',
    closureReasonPlaceholder: 'Reason (optional)',
    closureAdd: 'Add closure',
    closureDeleteConfirm: 'Remove this closure period?',
    closureStripped: '{{count}} future booking(s) moved to collision bookings.',
    fallbackSuppliersHeading: 'Suppliers when no vehicles',
    allowExternalBookingWhenNoCars: 'Allow supplier booking when no vehicles available',
    fallbackSuppliersHelp: 'All selected suppliers are notified when a booking is created without an available vehicle.',
    noSuppliersInOrg: 'No suppliers in organization.',
    supplierFallbackPrompt: 'No vehicle available – send booking to configured suppliers.',
    createSupplierFallbackBooking: 'Create booking via suppliers',
    managedRequestPrompt: 'No vehicle available – send a request to the car rental company, which will assign a vehicle.',
    createManagedRequest: 'Create request to car rental company',
    requestsRow: 'Requests',
    requestShort: 'Request',
    pastBookingNotDeletable: 'Past bookings cannot be cancelled.',
    bookingHistoryLimitHint: 'You see bookings from the last 3 months.',
    vehicleReplacementTitle: 'Vehicle replacement',
    replaceWithVehicle: 'Replace with vehicle',
    replaceVehicle: 'Replace vehicle',
    takeOutOfService: 'Take out of service',
    returnToService: 'Return to service',
    removeFromPool: 'Remove from pool',
    outOfService: 'Out of service',
    futureBookingsCount: '{{count}} future/ongoing booking(s) on this vehicle.',
    vehicleReplaceConfirm: 'Replace vehicle and move {{count}} booking(s)?',
    vehicleReplaceDone: '{{count}} booking(s) moved to new vehicle.',
    vehicleOutOfServiceConfirm: 'Mark out of service and reassign {{count}} booking(s)?',
    vehicleOutOfServiceDone: '{{reassigned}} reassigned, {{collisions}} in collision bookings.',
    vehicleReturnToServiceDone: 'Vehicle is back in service.',
    vehicleRemoveFromPoolConfirm: 'Remove vehicle and reassign {{count}} booking(s)?',
    vehicleRemoveFromPoolDone: '{{reassigned}} reassigned, {{collisions}} in collision bookings.',
    requestsTitle: 'Booking Requests',
    requestsDescription: 'Bookings that could not be assigned a vehicle at booking time – assign a vehicle or handle via supplier.',
    requestsBack: 'Back to booking',
    requestsEmpty: 'No booking requests right now.',
    requestsLoadError: 'Could not load booking requests.',
    requestsDetailLoadError: 'Could not open the booking.',
    requestsColPool: 'Pool',
    requestsColCustomer: 'Booked for',
    requestsColOrganization: 'Customer/group',
    bookingOrganization: 'Customer/group',
    requestsColTime: 'Time',
    requestsColCarClass: 'Car group',
    requestsColSuppliers: 'Suppliers',
    requestsColStatus: 'Status',
    requestsColAction: 'Action',
    requestsOpenBooking: 'Open booking',
    fleetAssignTitle: 'Assign vehicle from fleet',
    fleetAssignHint: 'The rental company can assign one of its vehicles to this pickup location. The customer can only add their own vehicles.',
    fleetAssignEmpty: 'No available fleet vehicles right now.',
    fleetAssignPlaceholder: 'Select a fleet vehicle',
    fleetAssignButton: 'Assign',
    fleetAssignSuccess: 'The vehicle has been assigned.',
    fleetAssignError: 'Could not assign the vehicle.',
    fleetAssignFromRequestHint: 'Or assign an available fleet vehicle for this booking only.',
    basisTitle: 'Booking documentation',
    basisBack: 'Back to booking',
    basisBackToList: 'Back to list',
    basisDescriptionOwn: 'Search your returned bookings and open the documentation.',
    basisDescriptionAll: 'Search returned bookings in the organization and open the documentation.',
    basisSearch: 'Search',
    basisSearchPlaceholder: 'Name, registration, pickup location…',
    basisEmpty: 'No returned bookings found.',
    basisLoadError: 'Could not load booking documentation.',
    basisDetailLoadError: 'Could not open the documentation.',
    basisOpen: 'Open',
    basisColVehicle: 'Vehicle',
    basisColPeriod: 'Period',
    basisDownloadPdf: 'Print / PDF',
    basisPdfError: 'Could not create PDF.',
    basisBookedFor: 'Booked for',
    basisLoanHint: 'The vehicle was temporarily assigned from the rental company fleet.',
    basisKpiDistance: 'Distance',
    basisKpiOdometer: 'Odometer',
    basisKpiWarnings: 'Warnings',
    basisKpiCo2: 'CO₂',
    basisSectionTrips: 'Trips',
    basisSectionGeofences: 'Geofences',
    basisSectionWarnings: 'Warnings',
    basisSectionEnvironment: 'Environment',
    basisNoTrips: 'No trips in this period.',
    basisNoWarnings: 'No warnings.',
    basisGeofenceCost: 'Cost',
    basisPassages: 'passages',
    basisFuel: 'Fuel',
    requestsSearchPlaceholder: 'Search name, purpose...',
    requestsCount: 'bookings',
    requestsNoResults: 'No booking requests found',
    requestsBookedBy: 'Booked by',
    resourceManagerTitle: 'Resource Manager',
    resourceManagerDescription: 'Daily overview per vehicle – see occupancy and booking status for all cars in the pool',
    resourceManagerStatus: 'Status',
    resourceManagerToday: 'Today',
    resourceManagerNowLine: 'Current time',
    resourceManagerSearchOrg: 'Search organization...',
    resourceManagerShowAllPools: 'Show all pools',
    oldBookingTitle: 'Old Booking',
    oldBookingDescription: 'Daily overview per vehicle – historical booking data (temporary module)',
    oldBookingTemporaryNotice: 'Temporary module with historical export. Will be removed when migration is complete.',
    oldBookingDateRange: 'Data: {{from}} – {{to}}',
    bookingSearchTitle: 'Customer service',
    bookingSearchDescription: 'Administer and support bookings – search, view and edit (reference, pool, dates, driver, vehicle, resource type).',
    bookingSearchPrompt: 'Choose filters and click Search to load results.',
    bookingSearchReference: 'Reference (booking id)',
    bookingSearchReferencePlaceholder: 'UUID or partial id',
    bookingSearchAllPools: 'All pools',
    bookingSearchDateFrom: 'Booking from',
    bookingSearchDateTo: 'Booking to',
    bookingSearchBookedFor: 'Booked for (driver / display name)',
    bookingSearchBookedForPlaceholder: 'Name or email',
    bookingSearchBookedForHelp: 'Matches customer name and the user who created the booking.',
    bookingSearchVehicle: 'Resource (vehicle)',
    bookingSearchVehiclePlaceholder: 'Registration or display name',
    bookingSearchVehicleHelp: 'Matches registration number, display name and supplier reference.',
    bookingSearchResourceTypeAll: 'All resource types',
    bookingSearchResourceTypeHint: 'Resource type is set per vehicle in the pool (pickup location). Empty type is treated as car when filtering by Car. Bicycles etc. require the pool vehicle to be tagged with that type.',
    bookingSearchStatusAll: 'All statuses',
    bookingSearchRun: 'Search',
    bookingSearchResults: 'Search results',
    bookingSearchNoHits: 'No bookings matched the filters.',
    bookingSearchLoadError: 'Could not refresh the search results. The previous list is still shown.',
    bookingSearchColReference: 'Reference',
    bookingSearchColPickupLocation: 'Pickup location',
    bookingSearchColBookingFrom: 'Booking from',
    bookingSearchColBookingTo: 'Booking to',
    bookingSearchColResource: 'Resource',
    bookingSearchColTripCount: 'No. of trips',
    bookingSearchColBookedBy: 'Booked by',
    viewList: 'List',
    viewTimeline: 'Timeline',
    timelineDragHint: 'Drag a booking to move it, drag the right edge to change the end time, or drag on empty space to create a new booking.',
    timelineResizeHint: 'Drag to change end time',
    timelineMoveFailed: 'Could not move the booking.',
    timelineMoveConfirmTitle: 'Confirm booking move',
    timelineMoveConfirmQuestion: 'Do you want to move the booking? Confirm to save the change.',
    timelineMoveFrom: 'From',
    timelineMoveTo: 'To',
    timelineMoveNewTime: 'New time',
    timelineMoveNewVehicle: 'New vehicle',
    timelineMoveConfirmButton: 'Confirm move',
    timelineParticipants: 'Participants',
    userConflictWarningTitle: 'The user already has a booking during this time:',
    editBooking: 'Edit booking',
    editButton: 'Edit',
    editBookingTitle: 'Edit booking',
    editBookingSelfSubtitle: 'Edit your booking – time, purpose and participants. With permission you can change pickup location and switch to another available vehicle before start.',
    editBookingStaffSubtitle: 'Edit the booking – time, pickup location, vehicle, purpose and participants.',
    extendConflictTitle: 'The time overlaps the next booking on the same vehicle.',
    extendConflictMax: 'You can extend until {{time}}.',
    extendConflictUseMax: 'Use that time',
    extendConflictContactCs: 'If you need a longer booking you must contact customer service.',
    deleteBooking: 'Delete booking',
    deleteBookingConfirm: 'Delete this booking? It will be deactivated, not removed.',
    deleteBookingSuccess: 'The booking was deactivated',
    deleteBookingError: 'Could not delete the booking',
    startTooFarInPast: 'Start time cannot be more than 5 minutes before now.',
    assignedVehicleLocked: 'Assigned vehicle (cannot be changed when extending)',
    bookedForRequired: 'Select who the vehicle is for',
    editBookingLoadError: 'Could not open the booking for editing',
    startLockedHint: 'The booking has already started – the start time cannot be changed.',
    editBookingStartAt: 'Booking from',
    editBookingEndAt: 'Booking to',
    editBookingCustomerName: 'Customer name',
    editBookingNotes: 'Comment',
    editBookingSaveSuccess: 'Booking updated',
    editBookingSaveError: 'Could not update booking',
    editBookingInvalidRange: 'End time must be after start time',
    editBookingPersonSearch: 'Search person (name, email or RSID)\u2026',
    editBookingNoMatches: 'No matches',
    editBookingFreeTextHint: 'Pick a person from the list or type a free-text name.',
    editBookingMembers: 'Additional participants',
    editBookingAddMember: 'Add participant (name, email or RSID)…',
    editBookingMembersHint: 'Add up to 4 extra people. Checking "Key" allows checkout/return. The same person cannot be added more than once.',
    editBookingGroupBadge: 'Group booking',
    newBookingButton: 'Create new booking',
    newBookingSelectPoolFirst: 'Select a pool first to create a new booking.',
    guideButton: 'Guide',
    guideMenuTitle: 'Start guide',
    guideSearch: 'Search & filters',
    guideList: 'List view',
    guideTimeline: 'Timeline',
    blockButton: 'Block',
    blockButtonHint: 'Block the vehicle and create a damage report',
    blockNoVehicle: 'This booking has no assigned vehicle to block',
    blockTitle: 'Block vehicle',
    blockVehicleLabel: 'Vehicle',
    blockDamageType: 'Damage type',
    blockDamageType_collision: 'Collision',
    blockDamageType_theft: 'Theft',
    blockDamageType_fire: 'Fire',
    blockDamageType_vandalism: 'Vandalism',
    blockDamageType_other: 'Other',
    blockDamageTypeRequired: 'Select a damage type',
    blockDescription: 'Description',
    blockLocation: 'Location (optional)',
    blockDuration: 'Block the resource for',
    blockHours_12: '12 hours',
    blockHours_24: '24 hours',
    blockHours_120: '120 hours',
    blockUntilPreview: 'Block lasts until',
    blockConfirm: 'Block',
    blockSaveSuccess: 'Vehicle blocked. Rebooked to a free vehicle: {{reassigned}}. Waiting for handling: {{collisions}}',
    blockSaveSuccessReassigned: 'Vehicle blocked. {{reassigned}} booking(s) moved to a free vehicle. Other bookings kept their cars.',
    blockSaveSuccessWaiting: 'Vehicle blocked. No free vehicle — {{collisions}} booking(s) are waiting for handling.',
    blockSaveSuccessNone: 'Vehicle blocked.',
    blockSaveSuccessEnded: 'The booking was ended because the vehicle is unusable. The end time was set to now.',
    blockSaveError: 'Could not block the vehicle',
    blockedListButton: 'Blocked vehicles',
    blockedListButtonHint: 'Show the customer’s blocked vehicles',
    blockedListTitle: 'Blocked vehicles',
    blockedListEmpty: 'No active blocks',
    blockedListLoadError: 'Could not load blocked vehicles',
    blockedListVehicle: 'Vehicle',
    blockedListStart: 'Start',
    blockedListEnd: 'End',
    blockedListDamage: 'Damage report',
    blockedListDamageLink: 'Damage report #{{id}}',
    blockedListDamageInline: 'Damage report #{{id}}',
    blockedListNoDamage: 'No damage report',
    blockedListRelease: 'Release',
    blockedListReleaseConfirm: 'Release the block so the vehicle can be booked again?',
    blockedListReleased: 'Block released',
    blockedListReleaseError: 'Could not release the block',
    blockedListUpdated: 'Block updated',
    blockedListUpdateError: 'Could not update the block',
    fuelPinTooltipTitle: 'Fuel cards (PIN)',
    fuelPinLoading: 'Loading fuel cards…',
    fuelPinNone: 'No fuel cards',
    fuelPinError: 'Could not load fuel cards',
    fuelPinNoPin: '(no PIN)',
    fuelCardsTitle: 'Fuel cards',
    optimizationTitle: 'Booking optimization',
    optimizationDescription: 'View the day\u2019s occupancy per pool and optimize vehicle utilization with AI.',
    optimizationDate: 'Date',
    optimizationAllPools: 'All pools',
    optimizationCurrentTitle: 'Current state',
    optimizationProposedTitle: 'Proposed layout',
    optimizationKpiBookings: 'Bookings',
    optimizationKpiUsedVehicles: 'Vehicles in use',
    optimizationKpiTotalVehicles: 'Total vehicles',
    optimizationKpiUtilization: 'Utilization',
    optimizationRun: 'Optimize',
    optimizationRunning: 'Optimizing\u2026',
    optimizationResultTitle: 'Result (before/after)',
    optimizationBefore: 'Before',
    optimizationAfter: 'After',
    optimizationFreedVehicles: 'Freed vehicles',
    optimizationSuggestions: 'Suggestions',
    optimizationNoSuggestions: 'No improvement suggestions for the selected day.',
    optimizationSuggestionMove: '{{booking}}: {{from}} \u2192 {{to}}',
    optimizationNoData: 'No bookings for the selected day.',
    optimizationAiUnavailable: 'The AI service is currently unavailable. The current state is still shown \u2013 try optimizing again later.',
    optimizationError: 'Could not run the optimization',
    optimizationLoadError: 'Could not load occupancy',
    optimizationApply: 'Apply optimization',
    optimizationApplying: 'Applying…',
    optimizationApplyConfirm: 'This moves the bookings according to the suggestion to free up vehicles. Do you want to continue?',
    optimizationApplied: 'Optimization applied: {{moves}} booking(s) moved, {{freed}} vehicle(s) freed.',
    optimizationApplyError: 'Could not apply the optimization',
    optimizationApplyNeedsPool: 'Select a pool above to apply the optimization.',
    fitTitle: 'Fit a booking',
    fitDescription: 'Enter the desired time and requirements. The system checks whether the booking fits by moving existing bookings between the pool vehicles (without changing their times).',
    fitStart: 'Desired start',
    fitEnd: 'Desired end',
    fitCustomerName: 'Name/reference',
    fitCustomerNamePlaceholder: 'Optional name or reference',
    fitBookedFor: 'Booked for (person)',
    fitSelectPerson: 'Select person',
    fitSelectPersonRequired: 'Select a person for the booking.',
    fitResourceType: 'Resource type',
    fitAnyResourceType: 'Any resource type',
    fitAutomaticOnly: 'Automatic only',
    fitEquipment: 'Required equipment',
    fitCheck: 'Check fit',
    fitChecking: 'Checking…',
    fitCheckError: 'Could not check fit',
    fitFeasible: 'The booking fits on {{vehicle}}.',
    fitInfeasible: 'The booking does not fit even with reassignment.',
    fitNoMoves: 'No existing bookings need to be moved.',
    fitMovesTitle: 'The following {{count}} booking(s) will be moved:',
    fitApply: 'Create booking and move',
    fitApplying: 'Applying…',
    fitApplyConfirm: 'This creates the new booking and moves existing bookings to other vehicles. Do you want to continue?',
    fitApplyError: 'Could not apply the fit',
    groupMembers: 'Group members',
    canCheckoutKey: 'Can pick up key',
    addMemberPlaceholder: 'Add member (name, email or RSID)...',
    maxExtraMembersHint: 'You can add at most 4 extra people.',
    memberAlreadyAdded: 'This person is already on the booking.',
    groupMemberCount: '{{count}} people',
    groupOrganizerRole: 'Organizer · can always collect key',
    groupDriverLicenseConfirmed: 'At least one group member has a valid driving licence',
    groupFooterStatus: '{{members}} group members · {{keys}} with key access',
    newGroupBookingSubtitle: 'Fill in a vehicle booking for several people on the same trip.',
    settingsTitle: 'Booking settings',
    settingsDescription: 'Configure booking form, projects, destinations, cost centers, trip purposes and booking terms per organization top node.',
    settingsTopNode: 'Organization (top node)',
    settingsSaved: 'Settings saved',
    settingsSaveError: 'Could not save settings',
    tabFormFields: 'Booking form',
    tabQuickGuest: 'Quick booking',
    quickGuestSettingsHelp:
      'By default the guest account is deleted 60 minutes after the booking ends. Name, phone and email stay on the booking.',
    quickGuestReuseLabel: 'Keep guest accounts for reuse',
    quickGuestReuseHint:
      'If checked, the account is not deleted. The same email fills in name and phone and reuses the account for the next quick booking.',
    quickGuestReusedHint: 'Saved guest account found — it will be reused.',
    quickSavedReused: 'Quick booking created, saved guest account reused',
    tabProjectImport: 'Import projects',
    tabDestinations: 'Destinations',
    tabCostCenters: 'Cost centers',
    tabCostSettings: 'Booking costs',
    costSettings: {
      orgHelp: 'General internal billing per resource type for the customer. Applies to all pickup locations unless overridden at location level.',
      pickupHeading: 'Booking costs (location)',
      pickupHelp: 'Empty field = inherit general setting for the resource type. Enter a value to override at this location.',
      noResourceTypes: 'No resource types are enabled for the customer. Enable system types or create customer types under Booking resources.',
      inheritPlaceholder: 'Inherit',
      inheritedValue: 'General',
      legacyCancellationNote: 'Cancellation rule at organization level (administration) is separate; billing per resource type is configured here.',
      baseCostPerBooking: 'Base cost (SEK/booking)',
      hourlyCost: 'Hourly rate (SEK/hour)',
      kmCost: 'Distance cost (SEK/km)',
      cancellationFee: 'Cancellation fee (SEK/booking)',
      cancellationHours: 'Cancel within (hours)',
      maxDailyCost: 'Max price per day (SEK)',
      monthlyCost30d: 'Monthly cost per 30 days (SEK)',
      billingTimeFrom: 'Hourly billing from',
      billingTimeTo: 'Hourly billing until',
      offHoursHourlyCost: 'Off-hours hourly rate (SEK/hour)',
    },
    tabTerms: 'Booking terms',
    tabTripPurposes: 'Trip purposes',
    tripPurposesTitle: 'Trip purposes',
    tripPurposesDescription: 'Manage predefined trip purposes per organization top node.',
    newTripPurposePlaceholder: 'New trip purpose',
    noTripPurposes: 'No trip purposes added yet.',
    costCenterFreeTextPlaceholder: 'Or enter a custom cost center',
    formSettingsHelp: 'Settings apply to all users under the selected organization top node.',
    formSettingsField: 'Field',
    formSettingsVisible: 'Visible',
    formSettingsRequired: 'Required',
    formSettingsListOnly: 'List only',
    formSettingsListOnlyForced: 'This field always reads from a list only.',
    formSettingsLocked: 'locked',
    projectImportDescription: 'Download the template, fill in projects and upload the file. Existing project numbers are updated.',
    downloadProjectTemplate: 'Download project template',
    importProjects: 'Import projects',
    importProjectsSuccess: 'Import done: {{created}} new, {{updated}} updated',
    importProjectsError: 'Import failed',
    importProjectsResult: 'Created {{created}}, updated {{updated}}',
    importRowError: 'Row {{row}}: {{message}}',
    projectSearchPlaceholder: 'Search projects...',
    projectCode: 'Project no.',
    projectDescription: 'Project description',
    projectAccountString: 'Account string',
    projectValidFrom: 'Valid from',
    projectNumber: 'Project number',
    projectPlaceholder: 'Search project...',
    noProjects: 'No projects. Import from template to get started.',
    downloadTemplateError: 'Could not download template',
    footerRequiredHint: 'Fields marked * are required',
    saveBlockedTitle: 'Cannot save yet:',
    saveBlockedPolicy: 'Accept the booking policy (scroll down in the form)',
    saveBlockedTerms: 'Accept the booking terms (scroll down in the form)',
    saveBlockedDriverLicense: 'Confirm that you have a valid driver\'s license',
    saveBlockedNoVehicle: 'No vehicle available for the selected time',
    saveBlockedProject: 'Select a project number from the list (click a match after searching)',
    section: {
      booking: 'Booking',
      group: 'Group members',
      vehicle: 'Vehicle',
      resource: 'Resource',
      trip: 'Trip',
      admin: 'Administrative',
    },
    formField: {
      booking_type: 'Type (single / group)',
      booked_for: 'Booked by / for',
      start_end: 'Start and end',
      recurrence: 'Recurring booking',
      group_members: 'Group members',
      vehicle: 'Vehicle',
      automatic_transmission_filter: 'Automatic transmission only',
      equipment_filter: 'Filter by equipment',
      trip_purpose: 'Purpose of trip',
      destination: 'Destination',
      estimated_km: 'Estimated distance',
      passenger_count: 'Passenger count',
      driver_license: 'Driving licence confirmed',
      service_booking: 'Service / on-call',
      approved_by: 'Approved by (attestation)',
      cost_center: 'Cost center',
      project: 'Project number',
      notes: 'Notes',
      booking_policy: 'Booking policy',
      terms: 'Booking terms',
    },
    acceptTerms: 'I accept the booking terms',
    status_BOOKED: 'Booked',
    status_CHECKED_OUT: 'Checked out',
    status_RETURNED: 'Returned',
    owner: 'Owner',
    checkedOut: 'Checked out',
    returned: 'Returned',
    checkedOutSuccess: 'Booking has been checked out',
    returnedSuccess: 'Booking has been returned',
    checkoutError: 'Could not check out booking',
    returnError: 'Could not return booking',
    returnOverrideButton: 'Allow return without key',
    returnOverrideHint: 'Allow the booking to be completed even if the key/card is missing in the box.',
    returnOverrideTitle: 'Allow return without key',
    returnOverrideDescription: 'The user can complete the booking even if the key/card is missing or the drawer is unlocked. Provide a reason (logged).',
    returnOverrideReasonLabel: 'Reason',
    returnOverrideReasonPlaceholder: 'E.g. key lost, card missing...',
    returnOverrideConfirm: 'Allow return',
    returnOverrideSuccess: 'Return without key allowed for the booking',
    returnOverrideError: 'Could not allow return without key',
    returnOffsiteButton: 'Return at another location',
    returnOffsiteHint: 'Allow the booking to be completed even if GPS does not show the car at the pickup site (delayed signal).',
    returnOffsiteTitle: 'Return at another location',
    returnOffsiteDescription:
      'The user can complete the booking even if GPS shows the car is outside the area. Provide a reason (logged).',
    returnOffsiteReasonPlaceholder: 'E.g. delayed GPS, the car is on site...',
    returnOffsiteConfirm: 'Allow other location',
    returnOffsiteSuccess: 'Return at another location allowed for the booking',
    returnOffsiteError: 'Could not allow return at another location',
    postReturnAccessButton: 'Grant 15 min access',
    postReturnAccessHint: 'Give the customer access to the vehicle again for 15 minutes (e.g. forgotten bag). Only within 60 minutes after return.',
    postReturnAccessConfirm:
      'Grant the customer 15 minutes of access again? This is allowed even if the vehicle is booked by someone else.',
    postReturnAccessSuccess: 'Customer has been granted 15 minutes of access again',
    postReturnAccessError: 'Could not grant new access',
    postReturnAccessTooLate: 'New access can only be granted within 60 minutes after return',
    postReturnAccessReturnedHint:
      'This booking is returned. Use "Grant 15 min access" for a forgotten bag — do not only change the end time.',
    termsTitle: 'Booking Terms',
    termsDescription: 'Manage booking terms for bookings',
    addTerms: 'New version',
    noTerms: 'No booking terms have been created yet.',
    termsVersion: 'Version',
    termsVersionTitle: 'Title',
    termsContent: 'Content',
    termsVersionPlaceholder: 'Version (e.g. 1.0)',
    termsTitlePlaceholder: 'Title',
    termsContentPlaceholder: 'Content (HTML or text)',
    termsActivate: 'Activate',
    termsDeactivate: 'Deactivate',
    termsActive: 'Active',
    vehiclePoolTitle: 'Vehicle Pool',
    vehiclePoolsTitle: 'Vehicle Pools',
    vehiclePoolDescription: 'Manage vehicle pools with automatic booking on key pickup',
    vehiclePoolsDescription: 'A vehicle pool is its own pickup location and cannot be booked. The cabinet can be shared with bookable locations. Checkouts still appear as normal bookings.',
    createPool: 'Create pool',
    addPool: 'Create vehicle pool',
    noPools: 'No vehicle pools yet',
    poolName: 'Pool name',
    poolNamePlaceholder: 'Vehicle pool name...',
    pickupLocation: 'Pickup location',
    cabinetViaPickupLocation: 'Cabinet via pickup location',
    cabinetViaPickupLocationHint: 'Choose the bookable location whose key cabinet the pool should share.',
    selectOrgFirstForUsers: 'Select an organization first to see users',
    selectKeysHint: 'Only keys that are not already on a bookable pickup location.',
    noCandidateKeys: 'No available pool keys for the selected cabinet and organization',
    maxHoursPerCheckout: 'Max hours per checkout',
    maxHours: 'Max hours',
    poolMembers: 'Members',
    members: 'Members',
    addMember: 'Add member',
    removeMember: 'Remove',
    selectUser: 'Select user',
    activeCheckouts: 'Active checkouts',
    noPoolsYet: 'No vehicle pools have been created yet',
    noMembers: 'No members yet',
    noMembersYet: 'No members in this pool',
    memberName: 'Name',
    memberEmail: 'Email',
    pinCode: 'PIN code',
    editPool: 'Edit pool',
    linkedCabinet: 'Linked cabinet',
    noCabinet: 'No cabinet linked — assign in Key Management',
    selectKeys: 'Which keys?',
    selectUsers: 'Users',
    noCabinetAtLocation: 'No cabinet linked to this pickup location',
    confirmDeletePool: 'Delete this vehicle pool? The key basket in KeyWin will also be removed.',
    cannotDeleteActiveCheckouts: 'Cannot delete — active checkouts exist',
    validFrom: 'Valid from',
    validTo: 'Valid to',
    poolWatchTitle: 'Missing details',
    poolWatchDescription: 'Pool checkouts where purpose or other required fields are not filled in.',
    poolWatchEmpty: 'No bookings are waiting for completion.',
    poolWatchComplete: 'Complete',
    poolWatchCompleteTitle: 'Complete booking',
    poolWatchCompleteHint: 'Fill in purpose and notes after the key was taken.',
    poolWatchSaved: 'The details have been saved.',
    poolWatchLoadError: 'Could not load the watch list.',
    poolWatchSaveError: 'Could not save the details.',
    needsPurposeFilter: 'Missing details only',
    needsPurposeBadge: 'Missing details',
    quickTitle: 'Quick booking',
    quickDescription: 'Book an available cabinet or Flinkey car for a guest who calls in. The PIN is emailed for cabinet cars.',
    quickCabinet: 'Key cabinet',
    quickAccessCabinet: 'Cabinet car',
    quickAccessFlinkey: 'Flinkey box',
    quickGuestName: 'Guest name',
    quickGuestPhone: 'Mobile',
    quickGuestEmail: 'Email',
    quickSubmit: 'Book and send PIN',
    quickSaved: 'Booking created',
    quickLoadError: 'Could not load available cars.',
    quickSaveError: 'Could not create the booking.',
    quickMissingFields: 'Fill in guest, vehicle and cabinet (for cabinet cars).',
    quickNoVehicles: 'No available cabinet or Flinkey cars in the selected slot.',
  },

  // Work Orders
  workOrders: {
    title: 'Work Orders',
    pageTitle: 'Work Order Management',
    description: 'Manage work orders, deliveries and home care assignments',
    
    // Actions
    create: 'Create work order',
    edit: 'Edit',
    delete: 'Delete',
    assign: 'Assign',
    unassign: 'Unassign',
    start: 'Start',
    complete: 'Complete',
    cancel: 'Cancel',
    optimize: 'Optimize route',
    
    // Types
    types: {
      SERVICE: 'Service job',
      DELIVERY: 'Delivery',
      HOMECARE: 'Home care',
      INSPECTION: 'Inspection',
      INSTALLATION: 'Installation',
    },
    
    // Status
    status: {
      DRAFT: 'Draft',
      PENDING: 'Pending',
      ASSIGNED: 'Assigned',
      OPTIMIZED: 'Optimized',
      IN_PROGRESS: 'In progress',
      ARRIVED: 'Arrived',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
      FAILED: 'Failed',
    },
    
    // Priority
    priority: {
      LOW: 'Low',
      NORMAL: 'Normal',
      HIGH: 'High',
      URGENT: 'Urgent',
    },
    
    // Table columns
    colType: 'Type',
    colStatus: 'Status',
    colStops: 'Stops',
    // Filter
    allStatuses: 'All statuses',
    allTypes: 'All types',
    searchPlaceholder: 'Search order number, title, customer...',
    // Fields
    orderNumber: 'Order number',
    customer: 'Customer',
    customerName: 'Customer name',
    customerPhone: 'Phone',
    customerEmail: 'Email',
    jobTitle: 'Title',
    jobDescription: 'Description',
    notes: 'Notes',
    plannedDate: 'Planned date',
    timeWindow: 'Time window',
    estimatedDuration: 'Estimated duration',
    assignedVehicle: 'Assigned vehicle',
    assignedDriver: 'Assigned driver',
    
    // Stops
    stops: 'Stops',
    addStop: 'Add stop',
    stopAddress: 'Address',
    stopContact: 'Contact person',
    stopTimeWindow: 'Time window',
    stopDuration: 'Estimated time',
    
    // Routes
    routes: 'Routes',
    route: 'Route',
    routeDate: 'Date',
    routeVehicle: 'Vehicle',
    routeDriver: 'Driver',
    routeStatus: 'Status',
    routeDistance: 'Distance',
    routeDuration: 'Duration',
    optimizeRoute: 'Optimize route',
    routeOptimized: 'Route optimized',
    routeNotOptimized: 'Route not optimized',
    
    // Optimization
    optimization: {
      title: 'Route Optimization',
      optimize: 'Optimize',
      optimizing: 'Optimizing...',
      freedTime: 'Freed time',
      reducedDistance: 'Reduced distance',
      newOrder: 'New order',
      originalOrder: 'Original order',
      vroomAvailable: 'VROOM available',
      vroomUnavailable: 'VROOM unavailable',
    },
    
    // Stats
    stats: {
      total: 'Total',
      today: 'Today',
      completed: 'Completed',
      pending: 'Pending',
      overdue: 'Overdue',
      avgTime: 'Avg time',
    },
    
    // Messages
    noOrders: 'No work orders found',
    selectVehicle: 'Select vehicle',
    selectDriver: 'Select driver',
    confirmDelete: 'Are you sure you want to delete this work order?',
    deleteSuccess: 'Work order deleted',
    createSuccess: 'Work order created',
    updateSuccess: 'Work order updated',
    assignSuccess: 'Work order assigned',
    completeSuccess: 'Work order completed',
    cancelSuccess: 'Work order cancelled',
    optimizeSuccess: 'Route optimized',
    delivery: {
      title: 'Package Delivery',
      subtitle: 'Work orders for packages and deliveries',
      recipient: 'Recipient',
      address: 'Address',
      stop: 'Stop',
      searchPlaceholder: 'Search order number, recipient, address...',
      newDelivery: 'New delivery',
      noDeliveries: 'No deliveries found',
    },
    craftsman: {
      title: 'Craftsman',
      subtitle: 'Work orders for craftsmen and service',
      customer: 'Customer',
      address: 'Address',
      time: 'Time',
      searchPlaceholder: 'Search order number, customer, address...',
      newWorkOrder: 'New work order',
      noWorkOrders: 'No work orders found',
      assigned: 'Assigned',
      unassigned: 'Unassigned',
      assignPlaceholder: 'Search person...',
      assignSuccess: 'Work order assigned',
      assignError: 'Could not assign',
      edit: 'Edit',
      detail: 'Details / execution',
      viewList: 'List',
      viewGantt: 'Gantt',
      viewCalendar: 'Calendar',
      ordersShort: 'orders',
    },

    form: {
      newTitle: 'New work order',
      editTitle: 'Edit work order',
      title: 'Title',
      titleRequired: 'Title is required',
      organization: 'Organization',
      orgRequired: 'Select an organization',
      serviceType: 'Service type',
      serviceTypeRequired: 'Select or add a service type',
      serviceTypeEmpty: 'No types yet – choose Add…',
      serviceTypeAdd: 'Add…',
      serviceTypeSelectOrgFirst: 'Select organization first',
      serviceTypeNamePlaceholder: 'Service type name',
      serviceTypeNameRequired: 'Enter a service type name',
      serviceTypeCreateError: 'Could not create service type',
      customerName: 'Customer name',
      customerPhone: 'Phone',
      address: 'Address',
      postalCode: 'Postal code',
      city: 'City',
      plannedDate: 'Planned date',
      timeWindowStart: 'Time window from',
      timeWindowEnd: 'Time window to',
      description: 'Description',
    },

    detail: {
      assignment: 'Assignment',
      person: 'Person',
      vehicleOptional: 'Vehicle (optional)',
      noVehicle: 'No vehicle',
      assign: 'Assign',
      gpsHint: 'With an assigned vehicle, travel time and distance are recorded automatically from the vehicle GPS. Without a vehicle, time is logged manually in the app.',
      start: 'Start',
      complete: 'Complete',
      accounting: 'Accounting',
      actualStart: 'Actual start',
      actualEnd: 'Actual end',
      totalTime: 'Total time',
      travelTime: 'Travel time',
      workTime: 'Work time',
      distance: 'Distance',
      manualTime: 'Manual time logging',
      segmentType: 'Type',
      minutes: 'Minutes',
      segments: 'Time segments',
      completionNotes: 'Completion notes',
    },

    import: {
      title: 'Import',
      selectFile: 'Select file (Excel/CSV)',
      dropHint: 'Click to select an Excel or CSV file',
      emptyFile: 'The file contains no rows',
      parseError: 'Could not read the file',
      mapping: 'Column mapping',
      rows: 'rows',
      row: 'Row',
      ignore: 'Ignore',
      import: 'Import',
      importing: 'Importing...',
      created: 'Created',
      failed: 'Failed',
      titleMappingRequired: 'Map the Title column',
      field: {
        title: 'Title',
        customerName: 'Customer name',
        customerPhone: 'Phone',
        customerEmail: 'Email',
        address: 'Address',
        postalCode: 'Postal code',
        city: 'City',
        plannedDate: 'Planned date',
        timeWindowStart: 'Time from',
        timeWindowEnd: 'Time to',
        estimatedDurationMinutes: 'Estimated time (min)',
        notes: 'Notes',
      },
    },
  },

  // ============================================================
  // Service Agreements
  // ============================================================
  serviceAgreements: {
    title: 'Service Agreements',
    subtitle: 'Recurring service jobs with intervals',
    new: 'New service agreement',
    newTitle: 'New service agreement',
    editTitle: 'Edit service agreement',
    empty: 'No service agreements',
    titleField: 'Title',
    titlePlaceholder: 'e.g. Exhaust fan inspection',
    customerSection: 'Customer and service address',
    serviceAddress: 'Service address',
    billingSection: 'Billing address',
    billingName: 'Billing name',
    billingAddress: 'Billing address',
    billingReference: 'Invoice reference',
    scheduleSection: 'Schedule and price',
    intervalMonths: 'Interval (months)',
    monthsShort: 'mo',
    nextDueDate: 'Next due date',
    pricingType: 'Pricing',
    hourly: 'Hourly',
    fixed: 'Fixed price',
    estimatedMinutes: 'Estimated time (min)',
    estimatedHours: 'Estimated time (hours)',
    estimatedHoursHint: 'How long the job is expected to take, e.g. 1.5',
    hourlyRate: 'Hourly rate (SEK/hour)',
    fixedPrice: 'Fixed price',
    dueWidgetTitle: 'Due within 30 days',
    noneDue: 'No agreements due within 30 days',
    dueInDays: 'Due in {{days}} days',
    overdueBy: 'Overdue by {{days}} days',
    assign: 'Assign person',
    assignTitle: 'Assign and create work order',
    createWorkOrder: 'Create work order',
    personRequired: 'Select a person',
    confirmDelete: 'Delete this service agreement?',
  },

  // ============================================================
  // Homecare Module
  // ============================================================
  homecare: {
    title: 'Home Care',
    subtitle: 'Route planning and scheduling for home care',
    date: 'Date',

    tabs: {
      planning: 'Planning',
      gantt: 'Gantt Chart',
      map: 'Map',
      roster: 'Work Roster',
      clients: 'Clients',
      staff: 'Staff',
    },

    planning: {
      visits: 'Visits',
      requiresMultiStaff: 'require 2+ staff',
      teams: 'Teams',
      pairTeams: 'pair teams',
      soloTeams: 'solo teams',
      pairTeam: 'Pair team',
      vehicles: 'Vehicles',
      vehicleShortage: 'missing',
      vehiclesOk: 'Sufficient',
      distance: 'Distance',
      suggestTeams: 'Suggest Teams',
      optimizeRoutes: 'Optimize Routes',
      suggestedTeams: 'Suggested Teams',
      optimizationResult: 'Optimization Result',
      unassignedVisits: 'Unassigned Visits',
      teamError: 'Could not suggest teams',
      optimizeError: 'Could not optimize routes',
      timeWindow: 'Time Window',
      duration: 'Duration',
      staff: 'Staff',
      priority: 'Priority',
      strategy: 'Strategy',
      strategyBalanced: 'Balanced',
      strategyBalancedDesc: 'Optimizes with competence requirements. Some visits may be unassigned.',
      strategyCoverAll: 'Cover All Visits',
      strategyCoverAllDesc: 'All visits assigned regardless of competence. Warnings shown.',
      strategyCalculateExtra: 'Calculate Resource Needs',
      strategyCalculateExtraDesc: 'Shows how many extra teams are needed for full coverage.',
      optimizationRuns: 'Optimization History',
      noRuns: 'No saved optimizations',
      selectRun: 'Select this plan',
      deleteRun: 'Delete',
      selectedRun: 'Active',
      archivedRun: 'Archived',
      draftRun: 'Draft',
      runStrategy: 'Strategy',
      runCreated: 'Created',
      extraTeamsNeeded: 'Extra teams needed',
      competenceWarnings: 'Competence Warnings',
      competenceWarningDetail: 'missing competence',
      missingCompetences: 'Missing competence',
      reassignVisit: 'Move to',
      reassignSuccess: 'Visit reassigned',
      reassignError: 'Could not reassign visit',
      reoptimize: 'Re-optimize',
      manualChanges: 'Manual changes made',
    },
    vehicleSource: 'Get vehicles from',
    selectVehicleSource: 'Select vehicle organization',

    gantt: {
      team: 'Team',
      summary: 'Summary',
      noData: 'No Gantt chart available',
      noDataHint: 'Run optimization in the planning tab first',
      travel: 'Travel',
      visit: 'At client',
      break: 'Break',
      unplanned: 'Unplanned',
      totalTeams: 'Teams',
      totalVisits: 'Visits',
      totalTravel: 'Distance',
      visitsLabel: 'visits',
      planned: 'planned time',
    },

    roster: {
      staff: 'Staff',
      week: 'Week',
      copyWeek: 'Copy to next week',
      weekCopied: 'Week schedule copied',
      confirmCopyWeek: 'Copy the schedule to next week? Existing entries in the target week will not be affected.',
      nothingToCopy: 'Nothing to copy – the schedule for this week is empty or next week already has the same entries.',
      copyFailed: 'Could not copy the schedule',
    },

    clients: {
      searchPlaceholder: 'Search clients...',
      add: 'New Client',
      newClient: 'New Client',
      editClient: 'Edit Client',
      name: 'Name',
      firstName: 'First Name',
      lastName: 'Last Name',
      personalNumber: 'Personal Number',
      address: 'Address',
      postalCode: 'Postal Code',
      city: 'City',
      phone: 'Phone',
      doorCode: 'Door Code',
      floor: 'Floor',
      specialInstructions: 'Special Instructions',
      emergencyContact: 'Emergency Contact',
      emergencyPhone: 'Emergency Phone',
      competences: 'Competence Requirements',
      noClients: 'No clients found',
      saved: 'Client saved',
      coordinates: 'Coordinates',
      allocatedHours: 'Allocated care time',
      hoursPerWeek: 'hours/week',
    },

    careNeeds: {
      title: 'Care Needs',
      add: 'Add care need',
      edit: 'Edit care need',
      name: 'Name',
      duration: 'Duration (min)',
      timeWindowStart: 'Time window start',
      timeWindowEnd: 'Time window end',
      frequency: 'Frequency',
      weekdays: 'Weekdays',
      requiredStaff: 'Required staff',
      priority: 'Priority',
      description: 'Description',
      competences: 'Competence requirements',
      mandatory: 'Mandatory',
      noNeeds: 'No care needs added',
      saved: 'Care need saved',
      deleted: 'Care need deleted',
      confirmDelete: 'Do you want to delete this care need?',
      generateVisits: 'Generate visits',
      generateVisitsDesc: 'Create visits from care needs for a period',
      fromDate: 'From date',
      toDate: 'To date',
      generated: 'visits generated',
      noVisitsGenerated: 'No new visits to generate',
      frequencies: {
        DAILY: 'Daily',
        WEEKDAYS: 'Weekdays',
        WEEKLY: 'Weekly',
        BIWEEKLY: 'Biweekly',
        MONTHLY: 'Monthly',
        CUSTOM: 'Custom',
      },
      priorities: {
        LOW: 'Low',
        NORMAL: 'Normal',
        HIGH: 'High',
        URGENT: 'Urgent',
      },
      weekdayNames: {
        '1': 'Mon',
        '2': 'Tue',
        '3': 'Wed',
        '4': 'Thu',
        '5': 'Fri',
        '6': 'Sat',
        '7': 'Sun',
      },
    },

    staff: {
      searchPlaceholder: 'Search staff...',
      startsFromHome: 'Starts from home',
      startsFromOffice: 'Starts from office',
      startType: 'Start position',
      homeAddress: 'Home address',
      homeLat: 'Latitude (home)',
      homeLng: 'Longitude (home)',
      shiftStart: 'Shift start',
      shiftEnd: 'Shift end',
      workHours: 'Work hours',
      competences: 'Competences',
      editStaff: 'Edit Staff',
      noCompetences: 'No competences',
      noStaff: 'No staff found',
      saved: 'Staff saved',
    },
  },

  // Fuel Management
  charging: {
    title: 'Charging',
    description: 'EV charging session reporting (kWh) and location',
    tab: 'Charging',
    month: 'Month',
    organization: 'Organization',
    allOrganizations: 'All organizations',
    selectVehicle: 'Vehicle',
    vehiclePlaceholder: 'All vehicles – search or select...',
    export: 'Export',
    estimateNote: 'kWh is estimated from the battery state of charge (SoC), not a measured wall value. An accurate tax basis requires a metering charge box.',
    loadError: 'Could not load charging sessions',
    exportError: 'Could not export',
    noData: 'No charging sessions for the period',
    location: {
      label: 'Location',
      all: 'All locations',
      home: 'Home',
      work: 'Work',
      public: 'Public',
      unknown: 'Unknown',
    },
    summary: {
      homeKwh: 'Home charging (kWh)',
      totalKwh: 'Total charged (kWh)',
      sessions: 'Sessions',
    },
    table: {
      vehicle: 'Vehicle',
      start: 'Start',
      end: 'End',
      soc: 'State of charge',
      wallKwh: 'kWh (wall)',
      location: 'Location',
    },
    config: {
      description: 'Configure battery capacity, charging efficiency and home/work locations for accurate charging reports. If Car.info has no value you can enter kWh manually — Car.info overwrites when available.',
      batteryCapacity: 'Battery capacity (kWh)',
      batteryFromCarInfo: 'Use looked-up value from Car.info: {{value}} kWh',
      batterySourceManual: 'Source: entered manually (overwritten when Car.info has kWh)',
      batterySourceCarInfo: 'Source: Car.info',
      efficiency: 'Charging efficiency (battery/wall, 0–1)',
      homeLatitude: 'Home charging latitude',
      homeLongitude: 'Home charging longitude',
      homeRadius: 'Home charging radius (m)',
      homeLocation: 'Home location',
      workLocation: 'Work location',
      pickOnMap: 'Pick on map',
      changeOnMap: 'Change on map',
      clear: 'Clear',
      address: 'Address',
      addressPlaceholder: 'Click the map to fetch the address',
      radius: 'Radius',
      pickerHelp: 'Search an address or click the map/drag the marker to choose the location. The address is fetched automatically.',
      pickerNoPoint: 'No location selected',
      searchPlaceholder: 'Search address or place…',
      publicNote: 'Charging outside home and work locations is automatically classified as public.',
    },
  },
  fuelCards: {
    title: 'Fuel cards',
    description: 'Register of fuel cards, OKQ8 numbers and vehicle linkage',
    create: 'New card',
    createTitle: 'Register fuel card',
    editTitle: 'Edit fuel card',
    fuelCompany: 'Fuel company',
    selectCompany: 'Select fuel company…',
    cardNumber: 'Card number',
    invoiceNumber: 'Invoice number',
    embossedNumber: 'Embossed number',
    cardName: 'Card name',
    pinCode: 'PIN',
    expiresAt: 'Expiry date',
    status: 'Status',
    statusActive: 'Active',
    statusBlocked: 'Blocked',
    statusCancelled: 'Cancelled',
    notes: 'Notes',
    vehicle: 'Vehicle',
    selectVehicle: 'Select vehicle…',
    missingOrganization: 'The card has no organization, so it cannot be linked to a vehicle.',
    notLinked: 'Not linked',
    link: 'Link',
    unlink: 'Unlink',
    linkTitle: 'Link card to vehicle',
    cancel: 'Cancel card',
    expiringFilter: 'Expires within 60 days',
    noCards: 'No fuel cards found',
    loadError: 'Could not load fuel cards',
    createSuccess: 'Fuel card registered',
    saveSuccess: 'Fuel card updated',
    saveError: 'Could not save fuel card',
    linkSuccess: 'Card linked to vehicle',
    linkError: 'Could not link card',
    unlinkSuccess: 'Card unlinked',
    unlinkError: 'Could not unlink card',
    confirmUnlink: 'Unlink this card from the vehicle? The card remains in the register.',
    cancelSuccess: 'Card cancelled',
    cancelError: 'Could not cancel card',
    confirmCancel: 'Cancel this fuel card?',
    historyTitle: 'Assignment history',
    historyVehicle: 'Vehicle',
    historyFrom: 'Linked from',
    historyTo: 'Unlinked',
    historyUnlinked: 'Unassigned',
    historyOngoing: 'Ongoing',
    historyCurrent: 'Current',
    historyPast: 'Ended',
    historyEmpty: 'No assignment history',
    historyLoadError: 'Could not load assignment history',
  },
  fuel: {
    title: 'Fuel Management',
    description: 'Invoices, fuel cards and card-vehicle linkage',
    invoices: {
      tab: 'Invoices',
      invoiceNumber: 'Invoice Number',
      invoiceDate: 'Invoice Date',
      transactionCount: 'Count',
      totalExclVat: 'Excl. VAT',
      vat: 'VAT',
      total: 'Total',
      noInvoices: 'No invoices found',
      loadError: 'Could not load invoices',
      date: 'Date',
      station: 'Station',
      product: 'Product',
      quantity: 'Liters',
      priceExclVat: 'Price excl. VAT',
      vatAmount: 'VAT',
      company: 'Company',
    },
    duplicate: {
      badge: 'Duplicate',
      tooltip: 'The same fill already exists from another company — possible double invoicing',
      tooltipCompany: 'The same fill already exists at {{company}} — possible double invoicing',
    },
    cards: {
      tab: 'Fuel Cards',
      cardNumber: 'Card Number',
      invoiceNumber: 'Invoice number',
      embossedNumber: 'Embossed number',
      cardName: 'Card name',
      pinCode: 'PIN',
      fuelCompany: 'Fuel Company',
      linkedVehicle: 'Linked Vehicle',
      linkedSince: 'Linked Since',
      notLinked: 'Not linked',
      link: 'Link',
      unlink: 'Unlink',
      noCards: 'No fuel cards found',
      loadError: 'Could not load cards',
      confirmUnlink: 'Are you sure you want to unlink this card?',
      unlinkSuccess: 'Card unlinked',
      unlinkError: 'Could not unlink card',
      linkModal: {
        title: 'Link Card to Vehicle',
        vehicle: 'Vehicle',
        validFrom: 'Linked from',
        selectVehicle: 'Select vehicle...',
        pinCode: 'PIN',
        pinCodePlaceholder: 'PIN',
        save: 'Link',
        success: 'Card linked to vehicle',
        error: 'Could not link card',
      },
      history: {
        title: 'Linkage History',
        vehicle: 'Vehicle',
        from: 'From',
        to: 'To',
        active: 'Active',
        inactive: 'Ended',
        noHistory: 'No history',
        loadError: 'Could not load history',
      },
    },
    vehicleCard: {
      noCards: 'No fuel cards have been linked to this vehicle',
      loadError: 'Could not load fuel cards',
      noTransactions: 'No fuel transactions found for selected period',
      outsidePeriod: 'No transactions in the selected period. {{count}} transactions exist outside it — widen the dates.',
      loadTxError: 'Could not load fuel transactions',
      totalLiters: 'Total liters',
      totalCost: 'Total cost',
      addCard: 'Add card',
      cardNumber: 'Card number',
      invoiceNumber: 'Invoice number',
      embossedNumber: 'Embossed number',
      okq8BothRequired: 'OKQ8 requires both invoice number and embossed number',
      validFrom: 'Valid from',
      linkSuccess: 'Fuel card linked',
      linkError: 'Could not link fuel card',
      unlinkSuccess: 'Fuel card unlinked',
      unlinkError: 'Could not unlink fuel card',
      confirmUnlink: 'Are you sure you want to unlink this card?',
      unlink: 'Unlink',
    },
  },

  // Module translations (for SystemLandingPage - maps module.name to translated title/description)
  modules: {
    addFavorite: 'Add to favorites',
    removeFavorite: 'Remove from favorites',
    // Administration
    roles: { title: 'Roles', description: 'Roles and permissions' },
    organizations: { title: 'Organizations', description: 'Manage organizations' },
    users: { title: 'Users', description: 'User management' },
    translations: { title: 'Translations', description: 'Translations and language' },
    imports: { title: 'Import', description: 'Import data from files' },
    packages: { title: 'Packages', description: 'Manage packages and subscriptions' },
    devices: { title: 'Devices', description: 'Manage devices' },
    'device-management': { title: 'Device Management', description: 'Advanced device management' },
    'sim-cards': { title: 'SIM Cards', description: 'Manage SIM cards, view usage and create reports via Jasper API' },
    geofences: { title: 'Geofences', description: 'Manage geofences' },
    'geofence-events': { title: 'Geofence Events', description: 'View geofence events' },
    support: { title: 'Support', description: 'Support functions' },
    // SysAdmin
    'ai-log': { title: 'AI Log', description: 'View AI logs' },
    'data-migration': { title: 'Data Migration', description: 'Migrate data between systems' },
    'device-commands': { title: 'Device Commands', description: 'Send commands to devices' },
    'audit-log': { title: 'Audit Log', description: 'View audit logs' },
    'general-audit-log': { title: 'Change Log', description: 'View change log' },
    'error-log': { title: 'Error Log', description: 'View error logs' },
    'eco-settings': { title: 'ECO Settings', description: 'Configure ECO settings' },
    'system-monitor': { title: 'System Monitor', description: 'Monitor system status' },
    'slow-queries': { title: 'Slow Queries', description: 'View slow database queries' },
    'data-replay': { title: 'Data Replay', description: 'Replay GPS data' },
    'trip-validation': { title: 'Trip Validation', description: 'Validate trips' },
    'import-sources': { title: 'Import Sources', description: 'Manage import sources' },
    'sso-log': { title: 'SSO log', description: 'Failed and abandoned SSO attempts, by customer and device' },
    'gps-data-lag': { title: 'GPS dump queue', description: 'Devices actively sending lagging GPS data; flush via SMS' },
    'api-metrics': { title: 'API metrics', description: 'p50/p95 per endpoint, API heap, Redis and GPS worker' },
    // Reports
    trips: { title: 'Trips', description: 'View and analyze trips for your vehicles' },
    'eco-driving': { title: 'ECO Driving', description: 'Analyze driving habits and environmental impact' },
    'speed-compliance': { title: 'Speed Compliance', description: 'Analyze speed compliance per organization and vehicle' },
    'energy-consumption': { title: 'Energy Consumption', description: 'Analyze fuel consumption per organization and vehicle' },
    'vehicle-wear': { title: 'Vehicle Wear', description: 'Analyze how driving behavior affects vehicle wear' },
    underutilized: { title: 'Underutilized Vehicles', description: 'Identify vehicles with low utilization' },
    co2: { title: 'CO2 Emissions', description: 'Calculate and analyze CO2 emissions' },
    'geofence-zones': { title: 'Geofence Zone Report', description: 'Analyze how often vehicles are within zones' },
    'driving-log': { title: 'Driving Log', description: 'Driving log according to tax authority guidelines' },
    odometer: { title: 'Odometer Report', description: 'View odometer reading per vehicle at specific date' },
    rental: { title: 'Rental Report', description: 'Complete report for rentals with trips, costs and status' },
    'rental-report': { title: 'Rental Report', description: 'Rental statistics and reports' },
    'stop-time': { title: 'Stop Time Analysis', description: 'Analyze vehicle stop times' },
    'route-analysis': { title: 'Route Analysis', description: 'Route optimization and analysis' },
    'vehicle-kpi': { title: 'Vehicle KPI', description: 'Key performance indicators per vehicle' },
    dtc: { title: 'DTC Report', description: 'View and analyze diagnostic trouble codes (DTC) per vehicle' },
    'dtc-report': { title: 'DTC Report', description: 'Diagnostic trouble codes' },
    // CRM
    customers: { title: 'Customers', description: 'Manage customers' },
    projects: { title: 'Projects', description: 'Manage projects' },
    procurements: { title: 'Procurements', description: 'Manage procurements' },
    pipeline: { title: 'Pipeline', description: 'Sales pipeline' },
    economy: { title: 'Economy', description: 'Economy overview' },
    invoices: { title: 'Invoices', description: 'Manage invoices' },
    pricelist: { title: 'Price List', description: 'Manage price list' },
    // Deque (DB module names)
    organization: { title: 'Organization', description: 'Manage organization structure' },
    fleet: { title: 'Fleet', description: 'Vehicle overview and management' },
    drivers: { title: 'Drivers', description: 'Driver management' },
    // Deque (legacy/alias keys)
    'fleet-overview': { title: 'Vehicle Fleet', description: 'Vehicle overview and management' },
    'live-tracking': { title: 'Live Map', description: 'Real-time vehicle tracking' },
    'track-playback': { title: 'Track Playback', description: 'View historical routes' },
    'map-services': { title: 'Map Services', description: 'Map, tracking, geofence and heatmap' },
    'fuel-management': { title: 'Fuel Management', description: 'Invoices, fuel cards and card-vehicle linkage' },
    'fuel-cards': { title: 'Fuel cards', description: 'Register of fuel cards and vehicle linkage' },
    assignments: { title: 'Assignments', description: 'Manage task assignments to people' },
    'vehicle-categories': { title: 'Vehicle categories', description: 'Manage selectable vehicle categories' },
    'equipment-tags': { title: 'Vehicle equipment', description: 'Manage vehicle equipment per top node' },
    'replacement-chain': { title: 'Replacement chain', description: 'See which vehicles were replaced in a chain' },
    'alternative-fuels': { title: 'Alternative fuels', description: 'Manage selectable alternative fuels per top node' },
    'usage-statuses': { title: 'Vehicle status', description: 'Manage selectable vehicle statuses per top node' },
    'service-overview': { title: 'Service', description: 'Service overview and management' },
    heatmap: { title: 'Heatmap', description: 'Heatmap of vehicle positions' },
    'heatmap-import': { title: 'Heatmap Import', description: 'Import CSV data for heatmap analysis' },
    'crash-detection': { title: 'Crash Detection', description: 'Crash events from devices with Crash Trace (AVL 257)' },
    'ai-analysis': { title: 'AI Analysis', description: 'Analyze vehicles and fleet with AI' },
    // Reports (DB module names)
    eco: { title: 'ECO Driving', description: 'Environment and driving behavior' },
    fuel: { title: 'Fuel', description: 'Fuel reports' },
    activity: { title: 'Activity', description: 'Activity logs' },
    // Analysis (DB module names)
    statistics: { title: 'Statistics', description: 'Statistical overview' },
    trends: { title: 'Trends', description: 'Trend analysis and mileage versus lease contracts' },
    benchmarks: { title: 'Benchmarks', description: 'Benchmarking' },
    // SysAdmin (DB module names)
    overview: { title: 'Overview', description: 'System status' },
    replay: { title: 'Data Replay', description: 'Replay GPS data' },
    logs: { title: 'Logs', description: 'System logs' },
    // Booking
    'pickup-locations': { title: 'Pickup Locations', description: 'Under Booking: locations where vehicles are picked up and returned' },
    suppliers: { title: 'Suppliers', description: 'Suppliers e.g. car rental companies' },
    destinations: { title: 'Destinations', description: 'Predefined destinations for bookings' },
    calendar: { title: 'Calendar', description: 'Booking calendar' },
    'bookings-search': { title: 'Customer service', description: 'Administer and support bookings' },
    'booking-optimization': { title: 'Booking optimization', description: 'AI optimization of a day\u2019s bookings per pool' },
    requests: { title: 'Booking Requests', description: 'Manage booking requests' },
    'vehicle-replacement': { title: 'Vehicle replacement', description: 'Replace vehicles in a pool, take out of service, and reassign bookings' },
    'own-car-trip': { title: 'Trip with own car', description: 'Register and manage business trips with a private car' },
    'own-car-trips': { title: 'Trip with own car', description: 'Register and manage business trips with a private car' },
    'collision-bookings': { title: 'Collision bookings', description: 'Bookings without an assigned vehicle after pool or vehicle changes' },
    // Workshop (DB module names)
    service: { title: 'Service', description: 'Service intervals and reminders' },
    repairs: { title: 'Repairs', description: 'Repair history and costs' },
    inspections: { title: 'Inspections', description: 'Inspection status and reminders' },
    // Key Management (DB module names)
    'key-cabinet': { title: 'Key Cabinet', description: 'Overview of key cabinets and keys' },
    'key-log': { title: 'Key History', description: 'Log of key checkouts and returns' },
    // Homecare
    'homecare-planning': { title: 'Route Planning', description: 'Homecare route optimization' },
    'homecare-clients': { title: 'Clients', description: 'Homecare clients' },
    'homecare-staff': { title: 'Staff', description: 'Homecare staff' },
    // Systems
    administration: { title: 'Administration', description: 'System administration' },
    sysadmin: { title: 'System Administration', description: 'Advanced system settings' },
    reports: { title: 'Reports', description: 'Reports and statistics' },
    crm: { title: 'CRM', description: 'Customer Relationship Management' },
    deque: { title: 'Deque', description: 'Fleet management' },
    booking: { title: 'Booking', description: 'Vehicle booking' },
    homecare: { title: 'Homecare', description: 'Homecare planning' },
    workshop: { title: 'Workshop', description: 'Workshop and maintenance' },
    keymanagement: { title: 'Key Management', description: 'Key cabinet management' },
    workorder: { title: 'Work Orders', description: 'Work orders and assignments' },
    analysis: { title: 'Analysis', description: 'Statistics and analysis' },
    'cost-centers': { title: 'Cost Centers', description: 'Manage cost centers' },
    'old-booking': { title: 'Old Booking', description: 'Historical booking data (temporary)' },
    'cabinet-sync': { title: 'Cabinet sync', description: 'What KeyWin sends to cabinets (global admin)' },
    'carinfo-filter': { title: 'Car.info Filter', description: 'Filter configuration for car data' },
  },

  // System Landing Page
  systemPage: {
    loading: 'Loading system...',
    errorTitle: 'Error loading system',
    tryAgain: 'Try again',
    systemNotFound: 'System not found',
    systemNotFoundDescription: 'The system "{name}" could not be found in the database.',
    backToStart: 'Back to start page',
    noModules: 'No modules found for this system.',
    comingSoon: 'Coming soon',
    plannedFeatures: 'Planned features',
    underDevelopment: 'This module is under active development.',
    notFoundOrNoAccess: 'The system "{name}" was not found or you do not have permission.',
    notLoggedIn: 'You are not logged in or your session has expired. You will be redirected to login.',
    noPermission: 'You do not have permission to view this information.',
    serverError: 'Server error. Please try again later.',
    unexpectedError: 'An unexpected error occurred while loading system data',
    noSystemName: 'No system name specified',
    apiError: 'API returned an error',
    // Coming Soon features
    comingSoonBooking: {
      feature1: 'Calendar view for bookings',
      feature2: 'Requests and approvals',
      feature3: 'Conflict management',
      feature4: 'Driver notifications',
      feature5: 'Integration with vehicle fleet',
    },
    comingSoonWorkshop: {
      feature1: 'Service intervals and reminders',
      feature2: 'Repair history',
      feature3: 'Inspection status',
      feature4: 'Cost tracking',
      feature5: 'Workshop booking',
    },
    comingSoonKeymanagement: {
      feature1: 'Key cabinet overview',
      feature2: 'In/out logging',
      feature3: 'Access management',
      feature4: 'Alarm functions',
      feature5: 'Integration with booking system',
    },
    comingSoonWorkorder: {
      feature1: 'Create work orders',
      feature2: 'Assign to driver/vehicle',
      feature3: 'Status tracking',
      feature4: 'Prioritization and deadline',
      feature5: 'Documentation and attachments',
    },
    comingSoonAnalysis: {
      feature1: 'Statistical overview',
      feature2: 'Trend analysis over time',
      feature3: 'Comparisons between vehicles',
      feature4: 'Export reports',
      feature5: 'Custom dashboards',
    },
  },

  // Vehicle attachments
  vehicleAttachments: {
    attachments: 'Attachments',
    uploadButton: 'Upload',
    category: 'Category',
    insurance: 'Insurance',
    inspection: 'Inspection',
    damage: 'Damage',
    invoice: 'Invoice',
    contract: 'Contract',
    service: 'Service',
    other: 'Other',
    clickToPreview: 'Click to preview',
    download: 'Download',
    deleteFile: 'Delete',
    preview: 'Preview',
    closePreview: 'Close',
  },

  // Eco settings
  ecoSettings: {
    couldNotSave: 'Could not save settings',
    confirmDelete: 'Are you sure you want to delete these organization-specific settings?',
    couldNotDelete: 'Could not delete settings',
    globalDefaults: 'Global defaults',
    globalDefaultsOption: 'Global defaults',
    hasOwnSettings: 'Has own settings',
    usesGlobal: 'Uses global',
    orgsWithOwnSettings: 'Organizations with own settings:',
    removeOrgSettings: 'Remove organization-specific settings',
    hardAccelDescription: 'Thresholds for detecting hard acceleration...',
    defaultSpeedOverLimit: 'Default: 10% over speed limit',
    smoothDrivingDescription: 'Thresholds for rewarding economical and eco-friendly driving.',
    defaultSmoothAccel: 'Default: 1.5 m/s\u00B2 for smooth driving',
    defaultSmoothBrake: 'Default: 2.0 m/s\u00B2 for smooth braking',
    defaultMinSpeed: 'Default: 10 km/h (stationary not counted)',
    defaultIdleTime: 'Default: 120 seconds (2 minutes) continuous',
  },

  // Geofences page
  geofencesPage: {
    exportToCsv: 'Export to CSV',
    clearSelection: 'Clear selection',
    noGeofences: 'No geofences created',
    hide: 'Hide',
    show: 'Show',
    editGeofence: 'Edit',
    deleteGeofence: 'Delete',
    clickMapToAddPoints: 'Click on the map to add points',
    minPointsRequired: 'At least 3 points are required for a polygon',
    globalAllOrgs: 'Global (all organizations)',
    specificOrg: 'Specific organization',
    selectOrg: 'Select organization',
    hideCoordinatesInArea: 'Hide coordinates within area',
    tollArea: 'Toll area',
    defaultPricePlaceholder: 'Default price if no time rule matches',
  },

  // Dashboard widgets
  widgets: {
    remove: 'Remove',
    addWidget: 'Add Widget',
    selectColumn: 'Select column:',
    available: 'Available',
    lowBattery: 'Low battery',
    vehicleStatus: 'Vehicle Status',
    vehicleStatusSubtitle: 'Vehicles by status',
    noUsageStatus: 'No status',
    noVehicles: 'No vehicles',
    serviceDue: 'Service Due',
    overdue: 'overdue',
    noServiceDue: 'No service due',
    servicePointsNeedAction: 'service points need action',
    overdueLabel: 'Overdue',
    soonLabel: 'Soon',
    bookedLabel: 'Booked',
    allBatteriesOk: 'All batteries OK',
    move: 'Move',
    alreadyAdded: 'Already on dashboard',
    alreadyAddedHint: 'Close this dialog and check your dashboard — the widget is already there. Remove it in Edit mode if you want to place it in another column.',
    sectionAvailable: 'Can be added',
    sectionOnDashboard: 'Already on your dashboard',
    notInPackage: 'Not enabled for your organization under Services',
    comingSoon: 'Coming soon',
    columns: {
      left: 'Left',
      center: 'Center',
      right: 'Right',
    },
  },

  // Users management
  usersManagement: {
    title: 'User Management',
    usersCount: 'users',
    driversLicense: 'Drivers license:',
    employeeId: 'Employee ID:',
    costCenter: 'Cost center:',
    createUser: 'Create user',
    editUser: 'Edit user',
    downloadTemplate: 'Download template',
    importUsers: 'Import users',
    importTitle: 'Import users from Excel',
    importDescription: 'Upload a filled-in Excel template to create users, drivers and vehicle assignments in one step.',
    importSelectOrg: 'Select organization to import into',
    importUploadLabel: 'Select Excel file (.xlsx)',
    importUploadHint: 'Download the template, fill it in and upload it here.',
    importPreviewTitle: 'Preview',
    importPreviewRows: 'rows found',
    importRun: 'Run import',
    importRunning: 'Importing...',
    importResultTitle: 'Import result',
    importResultSuccess: 'users created',
    importResultFailed: 'rows failed',
    importErrorsTitle: 'Failed rows',
    importPasswordsTitle: 'Generated passwords',
    importPasswordsHint: 'Copy and share these passwords with the respective users.',
    importPasswordsCopied: 'Copied!',
    importDone: 'Close',
    importNoFile: 'Please select a file before continuing.',
    importNoOrg: 'Please select an organization before continuing.',
    importVehicleNotFound: 'Registration number not found',
    sendWelcomeEmail: 'Send welcome email with password reset',
    sendWelcomeEmailDesc: 'The user will receive an email with a link to activate their account and set their own password. The link is valid for 72 hours.',
    sendWelcomeEmailSent: 'Welcome email sent',
    sendWelcomeEmailSentTo: 'Welcome email sent to {{email}}',
    sendWelcomeEmailButton: 'Send welcome email',
    sendWelcomeEmailSending: 'Sending...',
    sendWelcomeEmailError: 'Could not send welcome email',
    sendWelcomeEmailBulk: 'Send welcome email to list',
    sendWelcomeEmailBulkHint: 'Sends welcome email with activation link to all users in the list. Users without email, SSO login or inactive/locked are skipped.',
    sendWelcomeEmailBulkConfirm: 'Send welcome email with activation link to {{count}} users in the list?',
    sendWelcomeEmailBulkDone: 'Welcome email sent to {{sent}} users',
    sendWelcomeEmailBulkPartial: '{{skipped}} skipped, {{failed}} failed',
    sendWelcomeEmailBulkNone: 'There are no users in the list to send to',
    welcomeSkipNoEmail: 'missing email',
    welcomeSkipSso: 'SSO login',
    welcomeSkipInactive: 'inactive/locked',
    welcomeSkipNotFound: 'not found',
    notVerified: 'Not verified',
    neverLoggedIn: 'Never logged in',
    noPassword: 'No password',
    lastLoginAt: 'Last login: {{date}}',
    searchPlaceholder: 'Search name, email or reg. number...',
    department: 'Department',
    editUserTitle: 'Edit',
    deleteUserTitle: 'Delete',
    hardDeleteButton: 'Delete permanently',
    hardDeleteTitle: 'Permanently delete user (SSO test)',
    hardDeleteConfirm: 'This permanently removes {{name}} ({{email}}) so SSO can recreate the account. This cannot be undone.',
    emailRequiredForWelcome: 'Email is required to send a welcome email.',
    downloadTemplateError: 'Could not download the template',
    driverInformation: 'Driver Information',
    connectedVehicle: 'Connected vehicle: {{reg}}',
  },

  // User form fields
  userFormFields: {
    password: 'Password',
    twoFactorAuth: 'Two-factor authentication',
    selectOrg: 'Select organization...',
    selectOrgForRoles: 'Select organization first',
    noRolesForOrg: 'No roles for this organization',
    rolesFromOrganization: 'Showing roles from the organization top node.',
    keepCurrentPassword: 'Leave empty to keep current',
    mobileLoginOptional: 'Optional - for mobile app login',
    driverInfo: 'Driver info',
    tabSchedule: 'Schedule',
    tabVehicle: 'Vehicle',
    tabHistory: 'History',
    driverDraftHint: 'Schedule and vehicle are saved when you click Create user – you do not need to save the user first.',
    vehicleDraftSaved: 'Assignment saved as draft – applied when the user is created.',
    isAlsoDriver: 'Is also a driver',
    ongoing: ' — ongoing',
    globalAdminRolesHint:
      'Some internal roles (e.g. Deviaq Ledning) can only be assigned by a global admin.',
    roleViewer: 'Viewer',
    roleDriver: 'Driver',
    roleAdmin: 'Admin',
  },

  // Role permissions
  rolePermissions: {
    selectFunctions: 'Select which functions should be available',
    moduleNotEnabled: 'Module is not enabled',
    selectModuleLeft: 'Select a module on the left to see functions',
  },

  // Data table
  dataTable: {
    searchPlaceholder: 'Search...',
    filterPlaceholder: 'Filter…',
  },

  // Customer detail
  customerDetail: {
    privateCompany: 'Private company',
    companyName: 'Company name: ',
    employees: 'Employees: ',
    yearsInBusiness: 'Years in business: ',
    perBrand: 'Per brand',
    perFuelType: 'Per fuel type',
    usersLabel: 'Users',
    keyCabinet: 'Key cabinet',
    devices: 'Devices (GPS)',
    flinkeyBoxes: 'Flinkey boxes',
    registeredCables: 'Cables (registered)',
    devicesPerType: 'Devices per type',
    firstName: 'First name',
    primaryContact: 'Primary contact',
  },

  // Customer wizard
  customerWizard: {
    namePlaceholder: 'E.g. Linköping municipality',
    privateCompany: 'Private company',
    cityPlaceholder: 'Linköping',
    fixedMonthly: 'Fixed monthly fee',
    perUser: 'Per user',
    otherInfoPlaceholder: 'Other information...',
  },

  // Customer packages
  customerPackages: {
    allPackagesSelected: 'All available packages are selected',
  },

  // Customer API config
  customerApiConfig: {
    lastUsed: 'Last used:',
    noEndpoints: 'No endpoints available',
  },

  // Customer federation config
  customerFederationConfig: {
    autoGenerate: 'Leave empty for automatic generation based on organization ID.',
    redirectUri: 'Redirect URI (for IdP configuration)',
    clientAuthMethod: 'Client authentication',
    certificate: 'Certificate (private_key_jwt)',
    certHelp: 'We (the client) hold the private key. The public certificate is uploaded in Entra (Certificates & secrets → Certificates). The fields accept inline PEM or a file path on the server (recommended: a file with 0600 permissions).',
    certificatePem: 'Public certificate (PEM or file path)',
    privateKeyPem: 'Private key (PEM or file path)',
    privateKeyHint: 'Stored securely on our side and never returned by the API. Prefer a file path on the server.',
    defaultRole: 'Default role for new SSO users',
    defaultRoleFallback: 'Default (User / VIEWER)',
    defaultRoleHelp: 'Role assigned automatically when a new user signs in via SSO. Existing accounts keep their role.',
    claimMapping: 'Claim mapping',
    claimMappingHelp: 'Specify the claim names sent by the IdP. Leave empty to use defaults (email, given_name, family_name, rsid, phone_number).',
    claimRsid: 'RSID',
    claimEmail: 'Email',
    claimFirstName: 'First name',
    claimLastName: 'Last name',
    claimPhone: 'Phone',
    mustActivate: 'The customer must be activated to configure federation.',
    mustActivateSso: 'Activate the customer to create an organization and then configure SSO.',
    configured: 'Configured but inactive',
    notConfigured: 'Not configured',
    enabled: 'Enabled',
    activateSaml: 'Enable SAML 2.0',
    activateOidc: 'Enable OpenID Connect',
    activateAdSync: 'Enable AD user synchronization',
    updateSaml: 'Update SAML',
    saveSaml: 'Save SAML',
    updateOidc: 'Update OIDC',
    saveOidc: 'Save OIDC',
    update: 'Update',
    save: 'Save',
    delete: 'Delete',
    adSyncTab: 'AD user sync',
    adSyncTitle: 'What the customer needs to provide (Entra ID)',
    adSyncStep1: 'Step 1 – App registration in Entra ID',
    adSyncStep2: 'Step 2 – API permissions (requires Global Admin)',
    adSyncStep3: 'Step 3 – Client Secret',
    adSyncSyncMode: 'Sync mode',
    adSyncModeManual: 'Manual (via button)',
    adSyncModeNightly: 'Automatic every night',
    adSyncLastSync: 'Last synchronization',
    adSyncNone: 'No sync performed yet',
    adSyncSyncChanges: 'Sync changes',
    adSyncSyncAll: 'Sync all',
    adSyncFullSync: 'Full sync',
    adSyncTestConnection: 'Test connection (preview)',
    adSyncLogTitle: 'Recent synchronizations',
    adSyncPreviewOk: 'Connection OK – ~{{count}} users in AD ({{disabled}} disabled)',
    adSyncPreviewMore: 'Showing 10 of {{count}} fetched (preview)',
    adSyncIncremental: '(incremental)',
    adSyncFull: '(full)',
    adSyncFetched: 'fetched',
    adSyncCreated: 'created',
    adSyncUpdated: 'updated',
    adSyncDeactivated: 'deactivated',
    copyUrl: 'Copy URL',
    copyUrlShort: 'Copy',
    giveUrlToIt: 'Provide this URL to the customer\'s IT department to configure their IdP.',
    tableColName: 'Name',
    tableColEmail: 'Email',
    tableColDepartment: 'Department',
    tableColStatus: 'Status',
    userActive: 'Active',
    userInactive: 'Inactive',
  },

  // Customer SMS config
  customerSmsConfig: {
    enableSmsService: 'Enable SMS service',
    useCases: 'Use cases:',
    title: 'SMS configuration',
    subtitle: 'Configure SMS sending for notifications and alarms',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    provider: 'SMS provider',
    senderId: 'Sender ID (max 11 characters)',
    apiKey: 'API key',
    apiKeyUnchanged: '(unchanged)',
    apiKeyPlaceholder: 'Enter API key',
    apiKeyConfigured: 'An API key is already configured. Leave the field empty to keep it.',
    apiUrl: 'API URL (optional)',
    apiUrlPlaceholder: 'Leave empty for default URL',
    saveSettings: 'Save settings',
    saving: 'Saving...',
    saveSuccess: 'SMS settings saved',
    saveError: 'Could not save SMS settings',
    testSection: 'Test SMS configuration',
    testPhonePlaceholder: '07XXXXXXXX or +46...',
    testSend: 'Send test',
    testSending: 'Sending...',
    testSuccess: 'Test SMS sent!',
    testFailed: 'Test failed',
    testError: 'Could not send test SMS',
    noOrgId: 'The organization is missing an organization_id',
    noPhoneNumber: 'Enter a phone number for test',
    noOrgActivated: 'This customer has no linked organization. Activate the organization first.',
    useCaseGeofence: 'Geofence alerts to drivers',
    useCaseVehicleEvents: 'Notifications for vehicle events',
    useCaseService: 'Reminders for service and inspection',
    providerNone: 'None (disabled)',
  },

  // System packages manager
  systemPackagesManager: {
    featuresPlaceholder: 'Phone support\nSLA 99.9%\nAPI access',
  },

  // Imports management
  importsManagement: {
    namePlaceholder: 'E.g. Vehicle import from Excel',
    selectTable: 'Select table...',
    firstRowIsHeader: 'First row is header',
    dragFileHere: 'Drag file here or click to select',
    skipKeepExisting: 'Skip (keep existing)',
    replaceRow: 'REPLACE (replace row)',
    runImport: 'Run import',
    filesFromSource: 'Files from source',
    runImportFromFtp: 'Run import from FTP',
  },

  // Trip validation
  tripValidation: {
    needsReview: 'Needs review',
    manage: 'Manage',
    resolve: 'Resolve',
    deleteReasonPlaceholder: 'Describe why this trip is being deleted (at least 5 characters)...',
    minCharsRequired: 'At least 5 characters required',
  },

  // System monitor
  systemMonitor: {
    title: 'System Monitor',
    queue: 'Queue',
    queueLength: 'Queue length',
    serverNumbers: 'Numbers from server:',
    running: 'Running',
    runNow: 'Run now',
    latestRuns: 'Latest runs',
    speedLimitBackfill: 'Speed limit backfill',
    speedLimitQueue: 'Queue (trip_speed_limit_backfill)',
    speedLimitConsumers: 'Consumers',
    speedLimitDlq: 'DLQ messages',
    speedLimitRate: 'Trips/min (5 min avg)',
    speedLimitProcessedTotal: 'Processed total',
    speedLimitWorkers: 'Workers online',
    speedLimitAlerts: 'Alerts',
    noAlerts: 'No alerts',
    importSources: {
      title: 'Import sources (intake)',
      total: 'Sources',
      active: 'Active',
      failed: 'Errors',
      pendingFiles: 'Files in directories',
      none: 'No import sources configured',
      name: 'Name',
      type: 'Type',
      lastFetch: 'Last fetch',
      filesInDir: 'Files in directory',
      newestFile: 'Newest file',
      inactive: 'Inactive',
      never: 'Never',
      dirMissing: 'Directory missing/unreadable',
    },
  },

  // Data replay
  dataReplay: {
    clearAllDescription: 'Clear all trip/GPS/eco/stats data and queues. Vehicles and devices are preserved.',
    searchPlaceholder: 'Search IMEI or registration number...',
    verifyDataStatus: 'Verify data status',
    fullReset: 'Full reset',
    replayAllFiles: 'Replay all files (Dev)',
    fullResetInProgress: 'Full reset in progress... This may take several minutes. Please wait.',
    allDbEmpty: 'All databases empty (full reset verified)',
    someTablesHaveData: 'Some tables/collections still contain data',
    startReplay: 'Start Replay',
    startReplaySection: 'Start Replay',
    fromDate: 'From date',
    toDate: 'To date',
    organization: 'Organization',
    vehicleImei: 'Vehicle (IMEI)',
    selectOrgFirst: 'Select organization first',
    allVehiclesInOrg: 'All vehicles in organization',
    allVehiclesInOrgHint: 'Empty IMEI — all devices in org (according to database scope)',
    noVehiclesFound: 'No vehicles in list for this date range (or none matching the search)',
    replayNote: 'NOTE: Replay clears existing data for the selected organization and date range first. With a selected vehicle (IMEI), batch replay builds and saves trips directly (recommended). Without IMEI, packets are queued via workers for all devices in the org scope.',
    availableDates: 'Available Dates',
    noBinFiles: 'No bin files found',
    clickDateHint: 'Click a date to set From/To. You can also type any date in the fields above (list shows max 90 days).',
    files: 'files',
    showingDates: 'Showing 90 of {{total}} dates. Use From/To fields for older dates.',
    replayJobs: 'Replay Jobs',
    noActiveJobs: 'No active jobs',
    orgLabel: 'Org {{id}}',
    allDevicesInOrg: 'all devices in org',
    allVehiclesGlobal: 'All vehicles (global)',
    filesProgress: '{{processed}}/{{total}} files',
    tripsDeleted: '{{count}} trips deleted',
    queued: '{{count}} queued',
    stopReplay: 'Stop replay',
    stepLog: 'Step log:',
    selectOrgPlaceholder: 'Select organization…',
  },

  // Speed compliance report
  speedComplianceReport: {
    selectOrgAndDateRange: 'Select organization and date range',
    violationPercent: 'Violation (%)',
    violations: 'Violations',
    timeOverLimit: 'Time over limit',
    segmentsHint: 'Shows data points per speed limit (OSM sign). Highest at violation is the actual highest GPS speed when the vehicle exceeded the signed limit by more than 10%. Only GPS spikes above 250 km/h are filtered out.',
  },

  // Vehicle wear report
  vehicleWearReport: {
    wearScore: 'Wear score',
    wear: 'Wear',
    noVehicleData: 'No vehicle data',
  },

  // CO2 report
  co2Report: {
    noVehicleData: 'No vehicle data',
  },

  // Odometer report
  odometerReport: {
    searchPlaceholder: 'Search reg no., brand...',
  },

  // Driving log
  drivingLog: {
    searchVehiclePlaceholder: 'Search or select vehicle...',
  },

  // Rental report
  rentalReport: {
    recipientNamePlaceholder: 'Recipient name',
  },

  // Geofence events
  geofenceEvents: {
    searchVehiclePlaceholder: 'Search vehicles...',
  },

  // Signup
  signup: {
    title: 'Create DQFleet account',
    subtitle: 'After signing up you must confirm your email before you can log in.',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    confirmPasswordLabel: 'Confirm password',
    firstNamePlaceholder: 'First name',
    lastNamePlaceholder: 'Last name',
    emailPlaceholder: 'you@company.com',
    passwordPlaceholder: 'At least 8 characters',
    confirmPasswordPlaceholder: 'Confirm password',
    passwordRulesHint:
      'At least 8 characters, uppercase, lowercase, digit and special character (same rules as for manually created accounts).',
    infoBoxTitle: 'Organization and access',
    infoBoxBody:
      'Prefer your work email. If your organization has registered your email domain, you will be linked automatically with the Viewer role after verification.',
    infoBoxManual:
      'If the domain is not registered, an administrator can link your account to the correct organization later.',
    tosPrefix: 'I accept the',
    tosTerms: 'terms of use',
    tosAnd: 'and',
    tosPrivacy: 'privacy policy',
    submit: 'Create account',
    creating: 'Creating account...',
    hasAccount: 'Already have an account?',
    loginLink: 'Sign in',
    toastSuccessOrgLinked:
      'Account created. Your domain is linked to an organization — verify your email to activate your account.',
    toastSuccessManual:
      'Account created. Verify your email. If your organization does not recognize your domain, an administrator may need to link your account.',
    createError: 'Could not create account. Please try again.',
  },

  // Packages management
  packagesManagement: {
    unlimitedPlaceholder: 'Unlimited',
    phoneSupportPlaceholder: 'Phone support...',
  },

  // Service overview
  serviceOverview: {
    statusOverdue: 'Overdue',
    statusDueSoon: 'Due soon',
    statusCompleted: 'Completed',
    allStatuses: 'All statuses',
    overdueFilter: 'Overdue',
    dueSoonFilter: 'Due soon',
    bookedFilter: 'Booked',
    nextKm: 'Next km',
  },

  // Device assign modal
  deviceAssignModal: {
    title: 'Assign IMEI to organization',
    pasteImeiAndSelectOrg: 'Upload .xlsx or paste IMEIs and select organization',
    assignmentComplete: 'Assignment complete',
    devicesAssigned: 'devices assigned',
    assigned: 'Assigned',
    skippedMounted: 'Skipped (mounted)',
    notFound: 'Not found',
    invalidFormat: 'Invalid format',
    notFoundImeis: 'Not found IMEIs:',
    errors: 'Errors:',
    selectOrg: 'Select organization...',
    imeiLabel: 'IMEI numbers (paste, one per line, comma or semicolon separated)',
    pasteImeiHere: 'Paste IMEI here...',
    validateImei: 'Validate IMEI',
    validImeiFound: '{count} valid IMEI numbers found',
    clear: 'Clear',
    assignDevices: 'Assign {count} devices',
    xlsxSection: 'Excel import (.xlsx)',
    xlsxHint: 'Click to choose file — column A = IMEI, cell B2 = cable count',
    xlsxOnly: 'Only .xlsx files are supported.',
    xlsxNoImei: 'No valid IMEIs found in column A.',
    xlsxReadError: 'Could not read the file.',
    xlsxImeiNote: 'Tip: format IMEI as text in Excel to avoid rounding.',
    orPaste: 'or',
    cablesFromFile: 'Cables from file (B2): {n} (saved on CRM customer when assigning)',
  },

  // Common UI
  commonUi: {
    closeModal: 'Close',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    noLanguages: 'No languages available',
    noRouteData: 'No route data available',
    highPriority: 'High',
  },

  // Customer devices tab
  customerDevicesTab: {
    inVehicle: 'In vehicle',
    customerStock: 'Customer stock',
    centralStock: 'Central stock',
    allOrganizations: 'All organizations',
    deviceCount: 'devices',
  },

  // Vehicle list widget
  vehicleListWidget: {
    totalVehicles: 'Total vehicles',
  },

  // Inspection widget
  inspectionWidget: {
    title: 'Inspections',
    titleWithDays: 'Inspection within 40d',
    noVehicles: 'No vehicles',
    overdue: 'overdue',
    daysAgo: '{days}d ago',
    today: 'Today',
  },

  leasingWidget: {
    title: 'Leasing',
    titleWithMonths: 'Leasing within 3 mo',
    noVehicles: 'No leases expiring soon',
    overdue: 'overdue',
    daysAgo: '{days}d ago',
    today: 'Today',
  },

  taxWidget: {
    title: 'Vehicle tax',
    titleWithDays: 'Tax within 45d',
    noVehicles: 'No vehicles with tax due within 45 days',
    overdue: 'overdue',
    daysAgo: '{days}d ago',
    today: 'Today',
  },

  // Unplugged widget
  unpluggedWidget: {
    title: 'Unplugged',
    subtitle: 'Unplugged',
    warning: 'Warning',
    allConnected: 'All devices connected',
  },

  gpsLagWidget: {
    title: 'GPS data lag',
    subtitle: 'Bin received vs data created on device (>1 h)',
    noVehicles: 'No vehicles with delayed GPS data',
    dumpQueue: 'in dump queue',
    dumpLagLabel: 'Diff receive−device',
    showDumpOnly: 'Show dump queue only',
    showAllStale: 'Show all stale',
    kind: {
      dumping_lag: 'Dump queue',
      silent: 'Silent',
      parked_or_old: 'Parked',
      lost_while_driving: 'Driving — lost',
    },
  },

  unplugEvents: {
    title: 'Unplug history',
    subtitle: 'Plug-in and unplug events with time and nearest position.',
    backToDashboard: 'Back to dashboard',
    openHistory: 'Show plug-in and unplug history',
    eventType: 'Event',
    allTypes: 'All',
    unplugged: 'Unplugged',
    plugged: 'Plugged in',
    occurredAt: 'Time',
    address: 'Position',
    email: 'Email',
    emailSent: 'Sent',
    emailNotSent: 'Not sent',
    searchPlaceholder: 'Search registration number',
    registrationNumber: 'Reg. no',
    empty: 'No events in the selected period.',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
    pageOf: 'Page {{page}} of {{pages}}',
    loadError: 'Could not load unplug history.',
    mapSource: 'Nearest GPS point',
  },

  // App (routes)
  appRoutes: {
    salesPipeline: 'Sales Pipeline',
    salesPipelineDesc: 'Sales funnel and leads',
    economicOverview: 'Economic Overview',
    economicOverviewDesc: 'Rolling 12, trends and revenue',
  },

  // Device import modal
  deviceImportModal: {
    pasteImeiAndSelectOrg: 'Paste IMEI numbers and select organization',
  },

  // Tire management
  tireManagement: {
    partnerPlaceholder: 'Tire Partner Sundsvall',
  },

  // Company Search (Checkbiz)
  companySearch: {
    title: 'Search Company',
    lookupButton: 'Search company',
    whoLabel: 'Company name / phone',
    whoPlaceholder: 'E.g. Volvo or 08-123',
    whereLabel: 'Location (optional)',
    wherePlaceholder: 'E.g. Stockholm',
    search: 'Search',
    searching: 'Searching...',
    minChars: 'Enter at least 2 characters',
    searchError: 'Could not search companies',
    noResults: 'No companies found. Try a different search term.',
    startSearching: 'Search by company name, phone number or address.',
    resultsCount: '{{count}} results',
    showingOfTotal: 'Showing {{showing}} of {{total}} results',
    refineSearch: 'Refine your search for more results',
    wildcardHint: 'Wildcard (*) is added automatically. Use ? for single character.',
    page: 'Page',
  },

  // Pool Car Demo (Region Skåne)
  poolcarDemo: {
    myBookings: 'My Bookings',
    bookingDetails: 'Booking Details',
    digitalKey: 'Digital Key',
    returnVehicle: 'Return Vehicle',
    receipt: 'Receipt',
    noBookings: 'No bookings',
    noBookingsHint: 'You have no upcoming bookings.',
    upcoming: 'Upcoming bookings',
    active: 'Active',
    vehicle: 'Vehicle',
    pool: 'Pool',
    period: 'Period',
    destination: 'Destination',
    licenseConfirm: 'I confirm that I hold a valid driving license and am fit to operate the vehicle.',
    startsIn: 'Booking starts in',
    checkoutVehicle: 'Check out vehicle',
    vehicleUnlocked: 'Vehicle is unlocked',
    vehicleLocked: 'Vehicle is locked',
    communicating: 'Communicating...',
    bookingEnded: 'Booking ended',
    cannotUnlock: 'The vehicle cannot be unlocked after the booking has ended.',
    tapToToggle: 'Tap to lock/unlock',
    bookingTime: 'Booking time',
    returnTitle: 'Return vehicle',
    checkParked: 'Vehicle is parked in the correct location',
    checkLocked: 'Vehicle is locked',
    reportDamage: 'Report damage or issue',
    damageDescription: 'Describe the damage...',
    confirmReturn: 'Confirm return',
    thankYou: 'Thank you!',
    vehicleReturned: 'The vehicle has been returned and registered.',
    backToBookings: 'Back to bookings',
    demoTitle: 'Keyless Car Pool – Demo',
    demoDescription: 'This demonstration shows the complete user flow for an employee using a pool vehicle, including booking, pickup, digital key solution, and return.',
    step1: 'Select booking',
    step2: 'Check out vehicle',
    step3: 'Unlock / Lock (Digital key)',
    step4: 'Return vehicle',
    step5: 'Receipt',
  },

  tidbok: {
    title: 'Digital Logbook',
    subtitle: 'Driving and rest times per EU 561/2006 and TSFS 2013:3',
    registerActivity: 'Register activity',
    selectDriver: 'Driver',
    selectDriverPlaceholder: 'Select driver...',
    searchDriverPlaceholder: 'Search driver (name or card number)…',
    selectDriverToStart: 'Select a driver to view the logbook',
    driverCard: 'Driver card',
    previousWeek: 'Previous',
    nextWeek: 'Next',
    weekLabel: 'Week {{week}}, {{year}}',
    noActivities: 'No activities registered this week',
    registerFirstActivity: 'Register first activity',
    activitySaved: 'Activity saved',
    errorLoadingDrivers: 'Could not load drivers',
    errorLoadingVehicles: 'Could not load vehicles',
    errorLoadingEntries: 'Could not load logbook entries',
    errorSaving: 'Could not save the activity',
    weekDriving: 'Weekly driving',
    weekWork: 'Weekly work',
    weekRest: 'Weekly rest',
    driving: 'Driving',
    continuous: 'Continuous',
    activityType: 'Activity type',
    vehicle: 'Vehicle',
    selectVehiclePlaceholder: 'Select vehicle...',
    date: 'Date',
    startTime: 'Start time',
    endTime: 'End time',
    noteOptional: 'Note (optional)',
    notePlaceholder: 'e.g. Stockholm → Gothenburg',
    saving: 'Saving...',
    saveActivity: 'Save activity',
    rulesTitle: 'Regulations – applicable limits',
    activity: {
      driving: 'Driving',
      otherWork: 'Other work',
      available: 'Available',
      rest: 'Rest',
    },
    rule: {
      maxDailyDriving: 'Max daily driving',
      maxContinuousDriving: 'Max continuous driving',
      mandatoryBreak: 'Mandatory break',
      minDailyRest: 'Min daily rest',
      maxWeeklyDriving: 'Max weekly driving',
      maxBiweeklyDriving: 'Max biweekly driving',
    },
    validation: {
      selectVehicle: 'Select a vehicle',
      enterDate: 'Enter date',
      enterStartTime: 'Enter start time',
      enterEndTime: 'Enter end time',
      endAfterStart: 'End time must be after start time',
    },
  },
  keyManagement: {
    title: 'Key Management',
    tabs: {
      cabinets: 'Cabinets',
      keys: 'Keys',
      cabinetSync: 'Cabinet sync',
    },
    cabinetSync: {
      title: 'Cabinet sync (Creone Rabbit)',
      subtitle: 'What KeyWin sends to each cabinet. Global admin only. PIN and personal data are masked.',
      spyConnected: 'Observer connected',
      spyDisconnected: 'Observer disconnected',
      traceOn: 'Firehose on',
      traceOff: 'Firehose off',
      eventsLastMinute: 'Events / min',
      eventsLastHour: 'Events / hour',
      bytesLastHour: 'Bytes / hour',
      storedEvents: 'Stored events',
      queues: 'Queues',
      events: 'Events',
      queue: 'Queue',
      kind: 'Kind',
      backlog: 'Backlog',
      consumers: 'Consumers',
      publishRate: 'Publish / s',
      deliverRate: 'Deliver / s',
      online: 'Cabinet up',
      offline: 'Cabinet down',
      time: 'Time',
      type: 'Type',
      size: 'Size',
      summary: 'Content',
      payload: 'Payload',
      noEvents: 'No messages captured yet. Create a booking or wait for the next sync.',
      loadError: 'Could not load cabinet sync',
      filterQueue: 'Filter queue',
      allQueues: 'All queues',
      cabinet: 'Cabinet',
      keywin: 'KeyWin cloud',
      other: 'Other',
      source: 'Source',
      sourceAll: 'All sources',
      sourceRabbit: 'Rabbit',
      sourceLocker: 'Our API',
      sourceCabinetHttp: 'Cabinet HTTP',
    },
    summary: {
      totalCabinets: 'Total cabinets',
      keysIn: 'Keys in',
      keysOut: 'Keys out',
      unusedSlots: 'Unused slots',
    },
    cabinet: {
      name: 'Name',
      location: 'Location',
      status: 'Status',
      capacity: 'Capacity',
      occupiedSlots: 'Occupied slots',
      availableSlots: 'Available slots',
      assignedGroup: 'Pickup location',
      noGroup: 'Not assigned',
      assignGroup: 'Assign pickup location',
      changeGroup: 'Change pickup location',
      removeGroup: 'Remove assignment',
      keysInCabinet: 'Keys in cabinet',
      showKeys: 'Show keys',
      hideKeys: 'Hide keys',
      lists: 'Lists',
      listsShort: 'lists',
      keysShort: 'keys',
      keysIn: 'Keys in',
      keysOut: 'Keys out',
      inShort: 'in',
      emptyShort: 'empty',
      unusedSlots: 'Unused slots',
      slotsPerList: 'slots/list',
      empty: 'empty',
      emptySlot: 'Empty slot',
      backToOverview: 'Back to cabinet overview',
      changeShort: 'Change',
      assignShort: 'Assign',
      linkedToCustomer: 'Linked to customer',
      notLinkedToCustomer: 'Not linked to customer',
      linkCustomer: 'Link to customer',
      unlinkCustomer: 'Remove customer link',
      linkCustomerConfirm: 'Link this cabinet to a customer? SuperAdmin and LocalAdmin will be created as KeyWin users with the cabinet tags (no web login).',
      unlinkCustomerConfirm: 'Remove the customer link? Pickup location and LocalAdmin will be removed.',
      linkCustomerFirst: 'Link to customer first',
      localAdminPin: 'LocalAdmin PIN',
      syncTags: 'Sync tags',
      syncTagsHelp: 'Connects only tags in this cabinet to SuperAdmin and LocalAdmin. No group is created — the keypad uses User + tags.',
    },
    key: {
      name: 'Name',
      keyId: 'KeyId',
      cabinet: 'Cabinet',
      slot: 'Slot',
      status: 'Status',
      available: 'Available',
      vehicle: 'Vehicle',
      noVehicle: 'Not linked',
      assignVehicle: 'Link vehicle',
      removeVehicle: 'Remove link',
      history: 'Assignment history',
      externalId: 'External ID',
      keyInfo: 'Key info',
      linkedVehicle: 'Linked vehicle',
    },
    status: {
      online: 'Online',
      offline: 'Offline',
      error: 'Error',
      maintenance: 'Maintenance',
      in_cabinet: 'In cabinet',
      taken: 'Taken',
      lost: 'Lost',
    },
    assignment: {
      assignedAt: 'Assigned',
      assignedBy: 'Assigned by',
      unassignedAt: 'Removed',
      note: 'Note',
      notePlaceholder: 'Optional note...',
      noAssignments: 'No active assignments',
    },
    logs: {
      activityLog: 'Activity log',
      keyHistory: 'In/out history',
      noEvents: 'No events',
      taken: 'Taken',
      returned: 'Returned',
      alarm: 'Alarm',
      alarmsExist: 'Unacknowledged alarms',
      linked: 'Linked to',
      unlinked: 'Unlinked from',
      cabinetEvents: 'Cabinet events',
      last24h: 'Last 24h',
      unacknowledgedAlarms: 'unacknowledged alarms',
      acknowledgeAll: 'Acknowledge all',
      filterAll: 'All',
      filterAlarms: 'Alarms',
      filterKeys: 'Keys',
      filterSystem: 'System',
      noEventsToShow: 'No events to show',
    },
    messages: {
      assignSuccess: 'Assignment created',
      unassignSuccess: 'Assignment removed',
      assignError: 'Could not create assignment',
      unassignError: 'Could not remove assignment',
      customerAssignSuccess: 'Cabinet linked to customer',
      customerUnassignSuccess: 'Customer link removed',
      customerAssignError: 'Could not link cabinet to customer',
      customerUnassignError: 'Could not remove customer link',
      syncTagsSuccess: '{{count}} tags connected to SuperAdmin and LocalAdmin',
      syncTagsEmpty: 'No tags in the cabinet yet. Open with the built-in SuperAdmin, insert the tags, then sync again.',
      syncTagsError: 'Could not sync tags',
      missingKeyId: 'This key has no KeyId (OnPrem tag) and cannot be linked to a vehicle.',
      renameWarning: 'The vehicle was linked, but the cabinet key name could not be changed to the registration number.',
      loadError: 'Could not load data',
      confirmRemove: 'Are you sure you want to remove this assignment?',
      alarmAcknowledged: 'Alarm acknowledged',
      allAlarmsAcknowledged: 'All alarms acknowledged',
      alarmError: 'Could not acknowledge alarm',
    },
    filters: {
      allCabinets: 'All cabinets',
      allStatuses: 'All statuses',
      searchPlaceholder: 'Search by key name...',
    },
    modal: {
      selectGroup: 'Select pickup location',
      selectVehicle: 'Select vehicle',
      registrationNumber: 'Registration number',
      searchVehicle: 'Search reg. number or model...',
      searchRegistration: 'Enter registration number, e.g. ABC123',
      typeRegistrationHint: 'Type at least two characters. Only vehicles without a key or Flinkey box are shown.',
      clickToSelect: 'Click a vehicle in the list to select it',
      noVehicleMatch: 'No available vehicle matched the search',
      currentAssignment: 'Current assignment',
      noGroupSelected: 'No pickup location selected',
      selectCustomer: 'Select customer',
      noCustomerSelected: 'No customer selected',
      noEligibleCustomers: 'No customers have cabinets enabled. Enable cabinets under Organization → Cabinet settings.',
      linkCustomerHelp: 'Choose which customer this cabinet should belong to. SuperAdmin and LocalAdmin become KeyWin users with the cabinet tags, without web login.',
    },
  },

  // Assignments
  assignments: {
    title: 'Assignments',
    description: 'Manage task assignments to people',
    myAssignments: 'My Assignments',
    myAssignmentsHint: 'Tasks assigned to you',
    assigneeFilterMine: 'Assigned to me',
    assigneeFilterAll: 'All assignments',
    create: 'Create Assignment',
    category: 'Category',
    whatToDo: 'What to do',
    titlePlaceholder: 'Describe the task briefly...',
    descriptionLabel: 'Description',
    assignedTo: 'Assigned to',
    assignedBy: 'Assigned by',
    assignedAt: 'Assigned',
    selectUser: 'Select user...',
    priorityLabel: 'Priority',
    priorityLow: 'Low',
    priorityNormal: 'Normal',
    priorityHigh: 'High',
    priorityUrgent: 'Urgent',
    vehicle: 'Vehicle',
    vehicleIdPlaceholder: 'Vehicle ID (optional)',
    dueDate: 'Due date',
    startWork: 'Start',
    complete: 'Mark Complete',
    cancel: 'Cancel',
    completionNotes: 'Notes',
    completionNotesPlaceholder: 'Describe what was done...',
    startedAt: 'Started',
    completedAt: 'Completed',
    allStatuses: 'All statuses',
    allCategories: 'All categories',
    noCategories: 'No categories available (run seed or check API)',
    statusPending: 'Pending',
    statusInProgress: 'In Progress',
    statusCompleted: 'Completed',
    statusCancelled: 'Cancelled',
    noAssignments: 'No assignments found',
    noOpenAssignments: 'No open assignments',
    open: 'open',
    viewAll: 'View all assignments',
    fillRequired: 'Fill in all required fields',
    created: 'Assignment created',
    started: 'Assignment started',
    completed: 'Assignment completed',
    cancelled: 'Assignment cancelled',
    confirmCancel: 'Are you sure you want to cancel this assignment?',
    assigned: 'assigned',
    assign: 'Assign',
    selectAll: 'Select all',
    assignSelected: 'Assign selected',
    vehiclesSelected: 'vehicles selected',
    assignmentsCreated: 'assignments created',
  },
  bookingWidget: {
    title: 'My Bookings',
    noBookings: 'No upcoming bookings',
    upcoming: 'upcoming',
    booked: 'Booked',
    checkedOut: 'Checked Out',
    awaiting: 'Waiting',
    awaitingHandling: 'Waiting for handling',
    noVehicleAssigned: 'No vehicle assigned',
    previousVehicle: 'previously',
    openCalendar: 'Open booking calendar',
  },
  resourceTypesAdmin: {
    title: 'Resource types (system)',
    description: 'Manage system-wide resource types that can be assigned to organizations',
    code: 'Code',
    nameSv: 'Name (Swedish)',
    nameEn: 'Name (English)',
    displayOrder: 'Sort order',
    addNew: 'Add new resource type',
    codePlaceholder: 'e.g. SCOOTER',
    nameSvPlaceholder: 'e.g. Elsparkcykel',
    nameEnPlaceholder: 'e.g. E-scooter',
    created: 'Resource type created',
    updated: 'Resource type updated',
    codeRequired: 'Code and name are required',
  },
  equipment: {
    title: 'Vehicle equipment',
    description: 'Manage vehicle equipment that can be assigned to vehicles',
    tagName: 'Name',
    organization: 'Organization',
    createUnderOrganization: 'Organization',
    icon: 'Icon',
    newTagPlaceholder: 'New vehicle equipment…',
    iconPlaceholder: 'Icon (optional)',
    noTags: 'No vehicle equipment. Add an item to get started.',
    vehicleEquipment: 'Vehicle equipment',
    noEquipment: 'No equipment assigned to this vehicle.',
    selectTag: 'Select equipment…',
    notesPlaceholder: 'Note (optional)',
    removed: 'Equipment removed',
  },
  flinkeyBoxes: {
    title: 'Flinkey Boxes',
    dashboardDescription: 'Manage Flinkey digital key boxes and individual assignments',
    addBox: 'Add box',
    search: 'Search product ID, serial number...',
    productId: 'Product ID',
    serialNumber: 'Serial number',
    boxType: 'Type',
    carName: 'Vehicle',
    licensePlate: 'License plate',
    contractStart: 'Contract start',
    contractState: 'Contract status',
    notes: 'Notes',
    organization: 'Organization',
    individuals: 'Individuals',
    noBoxes: 'No boxes found',
    noOrg: 'No organization',
    editBox: 'Edit box',
    linkVehicleTitle: 'Link vehicle',
    searchVehicle: 'Search vehicle...',
    individualsDescription: 'Individuals with access to this box',
    searchUser: 'Search user...',
    addUser: 'Add',
    noUsers: 'No individuals added',
    vehicleLinked: 'Vehicle linked',
    vehicleLinkError: 'Error linking vehicle',
    linkVehicle: 'Link vehicle',
    unlinkVehicle: 'Unlink vehicle',
    vehicleUnlinked: 'Vehicle unlinked',
    linkedVehicle: 'Linked vehicle',
    confirmDelete: 'Are you sure you want to delete box {{id}}?',
    userAddError: 'Error adding individual',
    removeUser: 'Remove individual',
    confirmRemoveUser: 'Are you sure you want to remove {{name}}?',
    firmwareVersion: 'Firmware',
    importDeviaq: 'Import Deviaq (Excel)',
    importDeviaqSuccess: 'Import complete: {{inserted}} new, {{updated}} updated',
    importDeviaqError: 'Import failed',
    deviaqDetailsTitle: 'Deviaq row',
    deviaqNoDetails: 'No Deviaq payload stored for this box.',
    selectedBoxes: 'box(es) selected',
    assignOrg: 'Assign organization',
    assignOrgTitle: 'Assign organization',
    assignOrgTopNode: 'Organization (top node)',
    searchOrg: 'Search organization...',
    noOrgMatches: 'No matches',
    battery: 'Battery',
    batteryUnknown: 'Unknown',
    batteryCharging: 'Charging',
    chargerDisconnected: 'Charger disconnected',
    nfcTestMode: 'Test mode (do not require key/card on return)',
    nfcTestModeHint: 'When test mode is off the key must be in the box. When checked, key and card are not checked.',
    nfcCardCheck: 'Require card on return',
    nfcCardCheckHint: 'Optional. Enable if the customer must also return a registered card. The card must be registered on the box.',
    nfcDetectMode: 'Detect cards in box',
    nfcDetectModeHint: 'On return/unlock: if an unknown NFC card is found, staff with permission can register it on the box.',
    permanentPower: 'Permanent charging cable',
    permanentPowerHint: 'Warn if the charger is reported disconnected.',
    nfcTagsTitle: 'Registered NFC tags (key/fuel card)',
    nfcTagsHint: 'Registration is done in the mobile app (near the box). Tags can be viewed and removed here.',
    noNfcTags: 'No NFC tags registered.',
    nfcRequired: 'Required',
    nfcLabelKey: 'Key',
    nfcLabelCard: 'Fuel card',
    nfcTagDeleted: 'NFC tag removed',
    nfcTagDeleteError: 'Could not remove NFC tag',
  },
  guide: {
    admin: {
      title: 'Guide Administration',
      description: 'Manage onboarding guides and tours',
      newTour: 'New tour',
      noTours: 'No tours created',
      inactive: 'Inactive',
      steps: 'Steps',
      preview: 'Preview',
      deactivate: 'Deactivate',
      activate: 'Activate',
      noSteps: 'No steps',
      noTitle: '(No title)',
      addStep: 'Add step',
      stepTitle: 'Step title',
      stepTitlePlaceholder: 'E.g. Welcome to the fleet overview',
      stepOrder: 'Order',
      content: 'Content',
      contentPlaceholder: 'Describe what the user should do in this step...',
      targetSelector: 'CSS selector (target)',
      targetSelectorHint: 'E.g. #fleet-map or .vehicle-list. Leave empty for centered dialog.',
      placement: 'Placement',
      tourKey: 'Tour key',
      tourName: 'Tour name',
      tourNamePlaceholder: 'E.g. Introduction to the fleet overview',
      tourDescription: 'Description',
      sortOrder: 'Sort order',
      confirmDeleteTour: 'Are you sure you want to delete this tour?',
      confirmDeleteStep: 'Are you sure you want to delete this step?',
    },
  },
  carinfoOrgSync: {
    title: 'Carinfo Vehicle Import',
    dashboardDescription: 'Import vehicles from Car.info by organization number',
    orgNumber: 'Org. No.',
    customer: 'Customer',
    lastSync: 'Last Sync',
    syncCount: 'Syncs',
    vehicleCount: 'Count',
    active: 'Active',
    inactive: 'Inactive',
    source: 'Source',
    actions: 'Actions',
    sync: 'Sync',
    log: 'Log',
    syncResult: 'Sync Result',
    resultFor: 'Result for',
    added: 'Added',
    updated: 'Updated',
    unchanged: 'Unchanged',
    inactivated: 'Inactivated',
    failed: 'Failed',
    total: 'Total',
    vehicles: 'vehicles',
    filialNumbersTitle: 'Detected branch numbers',
    addFilialsAndSync: 'Add selected and fetch vehicles',
    syncLog: 'Sync History',
    noLogEntries: 'No sync history available',
    noOrgNumbers: 'No organization numbers configured. Add org. numbers via CRM customer tab "Org. No. Vehicles".',
    date: 'Date',
  },

  themeSettings: {
    title: 'Theme & branding',
    description: 'Customize colors for light and dark mode. The preview updates instantly — nothing is saved until you click Save.',
    tabGlobal: 'Global default',
    tabPartner: 'Partner theme',
    selectPartner: 'Select partner',
    editMode: 'Edit mode',
    editModeHint: 'Switch between light and dark — both are saved when you click Save',
    lightTitle: 'Light mode',
    darkTitle: 'Dark mode',
    primary: 'Primary color',
    secondary: 'Secondary color',
    sectionPrimary: 'Primary colors',
    sectionBackground: 'Backgrounds',
    sectionText: 'Text colors',
    bgPage: 'Page background',
    bgContainer: 'Container / sidebar',
    bgElevated: 'Card / surface',
    textDefault: 'Body text',
    textPrimary: 'Accent text',
    textSecondary: 'Muted text',
    textTertiary: 'Faint text (tertiary)',
    textDisabled: 'Disabled text',
    textDanger: 'Danger/red text',
    textLight: 'Light text (on buttons)',
    previewTitle: 'Preview',
    previewLight: 'Light',
    previewDark: 'Dark',
    previewNavActive: 'Active menu',
    previewNavItem: 'Menu',
    previewHeader: 'Header',
    previewCardTitle: 'Card title',
    previewBody: 'Body text that users read in the interface.',
    previewLink: 'Accent link',
    previewMuted: 'Muted help text',
    previewPrimaryBtn: 'Primary',
    previewSecondaryBtn: 'Secondary',
    previewFormTitle: 'Form',
    previewTabsTitle: 'Tabs',
    previewTabOne: 'Overview',
    previewTabTwo: 'Details',
    previewTabThree: 'History',
    previewTabContent: 'Active tab content — this is how body text looks under tab navigation.',
    previewBadgesTitle: 'Badges',
    previewBadgePrimary: 'Primary',
    previewBadgeNeutral: 'Neutral',
    previewBadgeOutline: 'Outline',
    previewBadgeSecondary: 'Secondary',
    previewModalTitle: 'Modal',
    previewModalHeading: 'Confirm action',
    previewModalBody: 'Are you sure you want to continue? This is a preview of the dialog.',
    previewModalCancel: 'Cancel',
    previewModalConfirm: 'Confirm',
    previewComboboxLabel: 'Combobox',
    previewComboboxValue: 'Select option…',
    previewInputLabel: 'Text field',
    previewInputPlaceholder: 'Enter value…',
    previewGridTitle: 'Data grid',
    previewColName: 'Name',
    previewColStatus: 'Status',
    previewColDate: 'Date',
    previewRowA: 'Vehicle ABC123',
    previewRowB: 'Vehicle XYZ789',
    previewRowC: 'Vehicle DEF456',
    previewStatusActive: 'Active',
    previewStatusPending: 'Pending',
    previewStatusDone: 'Done',
    contrastWarning: 'Warning: low contrast between text and background may make content hard to read.',
    reset: 'Reset',
    saveSuccess: 'Theme saved',
    saveSuccessActivated: 'Theme saved and activated across the web app',
    activate: 'Activate theme',
    reapply: 'Re-apply',
    saveError: 'Could not save theme',
    loadError: 'Could not load theme',
    unsavedConfirm: 'You have unsaved changes. Continue without saving?',
    disabledBannerTitle: 'Dynamic theme is not active yet',
    disabledBannerBody: 'You can edit and preview here. Click Activate to enable the global theme for all users (partners with their own activated theme are not affected).',
    disabledBannerBodyPartner: 'You can edit and preview here. Click Activate to enable the theme only for this partner\'s users — everyone else and the global theme are unaffected.',
  },

  fotaMigration: {
    title: 'FOTA Configuration',
    description: 'Upload a .cfg file and send it to devices via Teltonika FOTA Web. Devices download the configuration the next time they check in with FOTA Web — regardless of which server they point to.',
    wakeupInfo: 'Note: Without SMS "web_connect" the device will fetch the configuration at its next scheduled FOTA check-in (typically within 1–24 h). Click Refresh Status to monitor progress.',
    // Tabs
    tabSend: 'Send configuration',
    tabStatus: 'Status',
    // Upload
    uploadFile: 'Upload config file (.cfg)',
    dropCfgHere: 'Drag and drop .cfg file here, or click to browse',
    onlyCfgAllowed: 'Only .cfg files are allowed',
    uploading: 'Uploading...',
    uploadSuccess: 'File "{name}" uploaded to FOTA Web',
    uploadError: 'Could not upload file',
    errorLoadingFiles: 'Could not fetch config files from FOTA Web',
    // File selection
    selectConfigFile: 'Select config file to send',
    configFilePlaceholder: 'Select a config file...',
    deliveryType: 'What do you want to send?',
    selectFirmwareFile: 'Select firmware file to send',
    firmwareFilePlaceholder: 'Select a firmware file...',
    firmwareWarning: 'A firmware update changes the device software. Only send firmware you have verified is correct for the device model.',
    noFilesFound: 'No files found in FOTA Web',
    selectFilFirst: 'Select a config file first',
    // File type filter
    filterAll: 'All',
    kindConfig: 'Config',
    kindFirmware: 'Firmware',
    kindLog: 'Log',
    kindOther: 'Other',
    // Organisation
    selectOrganization: 'Select organization',
    organizationPlaceholder: 'Search and select organization...',
    selectOrgFirst: 'Select an organization first',
    // Preview
    previewBtn: 'Preview devices',
    previewing: 'Fetching devices...',
    vehiclesFound: 'vehicles found with IMEI',
    vehiclesSelected: 'vehicles selected',
    sendToAll: 'All',
    sendToSelected: 'Selected',
    selectAll: 'Select all',
    noVehiclesSelected: 'No vehicles selected',
    noVehicles: 'No vehicles with IMEI found in selected organization',
    errorLoading: 'Could not fetch devices',
    // Send
    sendBtn: 'Send via FOTA',
    sending: 'Sending...',
    errorSending: 'Could not send configuration',
    successMessage: '{succeeded} of {total} tasks queued in FOTA Web',
    // Confirm
    confirmTitle: 'Confirm FOTA send',
    confirmMessage: 'You are about to send "{file}" to {count} devices via FOTA Web.',
    confirmWakeupNote: 'Devices will fetch the configuration at their next FOTA check-in. Use SMS "web_connect" to trigger immediately.',
    // Result
    resultTitle: 'Result',
    succeeded: 'Queued',
    failed: 'Failed',
    total: 'Total',
    // Task status
    taskQueued: 'Queued',
    taskDownloading: 'Downloading',
    taskApplied: 'Installed',
    taskFailed: 'Failed',
    taskStatusTitle: 'FOTA task status per device',
    refreshStatus: 'Refresh status',
    lastUpdated: 'Last updated',
    selectOrgForStatus: 'Select an organization on the "Send configuration" tab to view status',
    clickRefreshToLoad: 'Click "Refresh status" to load tasks',
    noTasksFound: 'No tasks found for selected organization',
    errorLoadingStatus: 'Could not fetch task status',
    // Table columns
    registrationNumber: 'Reg. no.',
    vehicleName: 'Vehicle name',
    imei: 'IMEI',
    status: 'Status',
    taskId: 'Task ID',
    errorMsg: 'Error message',
    configFile: 'Config file',
    columnSuccess: 'Queued',
    columnFailed: 'Error',
    // File management
    tabFiles: 'Files',
    fileListTitle: 'Uploaded config files in FOTA Web',
    deleteFile: 'Delete file',
    confirmDelete: 'Are you sure?',
    fileDeleted: 'File "{name}" was deleted',
    fileDeleteError: 'Could not delete file',
    downloadFile: 'Download file',
    fileDownloadError: 'Could not download file',
    // Legacy
    noImei: 'IMEI missing',
    apiKeyMissing: 'FOTA Web API key missing on server',
    offlineInfo: 'Offline devices (not connected to DQFleet) cannot be reached without SMS',
    responseLabel: 'Device response',
  },
  damageReports: {
    title: 'Damage Reports',
    subtitle: 'Incoming damage reports from the mobile app',
    filterStatus: 'Status',
    filterSeverity: 'Severity',
    filterFromDate: 'From date',
    filterToDate: 'To date',
    clearFilter: 'Clear filters',
    noReports: 'No damage reports found',
    loading: 'Loading...',
    vehicle: 'Vehicle',
    reporter: 'Reporter',
    severity: 'Severity',
    status: 'Status',
    date: 'Date',
    images: 'Images',
    description: 'Description',
    adminNotes: 'Notes (internal)',
    adminNotesPlaceholder: 'Add internal notes...',
    save: 'Save changes',
    saving: 'Saving...',
    saved: 'Report updated',
    saveError: 'Could not save',
    loadDetailError: 'Could not load report',
    location: 'Location',
    resolvedAt: 'Resolved',
    handling: 'Actions',
    statusPending: 'Pending',
    statusInProgress: 'In Progress',
    statusResolved: 'Resolved',
    statusClosed: 'Closed',
    severityLow: 'Low',
    severityMedium: 'Medium',
    severityHigh: 'High',
    severityCritical: 'Critical',
    statTotal: 'Total',
    statPending: 'Pending',
    statInProgress: 'In Progress',
    statResolved: 'Resolved',
    statCritical: 'Critical',
    allStatuses: 'All statuses',
    allSeverities: 'All',
    totalCount: '{{total}} reports total',
    prev: 'Previous',
    next: 'Next',
    fetchError: 'Could not load damage reports',
    damageImage: 'Damage image',
    reportTitle: 'Report #{{id}}',
    releaseBlock: 'Release block',
    releaseBlockUntil: 'Vehicle is blocked until {{end}}',
    releaseBlockConfirm: 'Release the block so the vehicle can be booked again?',
    releaseBlockSuccess: 'Block released',
    releaseBlockError: 'Could not release the block',
  },

  tireAnalysis: {
    title: 'Tyre Analysis',
    update: 'Refresh',
    analysisDate: 'Analysis performed {{date}} · Next season: {{season}}',
    loading: 'Analysing tyre data...',
    loadError: 'Could not load tyre analysis',
    vehiclesWithData: 'Vehicles with tyre data',
    ofTotal: 'of {{total}} total',
    criticalNow: 'Critical now',
    belowMin: 'Below recommended minimum',
    needsReplacement: 'Needs replacement',
    nextSeasonLabel: 'Next: {{label}}',
    tiresToBuy: 'Tyres to purchase',
    totalPurchaseNeed: 'Total purchase need',
    critical: 'Critical',
    warning: 'Warning',
    noCritical: 'No tyres below recommended minimum',
    noForecast: 'No tyres expected to need replacement next season',
    noProcurement: 'No procurement need identified',
    stored: '📦 Stored',
    installed: '🔩 Installed',
    currentTread: 'Current tread depth',
    predictedTread: 'Estimated at end of season',
    season: 'Season',
    wearRate: 'Wear rate',
    threshold: 'Limit',
    type: 'Type',
    size: 'Size',
    count: 'Quantity',
    level: 'Level',
    urgent: 'Urgent',
    planned: 'Planned',
    affectedVehicles: 'Affected vehicles:',
    orderList: '📋 Order list (click to expand)',
    copyToOrder: 'Copy to purchase order:',
    totalTires: 'TOTAL: {{count}} tyres',
    procurementSummary: 'Total {{count}} tyres to purchase ({{groups}} unique sizes)',
    tabCritical: 'Critical now',
    tabForecast: 'Next season forecast',
    tabProcurement: 'Procurement planning',
    criticalTitle: 'Critical tyres – action required now',
    criticalDesc: 'Installed tyres below the recommended minimum for their type. Winter ≥ 4 mm, snow ≥ 3 mm, summer ≥ 3 mm. Legal minimum for all: 1.6 mm.',
    forecastTitle: 'Forecast – next season: {{season}}',
    forecastDesc: 'Tyres whose projected tread depth at end of season risks falling below the recommended minimum. Calculated based on historical wear per vehicle.',
    procurementTitle: 'Procurement planning – grouped by size',
    procurementDesc: 'Summary of all tyres to purchase – both urgent (critical now) and planned (forecast). Click a row to see the affected vehicles.',
    methodology: 'ℹ️ How are forecasts calculated?',
    methodWear: 'Wear rate: Calculated from historical tyre changes in the change log. If no history, a default of 1 mm per 18,000 km is used.',
    methodSeason: 'Season length: Average km per season from the change log. Fallback: 15,000 km per season.',
    methodForecast: 'Forecast: Current tread depth minus (wear rate × estimated season length).',
    methodThresholds: 'Thresholds: Winter tyres 4 mm (recommended), studded 4 mm, snow 3 mm, summer 3 mm. Legal minimum: 1.6 mm.',
    treadMin: 'Tread depth (min)',
    thresholdLabel: 'Limit: {{mm}} mm',
    seasonKm: '🛣️ Season: ~{{km}} km',
    wearRateLabel: '📉 Wear: {{rate}}',
    thresholdMm: '🎯 Limit: {{mm}} mm',
    wearRatePer10k: '{{rate}} mm/10,000 km',
  },

  bookingResources: {
    title: 'Booking resources',
    description: 'Create custom resource types (e.g. bicycle, trailer) and register bookable resources with a unique asset ID.',
    organization: 'Organization (customer)',
    tabTypes: 'Resource types',
    tabResources: 'Resources',
    addType: 'Add resource type',
    editType: 'Edit resource type',
    addResource: 'Add resource',
    editResource: 'Edit resource',
    noTypes: 'No resource types yet. Create at least one type before registering resources.',
    noResources: 'No resources registered. Create resource types first, then add resources.',
    createTypeFirst: 'Create at least one active resource type before adding resources.',
    colCode: 'Code',
    colName: 'Name',
    colResourceCount: 'Resource count',
    colIdentity: 'Identity (asset ID)',
    colType: 'Resource type',
    colPool: 'Pickup location',
    nameEn: 'Name (English, optional)',
    confirmDeactivateType: 'Deactivate this resource type? Not allowed if resources or bookings are linked.',
    confirmDeactivateResource: 'Deactivate this resource? Not allowed if it has an active booking or is in a pool.',
  },

  travelPlanner: {
    title: 'Travel Planner',
    modeCar: 'Car',
    modeBicycle: 'Bicycle',
    modeFoot: 'Walking',
    modeTransit: 'Transit',
    from: 'From',
    to: 'To',
    fromPlaceholder: 'Search start address...',
    toPlaceholder: 'Search destination...',
    clickOnMap: 'Click on map',
    swap: 'Swap directions',
    timeNow: 'Depart now',
    timeDeparture: 'Depart at',
    timeArrival: 'Arrive by',
    showAlternatives: 'Show alternative routes',
    search: 'Search',
    sendEmail: 'Send directions by email',
    emailPlaceholder: 'Your email address',
    send: 'Send',
    routeAlternatives: 'Routes',
    route: 'Route',
    transitAlternatives: 'Departures',
    transfers: 'transfers',
    tripDetails: 'Trip details',
    selectBothPoints: 'Please select both start and destination',
    noRouteFound: 'No route found',
    noTripsFound: 'No departures found',
    transitNotConfigured: 'Public transit is not configured (ResRobot API key missing on server)',
    searchError: 'Search failed',
    emailSent: 'Directions sent!',
    emailError: 'Could not send email',
  },

  pipeline: {
    newCase: 'New case',
    caseType: 'Case type',
    caseTitle: 'Title',
    typeInquiry: 'Contact / prospect',
    typeProcurement: 'Procurement',
    filterAll: 'All',
    estimatedValue: 'Estimated value',
    calculatedValue: 'Calculated value',
    contractMonths: 'Contract period (months)',
    referenceNumber: 'Reference number',
    submissionDeadline: 'Submission deadline',
    procurementUrl: 'Procurement link',
    noCustomer: 'No customer linked',
    tabOverview: 'Overview',
    tabDocuments: 'Documents',
    tabRequirements: 'Requirement analysis',
    tabValue: 'Deal value',
    contact: 'Contact',
    analysisStatus: 'Analysis status',
    uploadDocument: 'Upload document',
    uploadSuccess: 'Document uploaded',
    uploadSuccessCount: '{{count}} documents uploaded',
    uploadPartial: '{{ok}} uploaded, {{failed}} failed',
    uploadFailed: 'Upload failed',
    uploadMultipleHint: 'You can select multiple files at once',
    noDocuments: 'No documents yet',
    runAnalysis: 'Run AI analysis',
    runAnalysisAll: 'Analyze requirements spec',
    runAnalysisAllHint: 'Analyzes the main Kravspecifikation file. Select more documents and use Analyze selected for other attachments.',
    runAnalysisSelected: 'Analyze selected documents',
    analyzeDocument: 'Analyze',
    analyzing: 'Analyzing...',
    analyzeStarting: 'Starting analysis...',
    analyzeStartedBackground: 'Analysis started in the background. You can leave this page — status updates automatically when you return.',
    analyzeAlreadyRunning: 'An analysis is already running for this case.',
    analyzeAlreadyRunningInfo: 'Analysis is already running in the background. Wait for it to finish, or click Restart analysis.',
    analyzeStuckHint: 'The analysis may be stuck (e.g. after a server restart). You can restart it.',
    forceRestartAnalysis: 'Restart analysis',
    analysisRunningHint: 'AI analysis is running in the background. You can navigate away — results will appear here when complete.',
    analysisStatusNone: 'Not analyzed',
    analyzeSlow: 'AI analysis in progress – large procurement documents may take several minutes.',
    analyzeTimeout: 'Analysis timed out. Try again or analyze fewer attachments at a time.',
    analyzeSuccess: 'Analysis complete',
    analyzeFailed: 'Analysis failed',
    requirementType: 'Type',
    requirement: 'Requirement',
    requirementText: 'Full requirement text',
    matchStatus: 'Match',
    fulfilled: 'fulfilled',
    skaCount: '{{count}} mandatory (SKA)',
    borCount: '{{count}} optional (BÖR)',
    systemHandles: 'What the system handles',
    whatIsMissing: 'What is missing',
    noCatalogMatch: 'No capability matched',
    noCoverageNotes: 'No assessment available – re-run the analysis to get the AI explanation',
    matchedCapability: 'Matched capability',
    noGapNotes: 'No assessment available',
    nothingMissing: 'Nothing missing – requirement fulfilled',
    confidence: 'Confidence',
    noRequirements: 'No requirements analyzed yet',
    createDevTask: 'Create development task',
    devTaskCreated: 'Development task created',
    handlingStatus: 'Handling',
    handlingNotSet: 'Not set',
    manualHandling: 'Manual handling',
    assessment: 'Assessment',
    reuseInOtherCases: 'Reuse this answer in other procurements',
    reuseInOtherCasesHint: 'Saves the assessment so similar requirements (e.g. fetching vehicle data) automatically get the same status.',
    comment: 'Comment',
    commentPlaceholder: 'Write a comment, e.g. that vehicle data is fetched via Car.info...',
    handlingSaved: 'Handling saved',
    handlingSavedReuse: 'Assessment saved and will be reused in other procurements',
    handling: {
      hanterat: 'Handled',
      utveckling: 'Development',
      juridik: 'Legal',
    },
    product: 'Product',
    selectProduct: 'Select product',
    quantity: 'Quantity',
    unitPrice: 'Unit price',
    lineTotal: 'Line total',
    addLine: 'Add line',
    monthlyTotal: 'Monthly value',
    caseNotFound: 'Case not found',
    status: {
      fulfilled: 'Fulfilled',
      partial: 'Partial',
      missing: 'Missing',
      unknown: 'Unknown',
      review: 'Review',
    },
    analysisStatusValues: {
      none: 'Not analyzed',
      pending: 'Pending',
      analyzing: 'Analyzing',
      completed: 'Analysis complete',
      failed: 'Analysis failed',
    },
  },
};

